import { 
  Dismiss20Regular,
  Add20Regular,
  MoreVertical20Regular,
  Document20Regular,
  DocumentPdf20Regular,
  Delete20Regular,
  ArrowDownload20Regular,
  Copy20Regular,
  CheckmarkCircle20Filled,
  TableRegular,
  ArrowLeft20Regular,
  Note20Regular,
  ArrowRight16Regular,
  Wrench20Regular,
  Pin20Filled,
  Pin20Regular,
  CommentMultiple20Regular,
  Edit20Regular,
  LockClosed20Regular,
  ChevronDown20Regular,
  Building24Regular,
  Money20Regular,
  Save20Regular,
  DocumentAdd20Regular,
  Search20Regular,
  ArrowImport20Regular,
  ScanDash20Regular,
  Settings20Regular,
  BrainCircuit20Regular,
  Info20Regular,
  Warning20Regular,
} from '@fluentui/react-icons';
import { PageHeader } from '../../components/PageHeader';
import { useState, Fragment, useEffect, useMemo, useCallback, useRef, useLayoutEffect, type ChangeEvent, type CSSProperties, type UIEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Comments, type Comment } from '../../components/Comments';
import TechnicalDocumentUpload from '../../components/TechnicalDocumentUpload';
import TechnicalDocumentProgress from '../../components/TechnicalDocumentProgress';
import ExtractBudgetDocumentsButton from '../../components/ExtractBudgetDocumentsButton';
import BoqValidationView from '../../components/BoqValidationView';
import { importAllConfidentLinesByBudget } from '../../technicalDocuments/technicalDocumentsApi';
import { StatusBadgeOrcamento } from '../../components/StatusBadgeOrcamento';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '../../components/SearchableSelect';
import {
  GerirExecucaoPanel,
  type ExecucaoLinha,
  type ExecucaoCustoMode,
  type ExecucaoOption,
} from '../../components/GerirExecucaoPanel';
import { getClients, type Client } from '../../clients/clientsApi';
import {
  createSupplier,
  getSuppliers,
  type Supplier,
} from '../../fornecedores/fornecedoresApi';
import {
  createSubcontractor,
  getSubcontractors,
  type Subcontractor,
} from '../../subempreiteiros/subempreiteirosApi';
import { getUsers, type UserAdmin } from '../../users/usersApi';
import {
  ApiError,
  exportBudgetToExcel,
  exportBudgetToPdf,
  formatBudgetStatusLabel,
  getBudgetAreaTypes,
  getBudgetById,
  getBudgetChapterTypes,
  getBudgetDocumentDownloadUrl,
  getBudgetDocument,
  getBudgetLineCostSuggestions,
  startBudgetQuantityImportJob,
  getImportJobStatus,
  reprocessBudgetDocument,
  getBudgetVersions,
  normalizeBudgetStatus,
  deleteBudgetDocument,
  createBudgetVersion,
  createWorkFromBudget,
  updateBudgetStatus,
  setActiveBudgetVersion,
  uploadBudgetDocument,
  updateBudget,
  type Budget,
  type BudgetAreaType,
  type BudgetChapterType,
  type BudgetDocument,
  type BudgetQuantityImportJobStatusResponse,
  type BudgetQuantityImportPreview,
  type BudgetPayload,
  type BudgetStatus,
  type BudgetVersionSummary,
  type WorkFromBudget,
  getMarginAlertRanges,
  resolveMarginColor,
  getAiSettings,
  getBudgetPercentageRules,
  findMatchingPercentageRule,
  type MarginAlertRange,
  type BudgetPercentageRule,
} from '../../orcamentos/orcamentosApi';
import { saveBudgetAsTemplate } from '../../budgetTemplates/budgetTemplatesApi';
import { GuardarComoTemplateModal } from '../../components/modals/GuardarComoTemplateModal';
import {
  createComment,
  deleteComment,
  getComments,
  getCommentAttachmentDownloadUrl,
  uploadCommentAttachment,
  updateComment,
  type Comment as ApiComment,
} from '../../comments/commentsApi';
import { APP_CURRENCY, APP_LOCALE } from '../../config/locale';

const normalizarMargemPercentual = (
  valor: number | null | undefined,
): number | undefined => {
  if (valor === null || valor === undefined) {
    return undefined;
  }

  if (!Number.isFinite(valor)) {
    return 0;
  }

  if (valor > 0 && valor < 100) {
    return Number((valor + 100).toFixed(2));
  }

  return valor;
};

const normalizeKey = (value: string): string => value.trim().toLowerCase();
const buildChapterKey = (name: string): string =>
  normalizeKey(name);

const DEFAULT_PERCENTUAL_VALOR_OBRA = 7;

const matchPercentageRule = (
  description: string,
  rules: BudgetPercentageRule[],
): BudgetPercentageRule | null =>
  findMatchingPercentageRule(description, rules);

const isPercentualValorObraTarget = (
  value: string,
  rules: BudgetPercentageRule[],
): boolean => matchPercentageRule(value, rules) !== null;
const chapterLabelWithCodeRegex =
  /^\s*(?<code>\d+(?:[./-]\d+)*)\s*(?:\||-|–|—|:)?\s+(?<name>.+?)\s*$/;

const splitChapterLabel = (
  rawChapterName: string,
  fallbackNumber: string,
): { number: string; name: string } => {
  const raw = rawChapterName.trim();
  if (raw === '') {
    return {
      number: fallbackNumber,
      name: '',
    };
  }

  const match = chapterLabelWithCodeRegex.exec(raw);
  if (!match?.groups) {
    return {
      number: fallbackNumber,
      name: raw.replace(/\s*[|]\s*$/, '').trim(),
    };
  }

  const parsedCode = (match.groups.code ?? '').trim();
  const parsedName = (match.groups.name ?? '').trim();
  if (parsedCode === '' || parsedName === '') {
    return {
      number: fallbackNumber,
      name: raw,
    };
  }

  return {
    number: parsedCode,
    name: parsedName,
  };
};

const composeChapterLabel = (
  chapterNumber: string,
  chapterName: string,
): string => {
  const number = chapterNumber.trim();
  const name = chapterName.trim();

  if (number === '') return name;
  if (name === '') return number;

  const existing = splitChapterLabel(name, number);
  const normalizedName =
    existing.number !== number ? name : existing.name;

  return `${number} | ${normalizedName}`;
};

const AREA_ORDER: Record<string, number> = {
  ARQUITECTURA: 1,
  ESPECIALIDADES: 2,
};

const parseLeadingNumber = (value: string): number => {
  const match = /^(\d+)/.exec(value.trim());
  return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
};

const reorderItensByChapterNumber = (items: ItemOrcamento[]): ItemOrcamento[] => {
  const areas = items.filter(i => i.tipo === 'area');
  const sortedAreas = [...areas].sort((a, b) => {
    const orderA = AREA_ORDER[a.descricao.toUpperCase()] ?? 99;
    const orderB = AREA_ORDER[b.descricao.toUpperCase()] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return a.descricao.localeCompare(b.descricao);
  });

  const result: ItemOrcamento[] = [];
  let globalChapterCounter = 0;
  for (const area of sortedAreas) {
    result.push(area);
    const chaptersOfArea = items.filter(i => i.tipo === 'capitulo' && i.parentId === area.id);
    const sortedChapters = [...chaptersOfArea]
      .sort((a, b) => parseLeadingNumber(a.numero) - parseLeadingNumber(b.numero))
      .map((chapter) => {
        globalChapterCounter += 1;
        return { ...chapter, numero: globalChapterCounter.toString() };
      });
    for (const chapter of sortedChapters) {
      result.push(chapter);
      const renumberedLines = items
        .filter(i => i.tipo === 'linha' && i.parentId === chapter.id)
        .map((line, idx) => ({ ...line, numero: `${chapter.numero}.${idx + 1}` }));
      result.push(...renumberedLines);
    }
  }
  return result;
};

const defaultUnitOptions: ComboboxOption[] = [
  { value: 'm2' },
  { value: 'm3' },
  { value: 'ud' },
  { value: 'vg' },
  { value: 'h' },
  { value: 'ml' },
  { value: 'kg' },
];

const workflowStatusOptions: BudgetStatus[] = [
  'elaboracao',
  'aguardando',
  'pronto',
  'enviado',
  'aceite',
  'recusado',
];

const parseErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (error instanceof ApiError) {
    const parsedApiError = (() => {
      const raw = (error.message ?? '').trim();
      if (!raw) {
        return null;
      }

      try {
        const payload = JSON.parse(raw) as {
          detail?: string;
          title?: string;
          errors?: Record<string, string[]>;
        };

        if (payload.detail && payload.detail.trim() !== '') {
          return payload.detail.trim();
        }

        if (payload.title && payload.title.trim() !== '') {
          return payload.title.trim();
        }

        if (payload.errors) {
          const firstKey = Object.keys(payload.errors)[0];
          const firstMessage = firstKey
            ? payload.errors[firstKey]?.[0]
            : null;
          if (firstMessage && firstMessage.trim() !== '') {
            return firstMessage.trim();
          }
        }

        return null;
      } catch {
        return null;
      }
    })();

    if (error.status === 401) {
      return 'Sessão expirada. Por favor, autentique-se novamente.';
    }
    if (error.status === 403) {
      return 'Sem permissões para consultar este orçamento.';
    }
    if (error.status === 404) {
      return 'Orçamento não encontrado.';
    }
    if (error.status === 429) {
      return parsedApiError ??
        'Limite temporário da IA atingido. Aguarde cerca de 1 minuto e tente novamente.';
    }
    if (parsedApiError) {
      return parsedApiError;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

interface ItemOrcamento {
  id: string;
  numero: string;
  descricao: string;
  unidade: string;
  quantidade: number;
  quantidadeFormula?: string | null;
  quantidadeFormulaNota?: string | null;
  precoUnitario: number;
  isPercentualValorObra?: boolean;
  percentagemValorObra?: number;
  margem?: number; // Percentagem de margem (G) incluindo 100%
  precoCustoUnitario?: number; // Preço de custo por unidade (H)
  isCostPriceValidated?: boolean;
  sugestaoCustoBudgetId?: number | null;
  sugestaoCustoBudgetReferencia?: string | null;
  tipo: 'area' | 'capitulo' | 'linha';
  parentId?: string;
  execucao?: ExecucaoLinha; // Nova propriedade para armazenar a execução
  nota?: string; // Nota de texto livre para a linha
  notaCapitulo?: string; // Nota opcional do capítulo (só usada em itens tipo 'capitulo')
  budgetAreaTypeId?: number | null;
  budgetChapterTypeId?: number | null;
}

interface Documento {
  id: string;
  nome: string;
  tamanho: string;
  sizeInBytes: number;
  url?: string | null;
  processingStatus?: string | null;
  processingError?: string | null;
  category?: string | null;
  technicalDocumentStatus?: string | null;
}

interface SugestaoPreco {
  budgetId: number;
  budgetReference: string;
  budgetDate: string;
  budgetDateRaw: string;
  clientName: string;
  workName: string;
  lineCode: string;
  lineDescription: string;
  unit: string;
  costUnitPrice: number;
}

interface ComboboxOption {
  value: string;
  label?: string;
}

interface ComboboxInputProps {
  value: string;
  onValueChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  disabled?: boolean;
  tooltipValue?: string;
  tooltipOnOverflow?: boolean;
  className?: string;
  style?: CSSProperties;
}

const ComboboxInput = ({
  value,
  onValueChange,
  options,
  placeholder,
  disabled = false,
  tooltipValue,
  tooltipOnOverflow = false,
  className,
  style,
}: ComboboxInputProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties | null>(null);

  const updateMenuPosition = useCallback(() => {
    if (!inputRef.current) {
      return;
    }

    const rect = inputRef.current.getBoundingClientRect();
    const top = rect.bottom + window.scrollY + 8;
    const left = rect.left + window.scrollX;

    setMenuStyle({
      top,
      left,
      width: rect.width,
      zIndex: 20,
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    updateMenuPosition();

    const handleScroll = () => updateMenuPosition();
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, updateMenuPosition]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onValueChange(event.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleSelect = (option: ComboboxOption) => {
    onValueChange(option.value);
    setIsOpen(false);
  };

  const dropdown =
    isOpen && !disabled && options.length > 0 && menuStyle
      ? createPortal(
          <div
            className="absolute bg-card border border-border shadow-lg max-h-60 overflow-auto"
            style={{ borderRadius: 'var(--radius-card)', ...menuStyle }}
          >
            <div className="py-1">
              {options.map((option) => {
                const label = option.label ?? option.value;
                const isActive = option.value === value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleSelect(option)}
                    className={`w-full px-3 py-2 text-left hover:bg-accent transition-colors ${
                      isActive ? 'bg-accent' : ''
                    }`}
                    style={{ fontSize: 'var(--text-base)' }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        ref={inputRef}
        onChange={handleChange}
        onFocus={() => {
          if (!disabled) {
            setIsOpen(true);
          }
        }}
        onClick={() => {
          if (!disabled) {
            setIsOpen(true);
          }
        }}
        onMouseEnter={() => {
          if (!tooltipOnOverflow || !inputRef.current) {
            return;
          }

          const content = (tooltipValue ?? value).trim();
          if (!content) {
            inputRef.current.title = '';
            return;
          }

          const hasOverflow =
            inputRef.current.scrollWidth >
            inputRef.current.clientWidth;
          inputRef.current.title = hasOverflow ? content : '';
        }}
        onBlur={() => setIsOpen(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setIsOpen(false);
          }
        }}
        className={className}
        style={style}
      />
      {dropdown}
    </div>
  );
};

type BudgetLineModel =
  Budget['lines'][number];

const normalizeDocumentStatus = (status?: string | null): string => {
  return status?.trim() || 'Pending';
};

const isDocumentReady = (status?: string | null): boolean => {
  return normalizeDocumentStatus(status) === 'Ready';
};

const isDocumentProcessing = (status?: string | null): boolean => {
  const normalized = normalizeDocumentStatus(status);
  return normalized === 'Processing' || normalized === 'Pending';
};

const isDocumentFailed = (status?: string | null): boolean => {
  return normalizeDocumentStatus(status) === 'Failed';
};

const resolveDocumentStatusLabel = (status?: string | null): string => {
  const normalized = normalizeDocumentStatus(status);
  switch (normalized) {
    case 'Ready':
      return 'Pronto';
    case 'Failed':
      return 'Falhou';
    case 'Processing':
      return 'A processar';
    case 'Pending':
      return 'A processar';
    default:
      return 'Indisponível';
  }
};

const resolveDocumentStatusClass = (status?: string | null): string => {
  const normalized = normalizeDocumentStatus(status);
  switch (normalized) {
    case 'Ready':
      return 'text-emerald-600';
    case 'Failed':
      return 'text-destructive';
    case 'Processing':
    case 'Pending':
      return 'text-secondary-text';
    default:
      return 'text-secondary-text';
  }
};

const resolveTechnicalStatusLabel = (technicalStatus?: string | null): string | null => {
  if (!technicalStatus) return null;
  switch (technicalStatus.toLowerCase()) {
    case 'pending':
      return 'Pronto para extracção';
    case 'analyzing':
      return 'A extrair texto...';
    case 'measured':
      return 'Medições extraídas';
    case 'generating':
      return 'A gerar BOQ...';
    case 'pending_validation':
      return 'Aguarda importação';
    case 'validated':
      return 'Importado';
    case 'failed':
      return 'Erro na análise';
    default:
      return null;
  }
};

export function EditarOrcamentoView() {
  const enableLineCostSuggestions = true;
  const navigate = useNavigate();
  const location = useLocation();
  const { budgetId } = useParams();
  const parsedBudgetId = budgetId
    ? Number.parseInt(budgetId, 10)
    : Number.NaN;
  const [budget, setBudget] = useState<Budget | null>(null);
  const [clienteId, setClienteId] = useState<number | "">("");
  const [nomeObra, setNomeObra] = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [estadoOrcamento, setEstadoOrcamento] = useState<BudgetStatus>("elaboracao");
  const [taxaIVA, setTaxaIVA] = useState(23);
  const [orderReference, setOrderReference] = useState<string | null>(null);
  const [areaTotalLoja, setAreaTotalLoja] = useState<number | null>(null);
  const [createdAt, setCreatedAt] = useState("");
  const [lastModified, setLastModified] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [users, setUsers] = useState<UserAdmin[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
  const [areaTypes, setAreaTypes] = useState<BudgetAreaType[]>([]);
  const [chapterTypes, setChapterTypes] = useState<BudgetChapterType[]>([]);
  const [marginAlertRanges, setMarginAlertRanges] = useState<MarginAlertRange[]>([]);
  const [percentageRules, setPercentageRules] = useState<BudgetPercentageRule[]>([]);
  const [comentarios, setComentarios] = useState<Comment[]>([]);
  const [comentariosLoading, setComentariosLoading] = useState(false);
  const [itens, setItens] = useState<ItemOrcamento[]>([]);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [documentosTab, setDocumentosTab] = useState<'mapa' | 'projetos' | 'geral'>(() => {
    const stored = sessionStorage.getItem(`docTab-${budgetId}`);
    return stored === 'projetos' || stored === 'geral' ? stored : 'mapa';
  });
  const [boqViewDocIds, setBoqViewDocIds] = useState<Set<number>>(new Set());
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [bulkImportStartedAt, setBulkImportStartedAt] = useState<number | null>(null);
  const [bulkImportNow, setBulkImportNow] = useState<number>(Date.now());
  const [documentPollingIds, setDocumentPollingIds] = useState<number[]>([]);
  const [isUploadingDocumento, setIsUploadingDocumento] = useState(false);
  const [hasUploadQueue, setHasUploadQueue] = useState(false);
  const [hasAnthropicApiKey, setHasAnthropicApiKey] = useState<boolean | null>(null);
  const [defaultSuggestionMargin, setDefaultSuggestionMargin] = useState<number>(120);
  const [documentUploadError, setDocumentUploadError] = useState<string | null>(null);
  const documentoInputRef = useRef<HTMLInputElement | null>(null);
  const [numericDrafts, setNumericDrafts] = useState<Record<string, string>>({});
  const [versionLabel, setVersionLabel] = useState('');
  const [budgetVersions, setBudgetVersions] = useState<BudgetVersionSummary[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [isVersionModalOpen, setIsVersionModalOpen] = useState(false);
  const [novaVersaoBaseId, setNovaVersaoBaseId] = useState<number | null>(null);
  const [novaVersaoLabel, setNovaVersaoLabel] = useState('');
  const [isCreatingVersion, setIsCreatingVersion] = useState(false);
  const [isSettingActiveVersion, setIsSettingActiveVersion] = useState(false);
  const [isGuardarTemplateModalOpen, setIsGuardarTemplateModalOpen] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const headerCollapsedBeforePin = useRef(false);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [isVersionCollapsed, setIsVersionCollapsed] = useState(false);
  const [isResumoPinned, setIsResumoPinned] = useState(false);
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [autoSaveMessage, setAutoSaveMessage] = useState<string | null>(null);
  const [autoSavePending, setAutoSavePending] = useState(false);
  const [autoSaveVersion, setAutoSaveVersion] = useState(0);
  const autoSaveTimerRef = useRef<number | null>(null);
  const autoSaveVersionRef = useRef(autoSaveVersion);
  const autoSaveMessageTimerRef = useRef<number | null>(null);
  const [modalIAAberta, setModalIAAberta] = useState<{
    linhaId: string;
    descricao: string;
    unidade: string;
    precoAtual?: number;
    sugestaoBudgetId?: number | null;
    sugestaoBudgetReferencia?: string | null;
  } | null>(null);
  const [sugestoesPreco, setSugestoesPreco] = useState<SugestaoPreco[]>([]);
  const [sugestoesPrecoLoading, setSugestoesPrecoLoading] = useState(false);
  const [sugestoesPrecoError, setSugestoesPrecoError] = useState<string | null>(null);
  const [sugestaoPesquisa, setSugestaoPesquisa] = useState('');
  const [sugestaoPesquisaDebounced, setSugestaoPesquisaDebounced] = useState('');
  const [sugestaoFiltroData, setSugestaoFiltroData] = useState<'6m' | '12m' | 'all'>('12m');
  const [modalMapaQuantidades, setModalMapaQuantidades] = useState<{
    passo: 1;
  } | null>(null);
  const [isImportingMapaQuantidades, setIsImportingMapaQuantidades] = useState(false);
  const [quantidadeFormulaModal, setQuantidadeFormulaModal] = useState<{
    linhaId: string;
    formula: string;
    nota: string;
  } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importProgressMessage, setImportProgressMessage] = useState<string | null>(null);
  const [importProgressPercent, setImportProgressPercent] = useState<number | null>(null);
  const [importPulseStep, setImportPulseStep] = useState(0);
  const [lastImportUpdateAt, setLastImportUpdateAt] = useState<number | null>(null);
  const [motivoRecusa, setMotivoRecusa] = useState<string>('');
  const [obraCriada, setObraCriada] = useState<{ id: number; codigo: string; nome: string } | null>(null);
  const [menuAberto, setMenuAberto] = useState<string | null>(null);
  const [isWorkflowMenuOpen, setIsWorkflowMenuOpen] = useState(false);
  const [isWorkflowUpdating, setIsWorkflowUpdating] = useState(false);
  const [isCreatingWork, setIsCreatingWork] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [workflowModal, setWorkflowModal] = useState<{ status: BudgetStatus; reason: string } | null>(null);
  const [workflowModalError, setWorkflowModalError] = useState<string | null>(null);
  const [painelExecucaoAberto, setPainelExecucaoAberto] = useState<{ linhaId: string; descricao: string } | null>(null);
  const [notaEmEdicaoId, setNotaEmEdicaoId] = useState<string | null>(null);
  const [precoVendaEmEdicaoId, setPrecoVendaEmEdicaoId] = useState<string | null>(null);
  const commentsSidebarWidth = 320;
  const workflowMenuRef = useRef<HTMLDivElement | null>(null);
  const importPollingTimerRef = useRef<number | null>(null);
  const importFileRef = useRef<File | null>(null);
  const importJobIdRef = useRef<string | null>(null);
  const lastSaveErrorRef = useRef<string | null>(null);
  const sugestaoPesquisaTimerRef = useRef<number | null>(null);

  const budgetRef = budget?.reference ?? (budgetId ?? '');
  const versionTag = budget?.versionNumber
    ? `V${budget.versionNumber}`
    : null;

  const canImportQuantityMap =
    Number.isFinite(parsedBudgetId) &&
    !isLoading &&
    !loadError &&
    budget !== null &&
    budget.id === parsedBudgetId;

  useEffect(() => {
    if (!isImportingMapaQuantidades) {
      setImportPulseStep(0);
      return;
    }

    const timer = window.setInterval(() => {
      setImportPulseStep((step) => (step + 1) % 4);
    }, 500);

    return () => window.clearInterval(timer);
  }, [isImportingMapaQuantidades]);

  const importPulseDots = isImportingMapaQuantidades
    ? '.'.repeat(importPulseStep)
    : '';
  const importIsStale =
    isImportingMapaQuantidades &&
    lastImportUpdateAt !== null &&
    Date.now() - lastImportUpdateAt > 12000;
  const importStatusMessage =
    importProgressMessage && importProgressMessage.trim()
      ? importProgressMessage.trim()
      : importIsStale
        ? 'A processar importação...'
        : 'A importar e a gravar...';
  const importDisplayMessage = isImportingMapaQuantidades
    ? `${importStatusMessage}${importPulseDots}`
    : 'Clique para carregar ficheiro';

  const responsaveisDisponiveis = useMemo(() => {
    return users
      .map((user) => `${user.firstName} ${user.lastName}`.trim())
      .filter((name) => name !== '')
      .sort((a, b) => a.localeCompare(b, APP_LOCALE));
  }, [users]);

  const responsavelOptions = useMemo(() => {
    if (!responsavel) return responsaveisDisponiveis;
    if (responsaveisDisponiveis.includes(responsavel)) {
      return responsaveisDisponiveis;
    }
    return [responsavel, ...responsaveisDisponiveis];
  }, [responsavel, responsaveisDisponiveis]);

  useEffect(() => {
    autoSaveVersionRef.current = autoSaveVersion;
  }, [autoSaveVersion]);

  useEffect(() => {
    return () => {
      if (autoSaveMessageTimerRef.current) {
        window.clearTimeout(autoSaveMessageTimerRef.current);
        autoSaveMessageTimerRef.current = null;
      }
    };
  }, []);

  const fornecedoresDisponiveis = useMemo(() => {
    return suppliers
      .map((supplier) => ({
        id: supplier.id.toString(),
        nome: supplier.name,
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome, APP_LOCALE));
  }, [suppliers]);

  const subempreiteirosDisponiveis = useMemo(() => {
    return subcontractors
      .map((sub) => ({ id: sub.id.toString(), nome: sub.name }))
      .sort((a, b) => a.nome.localeCompare(b.nome, APP_LOCALE));
  }, [subcontractors]);

  const criarFornecedorRapido = async (
    nome: string,
  ): Promise<ExecucaoOption> => {
    const trimmed = nome.trim();
    if (!trimmed) {
      throw new Error('Nome do fornecedor em falta.');
    }

    const existente = suppliers.find(
      (supplier) =>
        supplier.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (existente) {
      return { id: existente.id.toString(), nome: existente.name };
    }

    const criado = await createSupplier({
      name: trimmed,
      category: null,
    });

    setSuppliers((current) => [...current, criado]);

    return { id: criado.id.toString(), nome: criado.name };
  };

  const criarSubempreiteiroRapido = async (
    nome: string,
  ): Promise<ExecucaoOption> => {
    const trimmed = nome.trim();
    if (!trimmed) {
      throw new Error('Nome do sub-empreiteiro em falta.');
    }

    const existente = subcontractors.find(
      (sub) => sub.name.trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (existente) {
      return { id: existente.id.toString(), nome: existente.name };
    }

    const criado = await createSubcontractor({
      name: trimmed,
      specialtyIds: [],
    });

    setSubcontractors((current) => [...current, criado]);

    return { id: criado.id.toString(), nome: criado.name };
  };

  const orderedAreaTypes = useMemo(() => {
    return [...areaTypes].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }
      return a.name.localeCompare(b.name, APP_LOCALE);
    });
  }, [areaTypes]);

  const areaNameOptions = useMemo(() => {
    return orderedAreaTypes.map((area) => ({
      value: area.name,
    }));
  }, [orderedAreaTypes]);

  const unitOptions = useMemo(() => {
    const optionsByKey = new Map<string, ComboboxOption>();

    defaultUnitOptions.forEach((option) => {
      optionsByKey.set(normalizeKey(option.value), option);
    });

    itens.forEach((item) => {
      if (item.tipo !== 'linha') {
        return;
      }
      const trimmed = item.unidade.trim();
      if (!trimmed) {
        return;
      }
      const key = normalizeKey(trimmed);
      if (!optionsByKey.has(key)) {
        optionsByKey.set(key, { value: trimmed });
      }
    });

    return Array.from(optionsByKey.values());
  }, [itens]);

  const areaTypeByName = useMemo(() => {
    const map = new Map<string, BudgetAreaType>();
    orderedAreaTypes.forEach((area) => {
      map.set(normalizeKey(area.name), area);
    });
    return map;
  }, [orderedAreaTypes]);

  const orderedChapterTypes = useMemo(() => {
    const list = [...chapterTypes];
    list.sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }

      const aNumber = Number.parseFloat(a.code);
      const bNumber = Number.parseFloat(b.code);

      if (!Number.isNaN(aNumber) && !Number.isNaN(bNumber)) {
        return aNumber - bNumber;
      }

      return a.code.localeCompare(b.code, APP_LOCALE);
    });
    return list;
  }, [chapterTypes]);

  const chapterTypesByArea = useMemo(() => {
    const map = new Map<number, BudgetChapterType[]>();
    chapterTypes.forEach((chapter) => {
      const bucket = map.get(chapter.budgetAreaTypeId);
      if (bucket) {
        bucket.push(chapter);
      } else {
        map.set(chapter.budgetAreaTypeId, [chapter]);
      }
    });

    map.forEach((list) => {
      list.sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) {
          return a.sortOrder - b.sortOrder;
        }

        const aNumber = Number.parseFloat(a.code);
        const bNumber = Number.parseFloat(b.code);

        if (!Number.isNaN(aNumber) && !Number.isNaN(bNumber)) {
          return aNumber - bNumber;
        }

        return a.code.localeCompare(b.code, APP_LOCALE);
      });
    });

    return map;
  }, [chapterTypes]);

  const chapterTypeByName = useMemo(() => {
    const map = new Map<string, BudgetChapterType>();
    chapterTypes.forEach((chapter) => {
      map.set(buildChapterKey(chapter.name), chapter);
    });
    return map;
  }, [chapterTypes]);

  const chapterTypeByAreaName = useMemo(() => {
    const map = new Map<string, BudgetChapterType>();
    chapterTypes.forEach((chapter) => {
      const chapterKey = buildChapterKey(chapter.name);
      map.set(`${chapter.budgetAreaTypeId}::${chapterKey}`, chapter);
    });
    return map;
  }, [chapterTypes]);

  const clienteAtual = useMemo(() => {
    if (clienteId === '') {
      return budget?.clientName ?? '';
    }

    const client = clients.find((item) => item.id === clienteId);
    return client?.name ?? budget?.clientName ?? '';
  }, [clienteId, clients, budget]);

  const clienteOptions = useMemo<SearchableSelectOption<number>[]>(
    () => clients.map((client) => ({ value: client.id, label: client.name })),
    [clients],
  );

  const orderedBudgetVersions = useMemo(() => {
    return [...budgetVersions].sort(
      (a, b) => a.versionNumber - b.versionNumber,
    );
  }, [budgetVersions]);

  const activeVersion = useMemo(() => {
    if (orderedBudgetVersions.length > 0) {
      return orderedBudgetVersions.find((version) => version.isActiveVersion) ?? null;
    }

    if (budget?.isActiveVersion) {
      return {
        versionNumber: budget.versionNumber,
        versionLabel: budget.versionLabel,
        isActiveVersion: true,
      };
    }

    return null;
  }, [orderedBudgetVersions, budget]);

  // Fechar modal ao pressionar ESC
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (modalIAAberta) {
          setModalIAAberta(null);
        }
        if (quantidadeFormulaModal) {
          setQuantidadeFormulaModal(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [modalIAAberta, quantidadeFormulaModal]);

  useEffect(() => {
    if (!modalIAAberta) {
      setSugestaoPesquisa('');
      setSugestaoPesquisaDebounced('');
      if (sugestaoPesquisaTimerRef.current !== null) {
        window.clearTimeout(sugestaoPesquisaTimerRef.current);
        sugestaoPesquisaTimerRef.current = null;
      }
      return;
    }

    const descricao = modalIAAberta.descricao.trim();
    setSugestaoPesquisa(descricao);
    setSugestaoPesquisaDebounced(descricao);
  }, [modalIAAberta]);

  useEffect(() => {
    if (!modalIAAberta) {
      return;
    }

    if (sugestaoPesquisaTimerRef.current !== null) {
      window.clearTimeout(sugestaoPesquisaTimerRef.current);
    }

    sugestaoPesquisaTimerRef.current = window.setTimeout(() => {
      setSugestaoPesquisaDebounced(sugestaoPesquisa.trim());
    }, 350);

    return () => {
      if (sugestaoPesquisaTimerRef.current !== null) {
        window.clearTimeout(sugestaoPesquisaTimerRef.current);
        sugestaoPesquisaTimerRef.current = null;
      }
    };
  }, [modalIAAberta, sugestaoPesquisa]);

  useEffect(() => {
    const root = document.documentElement;
    if (isCommentsOpen) {
      root.classList.add('comments-sidebar-open');
      root.style.setProperty(
        '--comments-sidebar-width',
        `${commentsSidebarWidth}px`,
      );
    } else {
      root.classList.remove('comments-sidebar-open');
      root.style.removeProperty('--comments-sidebar-width');
    }

    return () => {
      root.classList.remove('comments-sidebar-open');
      root.style.removeProperty('--comments-sidebar-width');
    };
  }, [isCommentsOpen, commentsSidebarWidth]);

  useEffect(() => {
    if (!isWorkflowMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!workflowMenuRef.current) {
        return;
      }

      if (!workflowMenuRef.current.contains(event.target as Node)) {
        setIsWorkflowMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsWorkflowMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isWorkflowMenuOpen]);

  const formatDate = (value: string): string => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleDateString(APP_LOCALE);
  };

  const formatDateTime = (value: string): string => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString(APP_LOCALE, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (sizeInBytes: number): string => {
    if (!Number.isFinite(sizeInBytes)) return '—';
    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    }

    const sizeKb = sizeInBytes / 1024;
    if (sizeKb < 1024) {
      return `${Math.round(sizeKb)} KB`;
    }

    const sizeMb = sizeKb / 1024;
    return `${sizeMb.toFixed(1)} MB`;
  };

  const showToast = (message: string) => {
    const currentState =
      location.state && typeof location.state === 'object'
        ? (location.state as Record<string, unknown>)
        : {};

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: { ...currentState, toast: message },
    });
  };

  const carregarVersoes = useCallback(
    async (budgetIdValue: number) => {
      setVersionsLoading(true);
      setVersionsError(null);

      try {
        const versions = await getBudgetVersions(budgetIdValue);
        setBudgetVersions(versions);
        const currentVersion =
          versions.find((version) => version.id === budgetIdValue) ??
          versions[0];
        setNovaVersaoBaseId(currentVersion?.id ?? null);
      } catch (error) {
        setBudgetVersions([]);
        setVersionsError(
          parseErrorMessage(
            error,
            'Não foi possível carregar as versões do orçamento.',
          ),
        );
      } finally {
        setVersionsLoading(false);
      }
    },
    [],
  );

  const mapApiCommentToUi = (comment: ApiComment): Comment => {
    return {
      id: comment.id.toString(),
      author: {
        name: comment.author.name,
        avatar: comment.author.avatarUrl ?? undefined,
        isSystem: comment.author.isSystem,
      },
      content: comment.content,
      timestamp: new Date(comment.created),
      canEdit: comment.canEdit,
      attachments: comment.attachments.map((attachment) => ({
        id: attachment.id.toString(),
        name: attachment.fileName,
        url: getCommentAttachmentDownloadUrl(comment.id, attachment.id),
        type: attachment.contentType,
      })),
      isSystem: comment.isSystem,
      systemType: (comment.systemType ?? undefined) as Comment['systemType'],
    };
  };

  const buildExecucaoFromLine = useCallback(
    (line: BudgetLineModel): ExecucaoLinha | undefined => {
      const subempreiteiros = line.subcontractors.map((item) => ({
        id: `sub-${item.id}`,
        subempreiteiroId: item.subcontractorId.toString(),
        descricao: item.description,
        valor: item.value,
      }));

      const fornecedores = line.suppliers.map((item) => ({
        id: `forn-${item.id}`,
        fornecedorId: item.supplierId.toString(),
        descricao: item.description,
        quantidade: item.quantity ?? 0,
        valorUnitario: item.unitPrice ?? 0,
        valor: item.value,
      }));

      const materiais = (line.materials ?? []).map((item) => ({
        id: `mat-${item.id}`,
        descricao: item.description,
        quantidade: item.quantity ?? 0,
        valorUnitario: item.unitPrice ?? 0,
        valor: item.value,
      }));

      const equipaInterna = {
        ativo: line.internalTeamActive,
        valor: line.internalTeamValue ?? 0,
        notas: line.internalTeamNotes ?? '',
      };

      const totalExecucao =
        subempreiteiros.reduce((acc, item) => acc + item.valor, 0) +
        fornecedores.reduce((acc, item) => acc + item.valor, 0) +
        materiais.reduce((acc, item) => acc + item.valor, 0) +
        (equipaInterna.ativo ? equipaInterna.valor : 0);

      const inferirModoCusto = (): ExecucaoCustoMode => {
        if (!Number.isFinite(totalExecucao) || totalExecucao <= 0) {
          return 'total';
        }

        const custoUnitario = line.costUnitPrice ?? 0;
        if (!Number.isFinite(custoUnitario) || custoUnitario <= 0) {
          return 'total';
        }

        const quantidade = line.quantity;
        const custoPorTotal =
          quantidade > 0 ? totalExecucao / quantidade : totalExecucao;

        const diferencaUnitario = Math.abs(custoUnitario - totalExecucao);
        const diferencaTotal = Math.abs(custoUnitario - custoPorTotal);

        return diferencaUnitario + 0.01 < diferencaTotal ? 'unitario' : 'total';
      };

      if (
        subempreiteiros.length === 0 &&
        fornecedores.length === 0 &&
        materiais.length === 0 &&
        !equipaInterna.ativo
      ) {
        return undefined;
      }

      return {
        subempreiteiros,
        fornecedores,
        materiais,
        equipaInterna,
        modoCusto: inferirModoCusto(),
      };
    },
    [],
  );

  const buildItensFromBudget = useCallback(
    (
      budgetData: Budget,
      availableAreaTypes: BudgetAreaType[],
      availableChapterTypes: BudgetChapterType[],
      availablePercentageRules: BudgetPercentageRule[],
    ): ItemOrcamento[] => {
      const items: ItemOrcamento[] = [];

      if (budgetData.lines.length > 0) {
        const areaTypeByName = new Map<string, BudgetAreaType>();
        availableAreaTypes.forEach((area) => {
          areaTypeByName.set(normalizeKey(area.name), area);
        });

        const chapterTypeByName = new Map<string, BudgetChapterType>();
        const chapterTypeByAreaName = new Map<string, BudgetChapterType>();
        availableChapterTypes.forEach((chapter) => {
          const chapterKey = buildChapterKey(chapter.name);
          chapterTypeByName.set(chapterKey, chapter);
          chapterTypeByAreaName.set(`${chapter.budgetAreaTypeId}::${chapterKey}`, chapter);
        });

        const areaMap = new Map<string, ItemOrcamento>();
        const chapterMap = new Map<string, ItemOrcamento>();
        const chapterOrderByAreaKey = new Map<string, number>();
        let areaCounter = 0;
        let chapterCounter = 0;

        budgetData.lines.forEach((line) => {
          const areaName = line.areaName.trim();
          const rawChapterName = line.chapterName.trim();
          const areaKey = normalizeKey(areaName);

          let areaItem = areaMap.get(areaKey);
          if (!areaItem) {
            areaCounter += 1;
            const areaType = areaTypeByName.get(areaKey);
            const areaIdentity = splitChapterLabel(areaName, '');
            areaItem = {
              id: `area-${areaCounter}`,
              numero: areaIdentity.number,
              descricao: areaIdentity.name || areaName,
              unidade: '',
              quantidade: 0,
              precoUnitario: 0,
              tipo: 'area',
              budgetAreaTypeId: areaType?.id ?? null,
            };
            areaMap.set(areaKey, areaItem);
            items.push(areaItem);
          }

          const chapterKey = `${areaKey}::${buildChapterKey(rawChapterName)}`;
          let chapterItem = chapterMap.get(chapterKey);
          if (!chapterItem) {
            chapterCounter += 1;
            const nextChapterOrder = (chapterOrderByAreaKey.get(areaKey) ?? 0) + 1;
            chapterOrderByAreaKey.set(areaKey, nextChapterOrder);
            const chapterIdentity = splitChapterLabel(
              rawChapterName,
              nextChapterOrder.toString(),
            );
            const chapterTypeKey = areaItem.budgetAreaTypeId
              ? `${areaItem.budgetAreaTypeId}::${buildChapterKey(chapterIdentity.name)}`
              : null;
            const chapterType = chapterTypeKey
              ? chapterTypeByAreaName.get(chapterTypeKey)
              : chapterTypeByName.get(buildChapterKey(chapterIdentity.name));
            chapterItem = {
              id: `cap-${chapterCounter}`,
              numero: chapterIdentity.number,
              descricao: chapterIdentity.name,
              unidade: '',
              quantidade: 0,
              precoUnitario: 0,
              tipo: 'capitulo',
              parentId: areaItem.id,
              budgetChapterTypeId: chapterType?.id ?? null,
              notaCapitulo: line.chapterNote ?? undefined,
            };
            chapterMap.set(chapterKey, chapterItem);
            items.push(chapterItem);
          } else if (
            chapterItem.notaCapitulo === undefined &&
            line.chapterNote != null &&
            line.chapterNote.trim() !== ''
          ) {
            // Only first line of each chapter carries the note; pick it up if present later
            chapterItem.notaCapitulo = line.chapterNote;
          }

          items.push({
            id: `linha-${line.id}`,
            numero: line.code,
            descricao: line.description,
            unidade: line.unit,
            quantidade: line.quantity,
            quantidadeFormula: line.quantityFormula ?? null,
            quantidadeFormulaNota: line.quantityFormulaNote ?? null,
            precoUnitario: line.unitPrice,
            isPercentualValorObra:
              line.isPercentageOfWorkTotal ||
              isPercentualValorObraTarget(line.description, availablePercentageRules),
            percentagemValorObra:
              line.percentageOfWorkTotal ??
              matchPercentageRule(line.description, availablePercentageRules)?.defaultPercentage ??
              undefined,
            margem: normalizarMargemPercentual(line.marginPercent) ?? undefined,
            precoCustoUnitario: line.costUnitPrice ?? undefined,
            isCostPriceValidated: line.isCostPriceValidated ?? undefined,
            sugestaoCustoBudgetId: line.costSuggestionBudgetId ?? null,
            sugestaoCustoBudgetReferencia: line.costSuggestionBudgetReference ?? null,
            tipo: 'linha',
            parentId: chapterItem.id,
            execucao: buildExecucaoFromLine(line),
            nota: line.note ?? undefined,
          });
        });

        return reorderItensByChapterNumber(items);
      }

      if (availableAreaTypes.length === 0) {
        return items;
      }

      const orderedAreas = [...availableAreaTypes].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      );

      orderedAreas.forEach((area) => {
        const areaId = `area-type-${area.id}`;

        items.push({
          id: areaId,
          numero: '',
          descricao: area.name,
          unidade: '',
          quantidade: 0,
          precoUnitario: 0,
          tipo: 'area',
          budgetAreaTypeId: area.id,
        });
      });

      return items;
    },
    [buildExecucaoFromLine],
  );

  const buildLinesPayload = (
    sourceItems: ItemOrcamento[] = itens,
  ): BudgetPayload['lines'] => {
    const payload: NonNullable<BudgetPayload['lines']> = [];

    const orderedAreas = sourceItems.filter((item) => item.tipo === 'area');
    orderedAreas.forEach((area) => {
      const areaDescricao = area.descricao.trim();
      if (areaDescricao === '') {
        return;
      }
      const areaName = composeChapterLabel(area.numero, areaDescricao);

      const chapters = sourceItems.filter(
        (item) => item.tipo === 'capitulo' && item.parentId === area.id,
      );

      chapters.forEach((chapter) => {
        const chapterName = chapter.descricao.trim();
        if (chapterName === '') {
          return;
        }
        const chapterLabel = composeChapterLabel(
          chapter.numero,
          chapterName,
        );

        const lines = sourceItems.filter(
          (item) => item.tipo === 'linha' && item.parentId === chapter.id,
        );

        const trimmedChapterNote = chapter.notaCapitulo?.trim() || null;
        let chapterNoteAssigned = false;

        lines.forEach((line) => {
          if (linhaEstaVazia(line)) return;
          if (line.descricao.trim() === '') return;
          if (line.numero.trim() === '') return;
          if (line.unidade.trim() === '') return;

          const unitPrice = calcularPrecoUnitario(line, sourceItems);
          const execucao = line.execucao;

          const subcontractors = (execucao?.subempreiteiros ?? [])
            .map((item) => {
              const parsedId = Number.parseInt(
                item.subempreiteiroId,
                10,
              );
              if (!Number.isFinite(parsedId)) return null;
              if (item.descricao.trim() === '') return null;

              return {
                subcontractorId: parsedId,
                description: item.descricao.trim(),
                value: item.valor,
              };
            })
            .filter(
              (item): item is NonNullable<typeof item> =>
                item !== null,
            );

          const suppliers = (execucao?.fornecedores ?? [])
            .map((item) => {
              const parsedId = Number.parseInt(
                item.fornecedorId,
                10,
              );
              if (!Number.isFinite(parsedId)) return null;
              if (item.descricao.trim() === '') return null;

              return {
                supplierId: parsedId,
                description: item.descricao.trim(),
                quantity: item.quantidade,
                unitPrice: item.valorUnitario,
                value: item.valor,
              };
            })
            .filter(
              (item): item is NonNullable<typeof item> =>
                item !== null,
            );

          const materials = (execucao?.materiais ?? [])
            .map((item) => {
              if (item.descricao.trim() === '') return null;

              return {
                description: item.descricao.trim(),
                quantity: item.quantidade,
                unitPrice: item.valorUnitario,
                value: item.valor,
              };
            })
            .filter(
              (item): item is NonNullable<typeof item> =>
                item !== null,
            );

          const equipaInternaAtiva =
            execucao?.equipaInterna.ativo ?? false;

          const chapterNoteForLine = !chapterNoteAssigned ? trimmedChapterNote : null;
          if (!chapterNoteAssigned) {
            chapterNoteAssigned = true;
          }

          payload.push({
            areaName,
            chapterName: chapterLabel,
            chapterNote: chapterNoteForLine,
            code: line.numero.trim(),
            description: line.descricao.trim(),
            unit: line.unidade.trim(),
            quantity: line.quantidade,
            quantityFormula: line.quantidadeFormula?.trim() || null,
            quantityFormulaNote: line.quantidadeFormula?.trim()
              ? line.quantidadeFormulaNota?.trim() || null
              : null,
            unitPrice,
            isPercentageOfWorkTotal: line.isPercentualValorObra ?? false,
            percentageOfWorkTotal: line.isPercentualValorObra
              ? line.percentagemValorObra ?? DEFAULT_PERCENTUAL_VALOR_OBRA
              : null,
            marginPercent: line.margem ?? null,
            costUnitPrice: line.precoCustoUnitario ?? null,
            isCostPriceValidated: line.isCostPriceValidated ?? false,
            costSuggestionBudgetId: line.sugestaoCustoBudgetId ?? null,
            costSuggestionBudgetReference: line.sugestaoCustoBudgetReferencia?.trim() || null,
            note: line.nota?.trim() || null,
            internalTeamActive: equipaInternaAtiva,
            internalTeamValue: equipaInternaAtiva
              ? execucao?.equipaInterna.valor ?? 0
              : null,
            internalTeamNotes: equipaInternaAtiva
              ? execucao?.equipaInterna.notas?.trim() || null
              : null,
            subcontractors,
            suppliers,
            materials,
          });
        });
      });
    });

    return payload;
  };


  const resolveAreaTypeId = (name: string): number | null => {
    const key = normalizeKey(name);
    if (key === '') {
      return null;
    }

    const match = areaTypeByName.get(key);
    return match?.id ?? null;
  };

  const resolveChapterTypeId = (
    areaTypeId: number | null | undefined,
    name: string,
  ): number | null => {
    const nameKey = normalizeKey(name);
    if (nameKey === '') {
      return null;
    }

    if (areaTypeId) {
      const match = chapterTypeByAreaName.get(
        `${areaTypeId}::${nameKey}`,
      );
      if (match) {
        return match.id;
      }
    }

    const fallback = chapterTypeByName.get(nameKey);
    return fallback?.id ?? null;
  };

  const atualizarAreaDescricao = (areaId: string, value: string) => {
    const areaTypeId = resolveAreaTypeId(value);

    setItens((prev) =>
      prev.map((item) => {
        if (item.id === areaId && item.tipo === 'area') {
          return {
            ...item,
            descricao: value,
            budgetAreaTypeId: areaTypeId,
          };
        }

        if (item.tipo === 'capitulo' && item.parentId === areaId) {
          return {
            ...item,
            budgetChapterTypeId: resolveChapterTypeId(
              areaTypeId,
              item.descricao,
            ),
          };
        }

        return item;
      }),
    );
  };

  const atualizarNomeCapitulo = (capituloId: string, value: string) => {
    setItens((prev) => {
      const areasById = new Map<string, ItemOrcamento>();
      prev
        .filter((item) => item.tipo === 'area')
        .forEach((area) => areasById.set(area.id, area));

      return prev.map((item) => {
        if (item.id !== capituloId || item.tipo !== 'capitulo') {
          return item;
        }

        const area = item.parentId
          ? areasById.get(item.parentId)
          : undefined;

        return {
          ...item,
          descricao: value,
          budgetChapterTypeId: resolveChapterTypeId(
            area?.budgetAreaTypeId,
            value,
          ),
        };
      });
    });
  };

  useEffect(() => {
    let isMounted = true;

    const loadBudget = async () => {
      if (!Number.isFinite(parsedBudgetId)) {
        setLoadError('Orçamento inválido.');
        setIsLoading(false);
        setComentariosLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setComentariosLoading(true);

      try {
        const budgetData = await getBudgetById(parsedBudgetId);
        if (!isMounted) return;

        setBudget(budgetData);
        setClienteId(budgetData.clientId ?? '');
        setNomeObra(budgetData.workName);
        setResponsavel(budgetData.responsible ?? '');
        setEstadoOrcamento(normalizeBudgetStatus(budgetData.status));
        setTaxaIVA(budgetData.vatRate);
        setOrderReference(budgetData.orderReference ?? null);
        setAreaTotalLoja(budgetData.workArea ?? null);
        setMotivoRecusa(budgetData.rejectionReason ?? '');
        setObraCriada(
          budgetData.workId
            ? {
                id: budgetData.workId,
                codigo: budgetData.workCode ?? budgetData.workId.toString(),
                nome: budgetData.workName,
              }
            : null,
        );
        setCreatedAt(budgetData.created);
        setLastModified(budgetData.lastModified);
        setVersionLabel(budgetData.versionLabel ?? '');
        setDocumentos(
          budgetData.documents.map((doc) => ({
            id: doc.id.toString(),
            nome: doc.name,
            tamanho: formatFileSize(doc.sizeInBytes),
            sizeInBytes: doc.sizeInBytes,
            url: doc.url ?? null,
            processingStatus: doc.processingStatus ?? 'Pending',
            processingError: doc.processingError ?? null,
            category: (doc as any).category ?? null,
            technicalDocumentStatus: (doc as any).technicalDocumentStatus ?? null,
          })),
        );

        const results = await Promise.allSettled([
          getClients(),
          getUsers(),
          getSuppliers(),
          getSubcontractors(),
          getBudgetAreaTypes(),
          getBudgetChapterTypes(),
          getComments('budget', parsedBudgetId),
          getMarginAlertRanges(),
          getBudgetPercentageRules(),
        ]);

        if (!isMounted) return;

        const [
          clientsResult,
          usersResult,
          suppliersResult,
          subcontractorsResult,
          areaTypesResult,
          chapterTypesResult,
          commentsResult,
          marginAlertRangesResult,
          percentageRulesResult,
        ] = results;

        if (clientsResult.status === 'fulfilled') {
          setClients(clientsResult.value);
        }
        if (usersResult.status === 'fulfilled') {
          setUsers(usersResult.value);
        }
        if (suppliersResult.status === 'fulfilled') {
          setSuppliers(suppliersResult.value);
        }
        if (subcontractorsResult.status === 'fulfilled') {
          setSubcontractors(subcontractorsResult.value);
        }

        const resolvedAreaTypes =
          areaTypesResult.status === 'fulfilled'
            ? areaTypesResult.value
            : [];
        const resolvedChapterTypes =
          chapterTypesResult.status === 'fulfilled'
            ? chapterTypesResult.value
            : [];

        setAreaTypes(resolvedAreaTypes);
        setChapterTypes(resolvedChapterTypes);
        if (marginAlertRangesResult.status === 'fulfilled') {
          setMarginAlertRanges(marginAlertRangesResult.value);
        }

        const resolvedPercentageRules =
          percentageRulesResult.status === 'fulfilled'
            ? percentageRulesResult.value
            : [];
        setPercentageRules(resolvedPercentageRules);

        setItens(
          buildItensFromBudget(
            budgetData,
            resolvedAreaTypes,
            resolvedChapterTypes,
            resolvedPercentageRules,
          ),
        );

        if (commentsResult.status === 'fulfilled') {
          setComentarios(
            commentsResult.value.map(mapApiCommentToUi),
          );
        } else {
          setComentarios([]);
        }
      } catch (error) {
        if (!isMounted) return;
        setLoadError(
          parseErrorMessage(
            error,
            'Não foi possível carregar o orçamento.',
          ),
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setComentariosLoading(false);
        }
      }
    };

    loadBudget();

    return () => {
      isMounted = false;
    };
  }, [parsedBudgetId, buildItensFromBudget]);

  // Carregar estado da API Key Anthropic (para mostrar alerta se não configurada)
  useEffect(() => {
    getAiSettings()
      .then((s) => {
        setHasAnthropicApiKey(s.hasAnthropicApiKey);
        if (typeof s.defaultSuggestionMarginPercent === 'number' && s.defaultSuggestionMarginPercent > 0) {
          setDefaultSuggestionMargin(s.defaultSuggestionMarginPercent);
        }
      })
      .catch(() => setHasAnthropicApiKey(null));
  }, []);

  const reloadBudget = useCallback(async () => {
    try {
      const budgetData = await getBudgetById(parsedBudgetId);
      setBudget(budgetData);
      setItens(buildItensFromBudget(budgetData, areaTypes, chapterTypes, percentageRules));
    } catch {
      // silent — the user will see stale data but can refresh manually
    }
  }, [parsedBudgetId, buildItensFromBudget, areaTypes, chapterTypes, percentageRules]);

  const queueDocumentoPolling = useCallback((documentId: number) => {
    if (!Number.isFinite(documentId)) {
      return;
    }

    setDocumentPollingIds((prev) =>
      prev.includes(documentId) ? prev : [...prev, documentId],
    );
  }, []);

  const removeDocumentoPolling = useCallback((documentId: number) => {
    setDocumentPollingIds((prev) => prev.filter((id) => id !== documentId));
  }, []);

  useEffect(() => {
    setDocumentPollingIds([]);
  }, [parsedBudgetId]);

  useEffect(() => {
    if (!Number.isFinite(parsedBudgetId)) {
      return;
    }

    if (documentPollingIds.length === 0) {
      return;
    }

    let isActive = true;

    const intervalId = window.setInterval(async () => {
      try {
        const results = await Promise.allSettled(
          documentPollingIds.map((documentId) =>
            getBudgetDocument(parsedBudgetId, documentId),
          ),
        );

        const updates = results
          .filter(
            (result): result is PromiseFulfilledResult<BudgetDocument> =>
              result.status === 'fulfilled',
          )
          .map((result) => result.value);

        if (!isActive) {
          return;
        }

        setDocumentos((prev) =>
          prev.map((doc) => {
            const updated = updates.find((item) => item.id === Number(doc.id));
            if (!updated) {
              return doc;
            }

            return {
              ...doc,
              processingStatus: updated.processingStatus ?? doc.processingStatus,
              processingError: updated.processingError ?? doc.processingError ?? null,
              technicalDocumentStatus: (updated as any).technicalDocumentStatus ?? doc.technicalDocumentStatus ?? null,
            };
          }),
        );

        const updatesById = new Map(
          updates.map((item) => [Number(item.id), item]),
        );
        setDocumentPollingIds((prev) =>
          prev.filter((id) => {
            const updated = updatesById.get(id);
            if (!updated) {
              return true;
            }

            return isDocumentProcessing(updated.processingStatus);
          }),
        );
      } catch {
        // Ignore polling errors to avoid blocking the UI.
      }
    }, 5000);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [documentPollingIds, parsedBudgetId]);

  useEffect(() => {
    if (!enableLineCostSuggestions) {
      setSugestoesPreco([]);
      setSugestoesPrecoError(null);
      setSugestoesPrecoLoading(false);
      return;
    }

    if (!modalIAAberta) {
      setSugestoesPreco([]);
      setSugestoesPrecoError(null);
      setSugestoesPrecoLoading(false);
      return;
    }

    if (!Number.isFinite(parsedBudgetId)) {
      setSugestoesPreco([]);
      setSugestoesPrecoError('Orçamento inválido.');
      setSugestoesPrecoLoading(false);
      return;
    }

    const queryBase = sugestaoPesquisaDebounced.trim();
    const query = queryBase || modalIAAberta.descricao.trim();
    if (!query) {
      setSugestoesPreco([]);
      setSugestoesPrecoError(null);
      setSugestoesPrecoLoading(false);
      return;
    }

    let isActive = true;
    setSugestoesPrecoLoading(true);
    setSugestoesPrecoError(null);

    getBudgetLineCostSuggestions(parsedBudgetId, {
      query,
      unit: modalIAAberta.unidade,
    })
      .then((response) => {
        if (!isActive) {
          return;
        }
        setSugestoesPreco(
          response.map((item) => ({
            ...item,
            budgetDate: formatarData(item.budgetDate),
            budgetDateRaw: item.budgetDate,
          })),
        );
      })
      .catch((error) => {
        if (!isActive) {
          return;
        }
        setSugestoesPrecoError(
          parseErrorMessage(
            error,
            'Não foi possível obter sugestões de preço.',
          ),
        );
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setSugestoesPrecoLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    enableLineCostSuggestions,
    modalIAAberta,
    parsedBudgetId,
    sugestaoPesquisaDebounced,
  ]);

  useEffect(() => {
    if (!Number.isFinite(parsedBudgetId)) {
      return;
    }

    void carregarVersoes(parsedBudgetId);
  }, [carregarVersoes, parsedBudgetId]);

  // Aplicar sugestão de preço
  const aplicarSugestaoPreco = (linhaId: string, sugestao: SugestaoPreco) => {
    atualizarItem(linhaId, 'precoCustoUnitario', sugestao.costUnitPrice);
    atualizarItem(linhaId, 'sugestaoCustoBudgetId', sugestao.budgetId);
    atualizarItem(linhaId, 'sugestaoCustoBudgetReferencia', sugestao.budgetReference);

    // Apply default margin (from Settings → Linhas Percentuais) when the line has no margin yet.
    const linha = itens.find((item) => item.id === linhaId);
    if (linha && (linha.margem === undefined || linha.margem === null || linha.margem === 0)) {
      atualizarItem(linhaId, 'margem', defaultSuggestionMargin);
    }

    setModalIAAberta(null);
  };

  const calcularTotal = (quantidade: number, precoUnitario: number) => {
    return quantidade * precoUnitario;
  };

  const calcularBaseValorObra = (
    sourceItems: ItemOrcamento[] = itens,
    ignoredLineId?: string,
  ): number => {
    return sourceItems
      .filter(
        (item) =>
          item.tipo === 'linha' &&
          item.id !== ignoredLineId &&
          !item.isPercentualValorObra &&
          !linhaEstaVazia(item, sourceItems),
      )
      .reduce(
        (acc, item) =>
          acc + calcularTotal(item.quantidade, calcularPrecoUnitario(item, sourceItems)),
        0,
      );
  };

  const calcularPrecoUnitario = (
    linha: ItemOrcamento,
    sourceItems: ItemOrcamento[] = itens,
  ): number => {
    if (linha.isPercentualValorObra) {
      const percentagem = linha.percentagemValorObra ?? DEFAULT_PERCENTUAL_VALOR_OBRA;
      const quantidade = linha.quantidade > 0 ? linha.quantidade : 1;
      const baseValorObra = calcularBaseValorObra(sourceItems, linha.id);

      return Number(((baseValorObra * (percentagem / 100)) / quantidade).toFixed(2));
    }

    const custoUnitario = linha.precoCustoUnitario ?? 0;
    const margem = linha.margem ?? 0;

    if (margem === 0 || custoUnitario === 0) {
      return linha.precoUnitario;
    }

    return Number((custoUnitario * (margem / 100)).toFixed(2));
  };

  // Cálculo de Total Custo (I): Quantidade × Preço Custo Unit.
  const calcularTotalCusto = (quantidade: number, precoCustoUnitario: number) => {
    return quantidade * (precoCustoUnitario || 0);
  };

  // Cálculo de Margem Lucro Real (J): ((Total Venda - Total Custo) / Total Venda) × 100
  const calcularMargemLucroReal = (totalVenda: number, totalCusto: number) => {
    if (totalVenda === 0) return 0;
    return ((totalVenda - totalCusto) / totalVenda) * 100;
  };

  const linhaEstaVazia = (
    linha: ItemOrcamento,
    sourceItems: ItemOrcamento[] = itens,
  ) => {
    const precoUnitario = calcularPrecoUnitario(linha, sourceItems);
    return !linha.descricao || (linha.quantidade === 0 && precoUnitario === 0);
  };

  const calcularTotalCapitulo = (capituloId: string) => {
    return itens
      .filter(item => item.tipo === 'linha' && item.parentId === capituloId && !linhaEstaVazia(item))
      .reduce(
        (acc, item) =>
          acc + calcularTotal(item.quantidade, calcularPrecoUnitario(item)),
        0,
      );
  };

  const calcularTotalCustoCapitulo = (capituloId: string) => {
    return itens
      .filter(item => item.tipo === 'linha' && item.parentId === capituloId && !linhaEstaVazia(item))
      .reduce(
        (acc, item) =>
          acc + calcularTotalCusto(item.quantidade, item.precoCustoUnitario ?? 0),
        0,
      );
  };

  const calcularGastosExecucao = (execucao?: ExecucaoLinha) => {
    if (!execucao) {
      return 0;
    }

    const totalSub = execucao.subempreiteiros.reduce((acc, item) => acc + item.valor, 0);
    const totalForn = execucao.fornecedores.reduce((acc, item) => acc + item.valor, 0);
    const totalMateriais = execucao.materiais.reduce((acc, item) => acc + item.valor, 0);
    const totalInterno = execucao.equipaInterna.ativo ? execucao.equipaInterna.valor : 0;

    return totalSub + totalForn + totalMateriais + totalInterno;
  };

  const calcularGastosCapitulo = (capituloId: string) => {
    return itens
      .filter(item => item.tipo === 'linha' && item.parentId === capituloId && !linhaEstaVazia(item))
      .reduce((acc, item) => acc + calcularGastosExecucao(item.execucao), 0);
  };

  const calcularTotalCustoArea = (areaId: string) => {
    const capitulosDaArea = itens.filter(item => item.tipo === 'capitulo' && item.parentId === areaId);
    return capitulosDaArea.reduce((acc, cap) => acc + calcularTotalCustoCapitulo(cap.id), 0);
  };

  const calcularTotalArea = (areaId: string) => {
    const capitulosDaArea = itens.filter(item => item.tipo === 'capitulo' && item.parentId === areaId);
    return capitulosDaArea.reduce((acc, cap) => acc + calcularTotalCapitulo(cap.id), 0);
  };

  const calcularSubtotal = (sourceItems: ItemOrcamento[] = itens) => {
    return sourceItems
      .filter(item => item.tipo === 'linha' && !linhaEstaVazia(item))
      .reduce(
        (acc, item) =>
          acc + calcularTotal(item.quantidade, calcularPrecoUnitario(item)),
        0,
      );
  };

  const subtotal = calcularSubtotal();
  const sugestoesIA = useMemo(() => {
    if (!modalIAAberta) {
      return [];
    }

    if (sugestaoFiltroData === 'all') {
      return sugestoesPreco;
    }

    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - (sugestaoFiltroData === '6m' ? 6 : 12));

    return sugestoesPreco.filter((item) => {
      const rawDate = item.budgetDateRaw || item.budgetDate;
      const parsed = new Date(rawDate);
      if (Number.isNaN(parsed.getTime())) {
        return true;
      }

      return parsed >= cutoff;
    });
  }, [modalIAAberta, sugestoesPreco, sugestaoFiltroData]);

  // Cálculo de Custo Total
  const calcularCustoTotal = () => {
    return itens
      .filter(item => item.tipo === 'linha' && !linhaEstaVazia(item))
      .reduce((acc, item) => acc + calcularTotalCusto(item.quantidade, item.precoCustoUnitario || 0), 0);
  };

  const custoTotal = calcularCustoTotal();
  const lucroTotal = subtotal - custoTotal;
  const percentagemMargemTotal =
    subtotal > 0 ? (lucroTotal / subtotal) * 100 : 0;
  const areaTotalM2 = areaTotalLoja ?? null;
  const precoPorMetroQuadrado =
    areaTotalM2 && areaTotalM2 > 0 ? subtotal / areaTotalM2 : null;

  const formatarMoeda = (valor: number) => {
    const formatado = new Intl.NumberFormat(APP_LOCALE, {
      style: 'currency',
      currency: APP_CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(valor);
    
    // Substituir pontos por espaços nos milhares
    return formatado.replace(/\./g, ' ');
  };

  const formatarData = (valor: string): string => {
    const parsed = new Date(valor);
    if (Number.isNaN(parsed.getTime())) {
      return valor;
    }

    return new Intl.DateTimeFormat(APP_LOCALE, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(parsed);
  };

  const formatarPercentagem = (valor: number): string => {
    const formatado = new Intl.NumberFormat(APP_LOCALE, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(valor);

    return `${formatado}%`;
  };

  const formatarQuantidade = (valor: number): string => {
    if (!Number.isFinite(valor)) {
      return '—';
    }

    return new Intl.NumberFormat(APP_LOCALE, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    }).format(valor);
  };

  const normalizarNumeroInput = (value: string): string => {
    const cleaned = value.trim().replace(/[%€\s]/g, '');
    if (cleaned === '') {
      return '';
    }

    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');

    if (hasComma && hasDot) {
      return cleaned.replace(/\./g, '').replace(',', '.');
    }

    if (hasComma) {
      return cleaned.replace(',', '.');
    }

    return cleaned;
  };

  const parseNumeroInput = (value: string): number => {
    const normalized = normalizarNumeroInput(value);
    if (!normalized) {
      return 0;
    }

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const parseNumeroInputNullable = (value: string): number | null => {
    const normalized = normalizarNumeroInput(value);
    if (!normalized) {
      return null;
    }

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  type FormulaToken =
    | { type: 'number'; value: number }
    | { type: 'operator'; value: '+' | '-' | '*' | '/' }
    | { type: 'paren'; value: '(' | ')' };

  const tokenizeFormula = (expression: string): { tokens: FormulaToken[]; error: string | null } => {
    const tokens: FormulaToken[] = [];
    const normalized = expression.replace(/\s+/g, '');
    if (!normalized) {
      return { tokens, error: 'Indique uma fórmula.' };
    }

    let index = 0;
    while (index < normalized.length) {
      const char = normalized[index];
      if (/[0-9.,]/.test(char)) {
        const start = index;
        while (index < normalized.length && /[0-9.,]/.test(normalized[index])) {
          index += 1;
        }
        const raw = normalized.slice(start, index);
        const sanitized = raw.replace(/,/g, '.');
        if ((sanitized.match(/\./g) ?? []).length > 1) {
          return { tokens, error: 'Número inválido na fórmula.' };
        }
        const value = Number.parseFloat(sanitized);
        if (!Number.isFinite(value)) {
          return { tokens, error: 'Número inválido na fórmula.' };
        }
        tokens.push({ type: 'number', value });
        continue;
      }

      if (char === '+' || char === '-' || char === '*' || char === '/') {
        tokens.push({ type: 'operator', value: char });
        index += 1;
        continue;
      }

      if (char === '(' || char === ')') {
        tokens.push({ type: 'paren', value: char });
        index += 1;
        continue;
      }

      return { tokens, error: 'Caractere inválido na fórmula.' };
    }

    return { tokens, error: null };
  };

  const parseFormulaTokens = (tokens: FormulaToken[]): { value: number | null; error: string | null } => {
    let index = 0;

    const parseExpression = (): { value: number | null; error: string | null } => {
      let { value, error } = parseTerm();
      if (error || value === null) {
        return { value: null, error };
      }

      while (index < tokens.length) {
        const token = tokens[index];
        if (token.type !== 'operator' || (token.value !== '+' && token.value !== '-')) {
          break;
        }
        const operator = token.value;
        index += 1;
        const next = parseTerm();
        if (next.error || next.value === null) {
          return { value: null, error: next.error };
        }
        value = operator === '+' ? value + next.value : value - next.value;
      }

      return { value, error: null };
    };

    const parseTerm = (): { value: number | null; error: string | null } => {
      let { value, error } = parseFactor();
      if (error || value === null) {
        return { value: null, error };
      }

      while (index < tokens.length) {
        const token = tokens[index];
        if (token.type !== 'operator' || (token.value !== '*' && token.value !== '/')) {
          break;
        }
        const operator = token.value;
        index += 1;
        const next = parseFactor();
        if (next.error || next.value === null) {
          return { value: null, error: next.error };
        }
        if (operator === '/') {
          if (next.value === 0) {
            return { value: null, error: 'Divisão por zero.' };
          }
          value /= next.value;
        } else {
          value *= next.value;
        }
      }

      return { value, error: null };
    };

    const parseFactor = (): { value: number | null; error: string | null } => {
      const token = tokens[index];
      if (!token) {
        return { value: null, error: 'Fórmula incompleta.' };
      }

      if (token.type === 'operator' && (token.value === '+' || token.value === '-')) {
        index += 1;
        const next = parseFactor();
        if (next.error || next.value === null) {
          return { value: null, error: next.error };
        }
        return {
          value: token.value === '-' ? -next.value : next.value,
          error: null,
        };
      }

      if (token.type === 'number') {
        index += 1;
        return { value: token.value, error: null };
      }

      if (token.type === 'paren' && token.value === '(') {
        index += 1;
        const inner = parseExpression();
        if (inner.error || inner.value === null) {
          return { value: null, error: inner.error };
        }
        const closing = tokens[index];
        if (!closing || closing.type !== 'paren' || closing.value !== ')') {
          return { value: null, error: 'Parêntesis não fechados.' };
        }
        index += 1;
        return { value: inner.value, error: null };
      }

      return { value: null, error: 'Fórmula inválida.' };
    };

    const result = parseExpression();
    if (result.error || result.value === null) {
      return result;
    }
    if (index < tokens.length) {
      return { value: null, error: 'Fórmula inválida.' };
    }
    return result;
  };

  const roundQuantity = (value: number): number =>
    Number(value.toFixed(3));

  const avaliarFormulaQuantidade = (
    formula: string,
  ): { value: number | null; error: string | null } => {
    const trimmed = formula.trim();
    const { tokens, error } = tokenizeFormula(trimmed);
    if (error) {
      return { value: null, error };
    }

    const result = parseFormulaTokens(tokens);
    if (result.error || result.value === null) {
      return { value: null, error: result.error ?? 'Fórmula inválida.' };
    }

    if (!Number.isFinite(result.value)) {
      return { value: null, error: 'Fórmula inválida.' };
    }

    if (result.value < 0) {
      return { value: null, error: 'A fórmula deve resultar num valor positivo.' };
    }

    return { value: roundQuantity(result.value), error: null };
  };

  const linhaFormulaQuantidade = quantidadeFormulaModal
    ? itens.find(item => item.id === quantidadeFormulaModal.linhaId) ?? null
    : null;
  const previewFormulaQuantidade = quantidadeFormulaModal
    ? avaliarFormulaQuantidade(quantidadeFormulaModal.formula)
    : null;

  const getNumericDraft = (key: string): string | undefined =>
    numericDrafts[key];

  const setNumericDraft = (key: string, value: string): void => {
    setNumericDrafts((prev) => ({ ...prev, [key]: value }));
  };

  const clearNumericDraft = (key: string): void => {
    setNumericDrafts((prev) => {
      if (!(key in prev)) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const areaTotalLojaKey = 'areaTotalLoja';
  const areaTotalLojaInputValue =
    getNumericDraft(areaTotalLojaKey) ??
    (areaTotalLoja !== null ? formatarQuantidade(areaTotalLoja) : '');

  const buildItensFromImport = (
    preview: BudgetQuantityImportPreview,
  ): ItemOrcamento[] => {
    const items: ItemOrcamento[] = [];
    const now = Date.now();

    preview.areas.forEach((area, areaIndex) => {
      const areaId = `area-import-${now}-${areaIndex}`;
      const areaIdentity = splitChapterLabel(area.name, '');
      const areaTypeId = resolveAreaTypeId(areaIdentity.name || area.name);

      items.push({
        id: areaId,
        numero: areaIdentity.number,
        descricao: areaIdentity.name || area.name,
        unidade: '',
        quantidade: 0,
        precoUnitario: 0,
        tipo: 'area',
        budgetAreaTypeId: areaTypeId,
      });

      area.chapters.forEach((chapter, chapterIndex) => {
        const chapterId = `cap-import-${now}-${areaIndex}-${chapterIndex}`;
        const chapterIdentity = splitChapterLabel(
          chapter.name,
          (chapterIndex + 1).toString(),
        );
        const chapterNumber = chapterIdentity.number;

        items.push({
          id: chapterId,
          numero: chapterNumber,
          descricao: chapterIdentity.name,
          unidade: '',
          quantidade: 0,
          precoUnitario: 0,
          tipo: 'capitulo',
          parentId: areaId,
          budgetChapterTypeId: resolveChapterTypeId(
            areaTypeId,
            chapterIdentity.name,
          ),
          notaCapitulo: chapter.note?.trim() || undefined,
        });

        const sortedArticles = [...chapter.articles].sort((a, b) => {
          const partsA = (a.number ?? '').split('.').map(Number);
          const partsB = (b.number ?? '').split('.').map(Number);
          for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
            const diff = (partsA[i] || 0) - (partsB[i] || 0);
            if (diff !== 0) return diff;
          }
          return 0;
        });

        sortedArticles.forEach((article, lineIndex) => {
          const articleNumber = article.number?.trim()
            ? article.number.trim()
            : `${chapterNumber}.${lineIndex + 1}`;

          const hasSuggestedPrice =
            article.suggestedCostUnitPrice != null &&
            article.suggestedCostUnitPrice > 0;

          const hasSuggestedMargin =
            article.suggestedCostMargin != null &&
            article.suggestedCostMargin > 0;

          items.push({
            id: `linha-import-${now}-${areaIndex}-${chapterIndex}-${lineIndex}`,
            numero: articleNumber,
            descricao: article.description,
            unidade: article.unit,
            quantidade: article.quantity,
            precoUnitario: 0,
            margem: hasSuggestedMargin
              ? 100 + article.suggestedCostMargin!
              : hasSuggestedPrice ? 100 : 0,
            precoCustoUnitario: hasSuggestedPrice
              ? article.suggestedCostUnitPrice!
              : 0,
            isCostPriceValidated: hasSuggestedPrice ? false : false,
            sugestaoCustoBudgetId: article.suggestedCostBudgetId ?? null,
            sugestaoCustoBudgetReferencia:
              article.suggestedCostBudgetReference ?? null,
            tipo: 'linha',
            parentId: chapterId,
          });
        });
      });
    });

    return items;
  };

  const resolveImportStageMessage = (
    stage: BudgetQuantityImportJobStatusResponse['stage'] | null | undefined,
  ): string => {
    switch (stage) {
      case 'Uploading':
        return 'A enviar o ficheiro, aguarde um momento...';
      case 'OcrExtracting':
        return 'A ler o conteúdo do ficheiro...';
      case 'Condensing':
        return 'Quase pronto para analisar, um momento...';
      case 'AiExtracting':
        return 'A IA está a trabalhar no mapa, pode demorar um pouco...';
      case 'Parsing':
        return 'A organizar os dados extraídos...';
      case 'Merging':
        return 'A juntar tudo, falta pouco...';
      case 'Saving':
        return 'A procurar preços de custo em orçamentos anteriores...';
      case 'Done':
        return 'Quase terminado, a preparar o orçamento...';
      case 'Failed':
        return 'Ocorreu um erro na importação.';
      default:
        return 'A trabalhar na importação, tenha calma...';
    }
  };

  const updateImportProgressFromJob = (
    status: BudgetQuantityImportJobStatusResponse,
  ) => {
    const rawMessage = status.message?.trim() ?? '';
    const isFinalMessage = rawMessage.toLowerCase().startsWith('conclu');
    const message = !isFinalMessage && rawMessage
      ? rawMessage
      : resolveImportStageMessage(status.stage);
    setImportProgressMessage(message);
    setImportProgressPercent(
      status.percent === undefined ? null : status.percent,
    );
    setLastImportUpdateAt(Date.now());
  };

  const handleImportFile = async (file: File) => {
    setImportError(null);
    setImportProgressMessage(null);
    setImportProgressPercent(null);
    setLastImportUpdateAt(null);

    if (!Number.isFinite(parsedBudgetId)) {
      setImportError('Orçamento inválido.');
      return;
    }

    if (!canImportQuantityMap) {
      setImportError(
        'Orçamento indisponível para importação. Atualize a página e tente novamente.',
      );
      return;
    }

    setIsImportingMapaQuantidades(true);
    importFileRef.current = file;

    try {
      const started = await startBudgetQuantityImportJob(parsedBudgetId, file);
      importJobIdRef.current = started.jobId;
      setImportProgressMessage('Importação iniciada. A aguardar processamento...');
      setLastImportUpdateAt(Date.now());

      let status: BudgetQuantityImportJobStatusResponse;
      while (true) {
        status = await getImportJobStatus(started.jobId);
        updateImportProgressFromJob(status);

        if (status.status === 'Succeeded' || status.status === 'Failed') {
          break;
        }

        await new Promise((resolve) => {
          importPollingTimerRef.current = window.setTimeout(resolve, 1500);
        });
      }

      if (status.status === 'Failed') {
        setImportError(
          status.error ??
            'Não foi possível importar o ficheiro. Tente novamente.',
        );
        return;
      }

      const preview = status.result?.preview ?? null;
      if (!preview || preview.totalArtigos === 0) {
        setImportError(
          'Não foram encontrados artigos válidos no ficheiro.',
        );
        return;
      }

      setImportProgressMessage('A preparar linhas do orçamento...');
      const novosItens = buildItensFromImport(preview);
      const temLinhasImportadas = novosItens.some(
        (item) => item.tipo === 'linha',
      );

      if (!temLinhasImportadas) {
        setImportError(
          'Não foi possível identificar linhas válidas no ficheiro.',
        );
        return;
      }

      setImportProgressMessage('A gravar linhas no orçamento...');

      const gravado = await salvarOrcamento({
        itemsOverride: novosItens,
        silentAutoSaveMessage: true,
      });

      if (!gravado) {
        setImportError(
          lastSaveErrorRef.current ??
            'A importação foi processada mas não foi possível gravar o orçamento.',
        );
        return;
      }

      setItens(novosItens);
      setImportError(null);
      setModalMapaQuantidades(null);
      showToast(
        `Importação concluída (${preview.totalArtigos} artigos).`,
      );

      anexarDocumentoAoOrcamento(file, {
        showSuccessToast: false,
        showErrorToast: true,
      });
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 404) {
        setImportError(
          canImportQuantityMap
            ? 'Falha de importação (404 do servidor). A operação não foi encontrada ou ficou indisponível. Atualize/reinicie o backend e tente novamente.'
            : 'Orçamento não encontrado no servidor.',
        );
        return;
      }

      setImportError(
        parseErrorMessage(
          error,
          'Não foi possível importar o ficheiro. Confirme que é um PDF, imagem ou Excel válido.',
        ),
      );
    } finally {
      if (importPollingTimerRef.current !== null) {
        window.clearTimeout(importPollingTimerRef.current);
        importPollingTimerRef.current = null;
      }

      setIsImportingMapaQuantidades(false);
      setImportProgressMessage(null);
      setImportProgressPercent(null);
      setLastImportUpdateAt(null);
      importJobIdRef.current = null;
      importFileRef.current = null;
    }
  };

  const cancelImportIfRunning = () => {
    if (!isImportingMapaQuantidades) {
      return;
    }

    showToast('Importação em curso...');
  };

  const ajustarAlturaTextarea = (element: HTMLTextAreaElement | null) => {
    if (!element) {
      return;
    }

    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  };

  const focarDescricaoLinha = (lineId: string) => {
    if (typeof document === 'undefined') {
      return;
    }

    const element = document.querySelector<HTMLTextAreaElement>(
      `[data-line-id="${lineId}"][data-line-field="descricao"]`,
    );
    if (!element) {
      return;
    }

    element.focus();
    const length = element.value.length;
    element.setSelectionRange(length, length);
  };

  const adicionarArea = () => {
    // Proteção: não permitir adicionar áreas em modo READ-ONLY
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return;
    }

    markAutoSavePending();

    const novaArea: ItemOrcamento = {
      id: `area-new-${Date.now()}`,
      numero: '',
      descricao: '',
      unidade: '',
      quantidade: 0,
      precoUnitario: 0,
      tipo: 'area',
      budgetAreaTypeId: null,
    };

    setItens([...itens, novaArea]);
  };

  const removerArea = (areaId: string) => {
    // Proteção: não permitir remover áreas em modo READ-ONLY
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return;
    }

    if (
      !window.confirm(
        'Tem a certeza que pretende apagar esta área? Os capítulos e linhas associados também serão removidos.',
      )
    ) {
      return;
    }

    markAutoSavePending();

    setItens((prev) => {
      const chapterIds = prev
        .filter((item) => item.tipo === 'capitulo' && item.parentId === areaId)
        .map((item) => item.id);

      return prev.filter((item) => {
        if (item.id === areaId) return false;
        if (chapterIds.includes(item.id)) return false;
        if (item.parentId && chapterIds.includes(item.parentId)) return false;
        return true;
      });
    });
  };

  const removerCapitulo = (capituloId: string) => {
    // Proteção: não permitir remover capítulos em modo READ-ONLY
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return;
    }

    if (
      !window.confirm(
        'Tem a certeza que pretende apagar este capítulo? As linhas associadas também serão removidas.',
      )
    ) {
      return;
    }

    markAutoSavePending();

    setItens((prev) =>
      prev.filter(
        (item) => item.id !== capituloId && item.parentId !== capituloId,
      ),
    );
  };

  const adicionarCapitulo = (areaId: string) => {
    // Proteção: não permitir adicionar capítulos em modo READ-ONLY
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return;
    }

    markAutoSavePending();

    const capitulosDaArea = itens.filter(
      (item) => item.tipo === 'capitulo' && item.parentId === areaId,
    );
    const proximoNumero = capitulosDaArea.length + 1;
    
    const novoCapitulo: ItemOrcamento = {
      id: `cap-new-${Date.now()}`,
      numero: proximoNumero.toString(),
      descricao: '',
      unidade: '',
      quantidade: 0,
      precoUnitario: 0,
      tipo: 'capitulo',
      parentId: areaId,
      budgetChapterTypeId: null,
    };
    
    setItens([...itens, novoCapitulo]);
  };

  const criarLinhaVazia = (capituloId: string): string | null => {
    // Proteção: não permitir criar linhas em modo READ-ONLY
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return null;
    }

    markAutoSavePending();
    
    const linhasDoCapitulo = itens.filter(
      item => item.tipo === 'linha' && item.parentId === capituloId,
    );
    const capitulo = itens.find(item => item.id === capituloId);
    if (!capitulo || capitulo.numero.trim() === '' || capitulo.descricao.trim() === '') {
      return null;
    }
    const ultimoNumeroLinha = [...linhasDoCapitulo]
      .reverse()
      .find((linha) => linha.numero.trim() !== '')
      ?.numero.trim();
    const partesNumero = ultimoNumeroLinha
      ? ultimoNumeroLinha
          .split('.')
          .map((parte) => parte.trim())
          .filter((parte) => parte !== '')
      : [];
    const ultimoIndice = partesNumero.length
      ? Number.parseInt(partesNumero[partesNumero.length - 1], 10)
      : Number.NaN;
    const prefixoNumero =
      partesNumero.length >= 2 ? partesNumero.slice(0, -1).join('.') : null;
    const proximoNumero = Number.isFinite(ultimoIndice) ? ultimoIndice + 1 : linhasDoCapitulo.length + 1;
    const numeroBase = prefixoNumero || capitulo.numero.trim();
    const newLineId = `linha-empty-${capituloId}-${Date.now()}`;
    
    const linhaVazia: ItemOrcamento = {
      id: newLineId,
      numero: `${numeroBase}.${proximoNumero}`,
      descricao: '',
      unidade: 'm2',
      quantidade: 0,
      precoUnitario: 0,
      margem: 0,
      precoCustoUnitario: 0,
      tipo: 'linha',
      parentId: capituloId,
    };
    
    setItens([...itens, linhaVazia]);
    return newLineId;
  };

  const duplicarLinha = (linhaId: string) => {
    // Proteção: não permitir duplicar em modo READ-ONLY
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return;
    }

    markAutoSavePending();
    
    const linha = itens.find(item => item.id === linhaId);
    if (!linha) return;

    const novaLinha: ItemOrcamento = {
      ...linha,
      id: `linha-dup-${Date.now()}`,
    };
    
    setItens([...itens, novaLinha]);
    setMenuAberto(null);
  };

  const salvarExecucaoLinha = (execucao: ExecucaoLinha) => {
    if (!painelExecucaoAberto) return;

    markAutoSavePending();

    setItens(itens.map(item => {
      if (item.id !== painelExecucaoAberto.linhaId) {
        return item;
      }

      const totalExecucao = calcularGastosExecucao(execucao);
      const modoCusto = execucao.modoCusto ?? 'total';

      let novoCustoUnitario = item.precoCustoUnitario ?? 0;
      if (totalExecucao > 0) {
        if (modoCusto === 'unitario') {
          novoCustoUnitario = Number(totalExecucao.toFixed(2));
        } else if (item.quantidade > 0) {
          novoCustoUnitario = Number(
            (totalExecucao / item.quantidade).toFixed(2),
          );
        } else {
          novoCustoUnitario = Number(totalExecucao.toFixed(2));
        }
      }

      return {
        ...item,
        execucao,
        precoCustoUnitario: novoCustoUnitario,
      };
    }));
  };

  const gerarResumoExecucao = (execucao: ExecucaoLinha | undefined) => {
    if (!execucao) return null;

    const resumos: string[] = [];

    // Sub-empreiteiros
    if (execucao.subempreiteiros.length > 0) {
      const totalSub = execucao.subempreiteiros.reduce((acc, s) => acc + s.valor, 0);
      resumos.push(`👷 ${execucao.subempreiteiros.length} sub-empreiteiro${execucao.subempreiteiros.length > 1 ? 's' : ''} · ${formatarMoeda(totalSub)}`);
    }

    // Fornecedores
    if (execucao.fornecedores.length > 0) {
      const totalForn = execucao.fornecedores.reduce((acc, f) => acc + f.valor, 0);
      resumos.push(`🏪 ${execucao.fornecedores.length} fornecedor${execucao.fornecedores.length > 1 ? 'es' : ''} · ${formatarMoeda(totalForn)}`);
    }

    if (execucao.materiais.length > 0) {
      const totalMateriais = execucao.materiais.reduce((acc, item) => acc + item.valor, 0);
      resumos.push(`🧱 ${execucao.materiais.length} material${execucao.materiais.length > 1 ? 'is' : ''} · ${formatarMoeda(totalMateriais)}`);
    }

    // Equipa Interna
    if (execucao.equipaInterna.ativo) {
      resumos.push(`🏗 AJA2 · ${formatarMoeda(execucao.equipaInterna.valor)}`);
    }

    return resumos.length > 0 ? resumos : null;
  };

  const removerLinha = (id: string) => {
    // Proteção: não permitir remover em modo READ-ONLY
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return;
    }

    markAutoSavePending();
    
    setItens(itens.filter(item => item.id !== id));
    setMenuAberto(null);
  };

  const removerDocumento = async (id: string) => {
    // Proteção: não permitir remover documentos em modo READ-ONLY
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return;
    }

    if (!Number.isFinite(parsedBudgetId)) {
      setDocumentUploadError('Orçamento inválido.');
      return;
    }

    const documentId = Number.parseInt(id, 10);
    if (!Number.isFinite(documentId)) {
      setDocumentUploadError('Documento inválido.');
      return;
    }

    setDocumentUploadError(null);

    try {
      await deleteBudgetDocument(parsedBudgetId, documentId);
      setDocumentos((prev) => prev.filter(d => d.id !== id));
      removeDocumentoPolling(documentId);
      showToast('Documento removido com sucesso');
    } catch (error) {
      setDocumentUploadError(
        parseErrorMessage(
          error,
          'Não foi possível remover o documento.',
        ),
      );
    }
  };

  const reprocessarDocumento = async (doc: Documento) => {
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return;
    }

    if (!Number.isFinite(parsedBudgetId)) {
      setDocumentUploadError('Orçamento inválido.');
      return;
    }

    const documentId = Number.parseInt(doc.id, 10);
    if (!Number.isFinite(documentId)) {
      setDocumentUploadError('Documento inválido.');
      return;
    }

    setDocumentUploadError(null);

    try {
      const updated = await reprocessBudgetDocument(parsedBudgetId, documentId);
      setDocumentos((prev) =>
        prev.map((item) =>
          item.id === doc.id
            ? {
                ...item,
                processingStatus: updated.processingStatus ?? 'Pending',
                processingError: updated.processingError ?? null,
                technicalDocumentStatus: (updated as any).technicalDocumentStatus ?? null,
              }
            : item,
        ),
      );
      queueDocumentoPolling(Number(updated.id ?? documentId));
      showToast('Documento reprocessado com sucesso.');
    } catch (error) {
      setDocumentUploadError(
        parseErrorMessage(
          error,
          'Não foi possível reprocessar o documento.',
        ),
      );
    }
  };

  const anexarDocumentoAoOrcamento = async (
    file: File,
    options?: { showSuccessToast?: boolean; showErrorToast?: boolean },
  ): Promise<void> => {
    const showSuccessToast = options?.showSuccessToast ?? true;
    const showErrorToast = options?.showErrorToast ?? false;

    if (!Number.isFinite(parsedBudgetId)) {
      const message = 'Orçamento inválido.';
      setDocumentUploadError(message);
      if (showErrorToast) {
        showToast(message);
      }
      return;
    }

    setDocumentUploadError(null);

    try {
      const uploaded = await uploadBudgetDocument(parsedBudgetId, file);
      setDocumentos((prev) => [
        ...prev,
        {
          id: uploaded.id.toString(),
          nome: uploaded.name,
          tamanho: formatFileSize(uploaded.sizeInBytes),
          sizeInBytes: uploaded.sizeInBytes,
          url: uploaded.url ?? null,
          processingStatus: uploaded.processingStatus ?? 'Pending',
          processingError: uploaded.processingError ?? null,
          category: (uploaded as any).category ?? null,
          technicalDocumentStatus: (uploaded as any).technicalDocumentStatus ?? null,
        },
      ]);
      queueDocumentoPolling(Number(uploaded.id));
      if (showSuccessToast) {
        showToast('Documento carregado com sucesso');
      }
    } catch (error) {
      const message = parseErrorMessage(
        error,
        'Não foi possível carregar o documento.',
      );
      setDocumentUploadError(message);
      if (showErrorToast) {
        showToast(message);
      }
    }
  };

  const handleDocumentoSelecionado = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    event.target.value = '';

    if (file.name.toLowerCase().endsWith('.dwg')) {
      setDocumentUploadError('Ficheiros DWG nao sao suportados nesta versao.');
      return;
    }

    setIsUploadingDocumento(true);

    try {
      await anexarDocumentoAoOrcamento(file);
    } finally {
      setIsUploadingDocumento(false);
    }
  };

  const handleDownloadDocumento = (documentId: string) => {
    if (!Number.isFinite(parsedBudgetId)) return;

    const id = Number.parseInt(documentId, 10);
    if (!Number.isFinite(id)) return;

    const url = getBudgetDocumentDownloadUrl(parsedBudgetId, id);
    window.open(url, '_blank', 'noopener');
  };

  const clearAutoSaveTimer = useCallback(() => {
    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
  }, []);

  const showAutoSaveMessage = useCallback(() => {
    setAutoSaveMessage('Guardado automaticamente …');
    if (autoSaveMessageTimerRef.current) {
      window.clearTimeout(autoSaveMessageTimerRef.current);
    }
    autoSaveMessageTimerRef.current = window.setTimeout(() => {
      autoSaveMessageTimerRef.current = null;
      setAutoSaveMessage(null);
    }, 3000);
  }, []);

  const markAutoSavePending = () => {
    setAutoSavePending(true);
    setAutoSaveMessage(null);
    setAutoSaveVersion((prev) => prev + 1);
  };

  const atualizarItem = (id: string, campo: keyof ItemOrcamento, valor: any) => {
    // Proteção: não permitir atualizar itens em modo READ-ONLY
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return;
    }

    markAutoSavePending();

    let criarNovaLinhaVazia = false;
    let parentIdNovaLinha: string | null = null;

    setItens((prev) => {
      const itemAtualizado = prev.find(item => item.id === id);

      const next = prev.map(item => {
        if (item.id !== id) {
          return item;
        }

        const updated = { ...item, [campo]: valor };
        if (updated.tipo !== 'linha') {
          return updated;
        }

        if (campo === 'descricao') {
          const matchedRule = matchPercentageRule(String(valor), percentageRules);
          if (matchedRule !== null && !updated.isPercentualValorObra) {
            return {
              ...updated,
              isPercentualValorObra: true,
              percentagemValorObra:
                updated.percentagemValorObra ?? matchedRule.defaultPercentage,
              quantidade: updated.quantidade > 0 ? updated.quantidade : 1,
              unidade: updated.unidade.trim() ? updated.unidade : 'vg',
            };
          }
        }

        return updated;
      });

      // Se a linha estava vazia e está sendo preenchida, criar uma nova linha vazia
      if (itemAtualizado && itemAtualizado.tipo === 'linha') {
        const estaVazia = linhaEstaVazia(itemAtualizado);
        const novoValor = { ...itemAtualizado, [campo]: valor };
        const ficouPreenchida = !linhaEstaVazia(novoValor);

        if (estaVazia && ficouPreenchida && itemAtualizado.parentId) {
          // Verificar se já existe uma linha vazia neste capítulo
          const linhasDoCapitulo = prev.filter(item =>
            item.tipo === 'linha' &&
            item.parentId === itemAtualizado.parentId
          );
          const temLinhaVazia = linhasDoCapitulo.some(linha =>
            linha.id !== id && linhaEstaVazia(linha)
          );

          if (!temLinhaVazia) {
            criarNovaLinhaVazia = true;
            parentIdNovaLinha = itemAtualizado.parentId;
          }
        }
      }

      return next;
    });

    if (criarNovaLinhaVazia && parentIdNovaLinha) {
      setTimeout(() => criarLinhaVazia(parentIdNovaLinha!), 0);
    }
  };

  const atualizarPrecoVenda = (id: string, precoVenda: number) => {
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return;
    }

    if (!Number.isFinite(precoVenda)) {
      return;
    }

    const itemAtualizado = itens.find(item => item.id === id);
    if (!itemAtualizado || itemAtualizado.tipo !== 'linha') {
      return;
    }

    markAutoSavePending();

    const custoUnitario = itemAtualizado.precoCustoUnitario ?? 0;
    const margem = custoUnitario > 0
      ? Number(((precoVenda / custoUnitario) * 100).toFixed(2))
      : 0;

    setItens(itens.map(item => {
      if (item.id === id) {
        return {
          ...item,
          precoUnitario: precoVenda,
          margem,
        };
      }
      return item;
    }));

    const estaVazia = linhaEstaVazia(itemAtualizado);
    const novoValor = {
      ...itemAtualizado,
      precoUnitario: precoVenda,
      margem,
    };
    const ficouPreenchida = !linhaEstaVazia(novoValor);

    if (estaVazia && ficouPreenchida && itemAtualizado.parentId) {
      const linhasDoCapitulo = itens.filter(item =>
        item.tipo === 'linha' &&
        item.parentId === itemAtualizado.parentId
      );
      const temLinhaVazia = linhasDoCapitulo.some(linha =>
        linha.id !== id && linhaEstaVazia(linha)
      );

      if (!temLinhaVazia) {
        setTimeout(() => criarLinhaVazia(itemAtualizado.parentId!), 0);
      }
    }
  };

  const atualizarQuantidadeComFormula = (
    id: string,
    quantidade: number,
    formula: string | null,
    formulaNote: string | null,
  ) => {
    if (estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') {
      return;
    }

    markAutoSavePending();

    const itemAtualizado = itens.find(item => item.id === id);

    setItens(itens.map(item => {
      if (item.id === id) {
        return {
          ...item,
          quantidade,
          quantidadeFormula: formula,
          quantidadeFormulaNota: formulaNote,
        };
      }
      return item;
    }));

    if (itemAtualizado && itemAtualizado.tipo === 'linha') {
      const estaVazia = linhaEstaVazia(itemAtualizado);
      const novoValor = {
        ...itemAtualizado,
        quantidade,
        quantidadeFormula: formula,
        quantidadeFormulaNota: formulaNote,
      };
      const ficouPreenchida = !linhaEstaVazia(novoValor);

      if (estaVazia && ficouPreenchida && itemAtualizado.parentId) {
        const linhasDoCapitulo = itens.filter(item =>
          item.tipo === 'linha' &&
          item.parentId === itemAtualizado.parentId
        );
        const temLinhaVazia = linhasDoCapitulo.some(linha =>
          linha.id !== id && linhaEstaVazia(linha)
        );

        if (!temLinhaVazia) {
          setTimeout(() => criarLinhaVazia(itemAtualizado.parentId!), 0);
        }
      }
    }
  };

  const abrirFormulaQuantidade = (linha: ItemOrcamento) => {
    setQuantidadeFormulaModal({
      linhaId: linha.id,
      formula: linha.quantidadeFormula ?? '',
      nota: linha.quantidadeFormulaNota ?? '',
    });
  };

  const aplicarFormulaQuantidade = () => {
    if (!quantidadeFormulaModal) {
      return;
    }

    const resultado = avaliarFormulaQuantidade(quantidadeFormulaModal.formula);
    if (resultado.error || resultado.value === null) {
      return;
    }

    const formula = quantidadeFormulaModal.formula.trim();
    const nota = quantidadeFormulaModal.nota.trim();

    atualizarQuantidadeComFormula(
      quantidadeFormulaModal.linhaId,
      resultado.value,
      formula,
      nota ? nota : null,
    );
    setQuantidadeFormulaModal(null);
  };

  const limparFormulaQuantidade = () => {
    if (!quantidadeFormulaModal) {
      return;
    }

    const linha = itens.find(item => item.id === quantidadeFormulaModal.linhaId);
    if (!linha) {
      setQuantidadeFormulaModal(null);
      return;
    }

    atualizarQuantidadeComFormula(linha.id, linha.quantidade, null, null);
    setQuantidadeFormulaModal(null);
  };

  const areas = itens.filter(item => item.tipo === 'area');

  const isFormValido = nomeObra.trim() !== '';

  const salvarOrcamento = async (
    options?: {
      itemsOverride?: ItemOrcamento[];
      silentAutoSaveMessage?: boolean;
    },
  ): Promise<boolean> => {
    if (!Number.isFinite(parsedBudgetId)) {
      const message = 'Orçamento inválido.';
      setSaveError(message);
      lastSaveErrorRef.current = message;
      return false;
    }

    if (!isFormValido) {
      const message = 'Preencha o nome da obra antes de guardar.';
      setSaveError(message);
      lastSaveErrorRef.current = message;
      return false;
    }

    clearAutoSaveTimer();
    const saveVersion = autoSaveVersionRef.current;

    const referencia = budget?.reference ?? budgetRef;
    if (!referencia) {
      const message = 'Referência do orçamento em falta.';
      setSaveError(message);
      lastSaveErrorRef.current = message;
      return false;
    }
    const itemsToPersist = options?.itemsOverride ?? itens;

    setIsSaving(true);
    setSaveError(null);
    lastSaveErrorRef.current = null;

    const payload: BudgetPayload = {
      reference: referencia,
      versionLabel: versionLabel.trim() || null,
      clientId: clienteId === '' ? null : clienteId,
      workId: budget?.workId ?? null,
      workName: nomeObra.trim(),
      responsible: responsavel.trim() || null,
      orderReference: orderReference?.trim() || null,
      vatRate: taxaIVA,
      totalAmount: calcularSubtotal(itemsToPersist),
      workArea: areaTotalLoja,
      rejectionReason: motivoRecusa.trim() || null,
      lines: buildLinesPayload(itemsToPersist),
    };

    try {
      const updated = await updateBudget(parsedBudgetId, payload);
      setBudget(updated);
      setEstadoOrcamento(normalizeBudgetStatus(updated.status));
      setClienteId(updated.clientId ?? '');
      setNomeObra(updated.workName);
      setResponsavel(updated.responsible ?? '');
      setTaxaIVA(updated.vatRate);
      setOrderReference(updated.orderReference ?? null);
      setAreaTotalLoja(updated.workArea ?? null);
      setMotivoRecusa(updated.rejectionReason ?? '');
      setObraCriada(
        updated.workId
          ? {
              id: updated.workId,
              codigo: updated.workCode ?? updated.workId.toString(),
              nome: updated.workName,
            }
          : null,
      );
      setCreatedAt(updated.created);
      setLastModified(updated.lastModified);
      setVersionLabel(updated.versionLabel ?? '');
      setDocumentos(
        updated.documents.map((doc) => ({
          id: doc.id.toString(),
          nome: doc.name,
          tamanho: formatFileSize(doc.sizeInBytes),
          sizeInBytes: doc.sizeInBytes,
          url: doc.url ?? null,
          processingStatus: doc.processingStatus ?? 'Pending',
          processingError: doc.processingError ?? null,
          category: (doc as any).category ?? null,
          technicalDocumentStatus: (doc as any).technicalDocumentStatus ?? null,
        })),
      );
      if (!options?.silentAutoSaveMessage) {
        showAutoSaveMessage();
      }
      if (autoSaveVersionRef.current === saveVersion) {
        setAutoSavePending(false);
      }
      lastSaveErrorRef.current = null;
      return true;
    } catch (error) {
      const message = parseErrorMessage(
        error,
        'Não foi possível guardar o orçamento.',
      );
      setSaveError(message);
      lastSaveErrorRef.current = message;
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportExcel = async (): Promise<void> => {
    if (!Number.isFinite(parsedBudgetId)) return;

    setIsExportingExcel(true);
    try {
      await exportBudgetToExcel(parsedBudgetId);
    } catch (error) {
      setSaveError(
        parseErrorMessage(error, 'Não foi possível exportar o orçamento para Excel.'),
      );
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handleExportPdf = async (): Promise<void> => {
    if (!Number.isFinite(parsedBudgetId)) return;

    setIsExportingPdf(true);
    try {
      await exportBudgetToPdf(parsedBudgetId);
    } catch (error) {
      setSaveError(
        parseErrorMessage(error, 'Não foi possível exportar o orçamento para PDF.'),
      );
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleCreateWorkFromBudget = async (): Promise<void> => {
    if (!Number.isFinite(parsedBudgetId)) {
      setSaveError('Orçamento inválido.');
      return;
    }

    setIsCreatingWork(true);
    setSaveError(null);

    try {
      const created: WorkFromBudget = await createWorkFromBudget(parsedBudgetId);
      setObraCriada({
        id: created.id,
        codigo: created.code,
        nome: created.name,
      });
      setBudget((current) =>
        current
          ? {
              ...current,
              workId: created.id,
              workCode: created.code,
              workName: created.name,
            }
          : current,
      );
      // Criar a obra é um passo terminal — leva o utilizador logo para a ficha da obra.
      navigate(`/obras/${created.id}`, {
        state: { toast: `Obra criada a partir do orçamento (${created.code}).` },
      });
      return;
    } catch (error) {
      setSaveError(
        parseErrorMessage(
          error,
          'Não foi possível criar a obra a partir do orçamento.',
        ),
      );
    } finally {
      setIsCreatingWork(false);
    }
  };

  const criarNovaVersao = async (): Promise<void> => {
    if (!Number.isFinite(parsedBudgetId)) {
      setVersionsError('Orçamento inválido.');
      return;
    }

    const sourceId = novaVersaoBaseId ?? parsedBudgetId;
    setIsCreatingVersion(true);
    setVersionsError(null);

    try {
      const created = await createBudgetVersion(parsedBudgetId, {
        sourceBudgetId: sourceId,
        versionLabel: novaVersaoLabel.trim() || null,
      });

      setIsVersionModalOpen(false);
      setNovaVersaoLabel('');
      showToast('Nova versão criada com sucesso.');
      navigate(`/orcamentos/editar/${created.id}`);
    } catch (error) {
      setVersionsError(
        parseErrorMessage(
          error,
          'Não foi possível criar a nova versão.',
        ),
      );
    } finally {
      setIsCreatingVersion(false);
    }
  };

  const definirVersaoAtiva = async (): Promise<void> => {
    if (!Number.isFinite(parsedBudgetId)) {
      return;
    }

    setIsSettingActiveVersion(true);
    setVersionsError(null);

    try {
      const updated = await setActiveBudgetVersion(parsedBudgetId);
      setBudget(updated);
      setEstadoOrcamento(normalizeBudgetStatus(updated.status));
      setVersionLabel(updated.versionLabel ?? '');
      setCreatedAt(updated.created);
      setLastModified(updated.lastModified);
      showToast('Versão ativa atualizada.');
      void carregarVersoes(parsedBudgetId);
    } catch (error) {
      setVersionsError(
        parseErrorMessage(
          error,
          'Não foi possível definir a versão ativa.',
        ),
      );
    } finally {
      setIsSettingActiveVersion(false);
    }
  };

  const performWorkflowUpdate = async (
    status: BudgetStatus,
    rejectionReason?: string | null,
  ): Promise<void> => {
    if (!Number.isFinite(parsedBudgetId)) {
      setSaveError('Orçamento inválido.');
      throw new Error('Orçamento inválido.');
    }

    setIsWorkflowUpdating(true);
    setSaveError(null);

    try {
      const updated = await updateBudgetStatus(parsedBudgetId, {
        status,
        rejectionReason,
      });
      setBudget(updated);
      setEstadoOrcamento(normalizeBudgetStatus(updated.status));
      setMotivoRecusa(updated.rejectionReason ?? '');
      setCreatedAt(updated.created);
      setLastModified(updated.lastModified);
      showToast('Estado do orçamento atualizado.');
    } catch (error) {
      const message = parseErrorMessage(
        error,
        'Não foi possível atualizar o estado do orçamento.',
      );
      setSaveError(message);
      throw new Error(message);
    } finally {
      setIsWorkflowUpdating(false);
    }
  };

  const handleWorkflowStatusSelect = (
    status: BudgetStatus,
  ): void => {
    if (status === estadoOrcamento) {
      setIsWorkflowMenuOpen(false);
      return;
    }

    if (status === 'aceite' || status === 'recusado') {
      setWorkflowModalError(null);
      setWorkflowModal({
        status,
        reason: status === 'recusado' ? motivoRecusa : '',
      });
      setIsWorkflowMenuOpen(false);
      return;
    }

    setIsWorkflowMenuOpen(false);
    void performWorkflowUpdate(status);
  };

  const handleConfirmWorkflowModal = async (): Promise<void> => {
    if (!workflowModal) {
      return;
    }

    if (
      workflowModal.status === 'recusado' &&
      workflowModal.reason.trim() === ''
    ) {
      setWorkflowModalError('Indique o motivo da recusa.');
      return;
    }

    setWorkflowModalError(null);

    try {
      await performWorkflowUpdate(
        workflowModal.status,
        workflowModal.status === 'recusado'
          ? workflowModal.reason.trim()
          : null,
      );
      setWorkflowModal(null);
    } catch (error) {
      if (error instanceof Error && error.message) {
        setWorkflowModalError(error.message);
      } else {
        setWorkflowModalError('Não foi possível atualizar o estado do orçamento.');
      }
    }
  };

  const handleSelecionarVersao = (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    const value = event.target.value;
    if (!value) {
      return;
    }

    const selectedId = Number.parseInt(value, 10);
    if (!Number.isFinite(selectedId)) {
      return;
    }

    if (selectedId === parsedBudgetId) {
      return;
    }

    navigate(`/orcamentos/editar/${selectedId}`);
  };

  const handleAdicionarComentario = async (
    content: string,
    attachments?: File[],
  ) => {
    if (!Number.isFinite(parsedBudgetId)) return;

    const trimmedContent = content.trim();
    if (!trimmedContent) {
      alert('Escreva um comentário antes de enviar.');
      return;
    }

    try {
      const uploadedAttachments = attachments?.length
        ? await Promise.all(
            attachments.map((file) => uploadCommentAttachment(file)),
          )
        : [];

      const created = await createComment({
        contextType: 'budget',
        contextId: parsedBudgetId,
        content: trimmedContent,
        attachments: uploadedAttachments.map((attachment) => ({
          fileName: attachment.fileName,
          contentType: attachment.contentType || 'application/octet-stream',
          sizeInBytes: attachment.sizeInBytes,
          url: attachment.url ?? undefined,
        })),
      });

      setComentarios((prev) => [
        mapApiCommentToUi(created),
        ...prev,
      ]);
    } catch (error) {
      alert('Não foi possível adicionar o comentário.');
    }
  };

  const handleEditarComentario = async (
    commentId: string,
    newContent: string,
  ) => {
    const trimmed = newContent.trim();
    if (!trimmed) return;

    const parsedId = Number.parseInt(commentId, 10);
    if (!Number.isFinite(parsedId)) return;

    try {
      const updated = await updateComment(parsedId, trimmed);
      setComentarios((prev) =>
        prev.map((comment) =>
          comment.id === commentId
            ? mapApiCommentToUi(updated)
            : comment,
        ),
      );
    } catch (error) {
      alert('Não foi possível atualizar o comentário.');
    }
  };

  const handleApagarComentario = async (commentId: string) => {
    const parsedId = Number.parseInt(commentId, 10);
    if (!Number.isFinite(parsedId)) return;

    try {
      await deleteComment(parsedId);
      setComentarios((prev) =>
        prev.filter((comment) => comment.id !== commentId),
      );
    } catch (error) {
      alert('Não foi possível remover o comentário.');
    }
  };

  // Helper para modo READ-ONLY
  const isReadOnly =
    estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado';

  const salvarOrcamentoRef = useRef(salvarOrcamento);
  salvarOrcamentoRef.current = salvarOrcamento;

  useEffect(() => {
    if (isReadOnly) {
      clearAutoSaveTimer();
      return;
    }

    if (!autoSavePending) {
      return;
    }

    if (
      !Number.isFinite(parsedBudgetId) ||
      isSaving ||
      isLoading ||
      !isFormValido
    ) {
      return;
    }

    clearAutoSaveTimer();

    autoSaveTimerRef.current = window.setTimeout(() => {
      autoSaveTimerRef.current = null;
      void salvarOrcamentoRef.current();
    }, 1200);
  }, [
    autoSavePending,
    clearAutoSaveTimer,
    isFormValido,
    isLoading,
    isReadOnly,
    isSaving,
    parsedBudgetId,
  ]);

  useEffect(() => {
    return () => {
      clearAutoSaveTimer();
    };
  }, [clearAutoSaveTimer]);

  const handleGuardarComoTemplate = async (name: string, description: string | null): Promise<void> => {
    if (!parsedBudgetId) return;

    setIsSavingTemplate(true);
    setSaveError(null);

    try {
      await saveBudgetAsTemplate(parsedBudgetId, { name, description });
      setIsGuardarTemplateModalOpen(false);
      setAutoSaveMessage('Template guardado com sucesso!');
      
      setTimeout(() => {
        setAutoSaveMessage(null);
      }, 3000);
    } catch (error) {
      console.error('Error saving template:', error);
      setSaveError(
        error instanceof Error
          ? error.message
          : 'Erro ao guardar template. Tente novamente.'
      );
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleMainScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (event.currentTarget.scrollTop <= 0) {
        return;
      }

      if (!isHeaderCollapsed) {
        setIsHeaderCollapsed(true);
      }

      if (!isVersionCollapsed) {
        setIsVersionCollapsed(true);
      }
    },
    [isHeaderCollapsed, isVersionCollapsed],
  );

  const handleToggleResumoPinned = useCallback(() => {
    setIsResumoPinned((prev) => {
      const next = !prev;

      if (next) {
        headerCollapsedBeforePin.current = isHeaderCollapsed;
        setIsHeaderCollapsed(true);
      } else {
        setIsHeaderCollapsed(headerCollapsedBeforePin.current);
      }

      return next;
    });
  }, [isHeaderCollapsed]);

  const pageHeaderStyle: CSSProperties | undefined = isResumoPinned
    ? ({
        '--spacing-page-header-top': '8px',
        '--spacing-page-header-bottom': '8px',
        '--spacing-title-subtitle': '2px',
        '--text-page-title': '18px',
        '--text-base': '12px',
      } as CSSProperties)
    : undefined;

  const actionButtonPadding = isResumoPinned ? 'px-3 py-1.5' : 'px-4 py-2';
  const actionButtonMinHeight = isResumoPinned ? 'min-h-[32px]' : 'min-h-[44px]';
  const actionButtonFontSize = isResumoPinned ? 'var(--text-caption)' : 'var(--text-base)';
  const isWorkflowLocked = estadoOrcamento === 'aceite';
  const workflowButtonLabel = isWorkflowUpdating
    ? 'A atualizar...'
    : formatBudgetStatusLabel(estadoOrcamento);
  const workflowMenuDisabled =
    isWorkflowUpdating ||
    isLoading ||
    !Number.isFinite(parsedBudgetId) ||
    isWorkflowLocked;
  const comentariosCount = useMemo((): number => {
    return comentarios.filter((comment) => !comment.isSystem).length;
  }, [comentarios]);
  const contentPaddingClassName = isResumoPinned
    ? 'pb-6 pt-0 md:pt-0'
    : 'py-4 md:py-6';
  const resumoCardClassName = `bg-card border border-border p-6 ${
    isResumoPinned ? 'sticky top-0 z-40 shadow-sm' : ''
  }`;
  const canCreateWork =
    estadoOrcamento === 'aceite' &&
    !obraCriada &&
    Number.isFinite(parsedBudgetId);

  const actions = (
    <div className="flex items-center gap-3">
      {!isReadOnly && (
        <div
          className={`text-primary transition-opacity ${autoSaveMessage ? 'opacity-100' : 'opacity-0'}`}
          style={{ fontSize: actionButtonFontSize, minWidth: '170px' }}
          aria-live="polite"
        >
          {autoSaveMessage ?? 'Guardado automaticamente …'}
        </div>
      )}
      <div className="relative" ref={workflowMenuRef}>
        <button
          type="button"
          onClick={() => setIsWorkflowMenuOpen((prev) => !prev)}
          disabled={workflowMenuDisabled}
          className={`${actionButtonPadding} bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 ${actionButtonMinHeight} disabled:opacity-50 disabled:cursor-not-allowed`}
          style={{ borderRadius: 'var(--radius-button)', fontSize: actionButtonFontSize }}
          aria-haspopup="menu"
          aria-expanded={isWorkflowMenuOpen}
          title={
            isWorkflowLocked
              ? 'Orçamento aceite: o estado está bloqueado.'
              : undefined
          }
        >
          <span className="hidden sm:inline text-secondary-text">Estado</span>
          <span className="text-foreground font-semibold">{workflowButtonLabel}</span>
          {isWorkflowLocked && (
            <LockClosed20Regular className="w-4 h-4 text-secondary-text" />
          )}
          <ChevronDown20Regular className="w-4 h-4 text-secondary-text" />
        </button>

        {isWorkflowMenuOpen && (
          <div
            className="absolute right-0 mt-1 w-56 bg-card border border-border shadow-lg z-50"
            style={{ borderRadius: 'var(--radius-card)' }}
          >
            {workflowStatusOptions.map((status) => {
              const isActive = status === estadoOrcamento;
              return (
                <button
                  key={status}
                  type="button"
                  onClick={() => handleWorkflowStatusSelect(status)}
                  disabled={isWorkflowUpdating}
                  className={`w-full px-4 py-2 text-left hover:bg-accent transition-colors flex items-center gap-2 text-foreground ${
                    isActive ? 'bg-accent/60' : ''
                  }`}
                  style={{ fontSize: 'var(--text-base)' }}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    {isActive ? (
                      <CheckmarkCircle20Filled className="w-4 h-4 text-primary" />
                    ) : (
                      <span className="w-4 h-4" />
                    )}
                  </span>
                  <span>{formatBudgetStatusLabel(status)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      {canCreateWork && (
        <button
          className={`${actionButtonPadding} bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 ${actionButtonMinHeight} disabled:opacity-50 disabled:cursor-not-allowed`}
          style={{ borderRadius: 'var(--radius-button)', fontSize: actionButtonFontSize }}
          onClick={() => {
            void handleCreateWorkFromBudget();
          }}
          disabled={isCreatingWork}
        >
          <Building24Regular className="w-5 h-5" />
          <span className="hidden sm:inline">
            {isCreatingWork ? 'A criar obra...' : 'Criar obra com base neste Orçamento'}
          </span>
        </button>
      )}
      <button
        className={`${actionButtonPadding} bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 ${actionButtonMinHeight} disabled:opacity-50 disabled:cursor-not-allowed`}
        style={{ borderRadius: 'var(--radius-button)', fontSize: actionButtonFontSize }}
        onClick={() => { void handleExportExcel(); }}
        disabled={isExportingExcel || isLoading || !Number.isFinite(parsedBudgetId)}
        title="Exportar orçamento para Excel"
      >
        <ArrowDownload20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">
          {isExportingExcel ? 'A exportar...' : 'Exportar Excel'}
        </span>
      </button>
      <button
        className={`${actionButtonPadding} bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 ${actionButtonMinHeight} disabled:opacity-50 disabled:cursor-not-allowed`}
        style={{ borderRadius: 'var(--radius-button)', fontSize: actionButtonFontSize }}
        onClick={() => { void handleExportPdf(); }}
        disabled={isExportingPdf || isLoading || !Number.isFinite(parsedBudgetId)}
        title="Exportar orçamento para PDF (envio ao cliente)"
      >
        <DocumentPdf20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">
          {isExportingPdf ? 'A exportar...' : 'Exportar PDF'}
        </span>
      </button>
      <button
        className={`${actionButtonPadding} bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 ${actionButtonMinHeight}`}
        style={{ borderRadius: 'var(--radius-button)', fontSize: actionButtonFontSize }}
        onClick={() => navigate('/orcamentos')}
      >
        {isReadOnly ? (
          <ArrowLeft20Regular className="w-5 h-5" />
        ) : (
          <Dismiss20Regular className="w-5 h-5" />
        )}
        <span className="hidden sm:inline">{isReadOnly ? 'Voltar' : 'Sair'}</span>
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="comments-page-header" style={pageHeaderStyle}>
        <PageHeader
          title="Editar Orçamento"
          subtitle={
            [budgetRef || 'Editar Orçamento', versionTag, clienteAtual]
              .filter((value) => value && value !== '')
              .join(' · ')
          }
          actions={actions}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-background" onScroll={handleMainScroll}>
        <div
          className={`min-h-full comments-content ${isCommentsOpen ? 'comments-open' : ''}`}
          style={{ '--comments-sidebar-width': `${commentsSidebarWidth}px` } as CSSProperties}
        >
          <div className={`min-w-0 ${contentPaddingClassName}`}>
              {/* Info Meta */}
              {!isResumoPinned && (
                <div 
                  className="text-secondary-text mb-6"
                  style={{ 
                    fontSize: 'var(--text-caption)',
                    fontWeight: 'var(--font-weight-normal)'
                  }}
                >
                  Criado em {formatDate(createdAt)} · Última atualização: {formatDateTime(lastModified)}
                </div>
              )}

          {isLoading && (
            <div
              className="mb-6 border border-border bg-card px-4 py-3 text-secondary-text"
              style={{ borderRadius: 'var(--radius)' }}
            >
              A carregar orçamento...
            </div>
          )}

          {loadError && (
            <div
              className="mb-6 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
              style={{ borderRadius: 'var(--radius)' }}
            >
              {loadError}
            </div>
          )}

          {saveError && (
            <div
              className="mb-6 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3"
              style={{ borderRadius: 'var(--radius)' }}
            >
              {saveError}
            </div>
          )}

          {/* Status Badge - Visível quando Aceite ou Recusado */}
          {(estadoOrcamento === 'aceite' || estadoOrcamento === 'recusado') && (
            <div className="mb-6">
              <StatusBadgeOrcamento status={estadoOrcamento} />
              
              {/* Link para Obra - Visível apenas quando Aceite e obra foi criada */}
              {estadoOrcamento === 'aceite' && obraCriada && (
                <div className="mt-1">
                  <button
                    className="flex items-center gap-1.5 hover:underline transition-all"
                    style={{ 
                      fontSize: 'var(--text-caption)',
                      color: 'var(--colorBrandForegroundLink)'
                    }}
                    onClick={() => navigate(`/obras/${obraCriada.id}`)}
                  >
                    <ArrowRight16Regular className="w-4 h-4" />
                    <span>Ver Obra: {obraCriada.codigo} — {obraCriada.nome}</span>
                  </button>
                </div>
              )}
              
              {/* Motivo da recusa - Visível apenas quando Recusado e motivoRecusa existe */}
              {estadoOrcamento === 'recusado' && motivoRecusa && (
                <div className="mt-3 flex items-start gap-2">
                  <Note20Regular className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div>
                    <span 
                      className="text-muted-foreground"
                      style={{ fontSize: 'var(--text-caption)' }}
                    >
                      Motivo da recusa:{' '}
                    </span>
                    <span 
                      className="text-foreground"
                      style={{ fontSize: 'var(--text-caption)' }}
                    >
                      {motivoRecusa}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-6 mb-20">
            {/* Card: Dados Principais */}
            {!isResumoPinned && (
              <div className="bg-card border border-border p-6" style={{ borderRadius: 'var(--radius-card)' }}>
                <div className="flex items-center justify-between">
                  <h3 
                    className={`text-foreground ${isHeaderCollapsed ? 'mb-0' : 'mb-5'}`}
                    style={{ 
                      fontSize: 'var(--text-section-title)',
                      fontWeight: 'var(--font-weight-semi-bold)'
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setIsHeaderCollapsed((prev) => !prev)}
                      aria-expanded={!isHeaderCollapsed}
                      aria-controls="orcamento-header-body"
                      className="bg-transparent border-0 p-0 text-foreground text-left cursor-pointer hover:text-foreground transition-colors"
                      style={{ fontSize: 'inherit', fontWeight: 'inherit' }}
                    >
                      Cabeçalho do Orçamento
                    </button>
                  </h3>
                  <button
                    type="button"
                    onClick={() => setIsHeaderCollapsed((prev) => !prev)}
                    aria-expanded={!isHeaderCollapsed}
                    aria-controls="orcamento-header-body"
                    className="flex items-center gap-2 px-3 py-2 border border-border rounded bg-background text-foreground hover:bg-secondary transition-colors"
                    style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
                  >
                    <ArrowRight16Regular
                      className={`w-4 h-4 transition-transform ${isHeaderCollapsed ? '' : 'rotate-90'}`}
                    />
                    <span>{isHeaderCollapsed ? 'Mostrar' : 'Ocultar'}</span>
                  </button>
                </div>

                {!isHeaderCollapsed && (
                  <div id="orcamento-header-body" className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Cliente */}
                  <div>
                    <label 
                      htmlFor="cliente"
                      className="block text-foreground mb-2"
                      style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
                    >
                      Cliente <span className="text-secondary-text">(opcional)</span>
                    </label>
                    <SearchableSelect<number>
                      id="cliente"
                      value={clienteId === '' ? null : clienteId}
                      onChange={(next) => {
                        setClienteId(next ?? '');
                        markAutoSavePending();
                      }}
                      options={clienteOptions}
                      disabled={isReadOnly || isLoading}
                      placeholder="Selecione um cliente"
                      searchPlaceholder="Pesquisar cliente..."
                    />
                  </div>

                {/* Nome da Obra */}
                <div>
                  <label 
                    htmlFor="nomeObra"
                    className="block text-foreground mb-2"
                    style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
                  >
                    Nome da Obra <span className="text-primary">*</span>
                  </label>
                  <input
                    id="nomeObra"
                    type="text"
                    value={nomeObra}
                    onChange={(e) => {
                      setNomeObra(e.target.value);
                      markAutoSavePending();
                    }}
                    disabled={isReadOnly || isLoading}
                    placeholder="Ex: Remodelação Edifício Pombalino"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
                  />
                </div>

                {/* Responsável Técnico */}
                <div>
                  <label 
                    htmlFor="responsavel"
                    className="block text-foreground mb-2"
                    style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
                  >
                    Responsável Técnico
                  </label>
                  <select
                    id="responsavel"
                    value={responsavel}
                    onChange={(e) => {
                      setResponsavel(e.target.value);
                      markAutoSavePending();
                    }}
                    disabled={isReadOnly || isLoading}
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
                  >
                    <option value="">Selecione um responsável</option>
                    {responsavelOptions.map((nome) => (
                      <option key={nome} value={nome}>
                        {nome}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Referência Encomenda (Externa) */}
                <div>
                  <label 
                    htmlFor="orderReference"
                    className="block text-foreground mb-2"
                    style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
                  >
                    Referência Encomenda (Externa)
                  </label>
                  <input
                    id="orderReference"
                    type="text"
                    value={orderReference ?? ''}
                    onChange={(e) => {
                      setOrderReference(e.target.value);
                      markAutoSavePending();
                    }}
                    disabled={isReadOnly || isLoading}
                    placeholder="Ex: ENQ-2024-001 / Concurso 34/2024"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
                  />
                  <p 
                    className="mt-1 text-secondary-text"
                    style={{ fontSize: 'var(--text-caption)' }}
                  >
                    Referência da encomenda do cliente ou número de concurso
                  </p>
                </div>

                {/* Estado */}
                <div>
                  <label 
                    className="block text-foreground mb-2"
                    style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
                  >
                    Estado
                  </label>
                  <div 
                    className="px-3 py-2 bg-secondary border border-border rounded text-foreground"
                    style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
                  >
                    {formatBudgetStatusLabel(estadoOrcamento)}
                  </div>
                </div>

                {/* Data de Criação */}
                <div>
                  <label 
                    className="block text-foreground mb-2"
                    style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
                  >
                    Data de Criação
                  </label>
                  <div 
                    className="px-3 py-2 bg-secondary border border-border rounded text-foreground"
                    style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
                  >
                    {formatDate(createdAt)}
                  </div>
                </div>
                  </div>
                )}
              </div>
            )}

            {budget && (
              <div className="bg-card border border-border p-5" style={{ borderRadius: 'var(--radius-card)' }}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3
                      className={`text-foreground ${isVersionCollapsed ? 'mb-0' : 'mb-4'}`}
                      style={{
                        fontSize: 'var(--text-section-title)',
                        fontWeight: 'var(--font-weight-semi-bold)',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setIsVersionCollapsed((prev) => !prev)}
                        aria-expanded={!isVersionCollapsed}
                        aria-controls="orcamento-version-body"
                        className="bg-transparent border-0 p-0 text-foreground text-left cursor-pointer hover:text-foreground transition-colors"
                        style={{ fontSize: 'inherit', fontWeight: 'inherit' }}
                      >
                        Versão do Orçamento
                      </button>
                    </h3>
                    {isVersionCollapsed && activeVersion && (
                      <div
                        className="flex flex-wrap items-center gap-2 text-secondary-text"
                        style={{ fontSize: 'var(--text-caption)' }}
                      >
                        <span
                          className="text-foreground"
                          style={{ fontWeight: 'var(--font-weight-semi-bold)' }}
                        >
                          V{activeVersion.versionNumber}
                        </span>
                        {activeVersion.versionLabel && (
                          <span>· {activeVersion.versionLabel}</span>
                        )}
                        <span
                          className="px-2 py-0.5 border border-primary/20 text-primary bg-primary/10"
                          style={{ borderRadius: '999px', fontSize: '12px' }}
                        >
                          Ativa
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsVersionCollapsed((prev) => !prev)}
                    aria-expanded={!isVersionCollapsed}
                    aria-controls="orcamento-version-body"
                    className="flex items-center gap-2 px-3 py-2 border border-border rounded bg-background text-foreground hover:bg-secondary transition-colors"
                    style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
                  >
                    <ArrowRight16Regular
                      className={`w-4 h-4 transition-transform ${isVersionCollapsed ? '' : 'rotate-90'}`}
                    />
                    <span>{isVersionCollapsed ? 'Mostrar' : 'Ocultar'}</span>
                  </button>
                </div>

                {!isVersionCollapsed && (
                  <div id="orcamento-version-body" className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div
                          className="text-secondary-text"
                          style={{ fontSize: 'var(--text-caption)' }}
                        >
                          Versão atual
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className="text-foreground"
                            style={{
                              fontSize: 'var(--text-base)',
                              fontWeight: 'var(--font-weight-semi-bold)',
                            }}
                          >
                            V{budget.versionNumber}
                          </span>
                          {versionLabel && (
                            <span className="text-secondary-text">
                              · {versionLabel}
                            </span>
                          )}
                          {budget.isActiveVersion && (
                            <span
                              className="px-2 py-0.5 border border-primary/20 text-primary bg-primary/10"
                              style={{ borderRadius: '999px', fontSize: '12px' }}
                            >
                              Ativa
                            </span>
                          )}
                        </div>
                        <div
                          className="text-secondary-text"
                          style={{ fontSize: 'var(--text-caption)' }}
                        >
                          Criada em {formatDateTime(budget.created)}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-secondary-text"
                            style={{ fontSize: 'var(--text-caption)' }}
                          >
                            Versão
                          </span>
                          <select
                            value={parsedBudgetId}
                            onChange={handleSelecionarVersao}
                            disabled={versionsLoading || isLoading}
                            className="px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50"
                            style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
                          >
                            {orderedBudgetVersions.length === 0 ? (
                              <option value={parsedBudgetId}>V{budget.versionNumber}</option>
                            ) : (
                              orderedBudgetVersions.map((version) => {
                                const label = version.versionLabel
                                  ? ` - ${version.versionLabel}`
                                  : '';
                                const activeTag = version.isActiveVersion ? ' (Ativa)' : '';
                                return (
                                  <option key={version.id} value={version.id}>
                                    V{version.versionNumber}{label}{activeTag}
                                  </option>
                                );
                              })
                            )}
                          </select>
                        </div>

                        {!budget.isActiveVersion && (
                          <button
                            className="px-3 py-2 bg-secondary hover:bg-accent border border-border text-foreground rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
                            onClick={() => void definirVersaoAtiva()}
                            disabled={isSettingActiveVersion}
                          >
                            {isSettingActiveVersion ? 'A definir...' : 'Definir como ativa'}
                          </button>
                        )}

                        <button
                          className="px-3 py-2 bg-secondary hover:bg-accent border border-border text-foreground rounded transition-colors"
                          style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
                          onClick={() => setIsVersionModalOpen(true)}
                          disabled={isCreatingVersion}
                        >
                          Nova versão
                        </button>

                        <button
                          className="px-3 py-2 bg-secondary hover:bg-accent border border-border text-foreground rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
                          onClick={() => setIsGuardarTemplateModalOpen(true)}
                          disabled={isSavingTemplate}
                          title="Guardar este orçamento como template reutilizável"
                        >
                          <Save20Regular className="w-4 h-4" />
                          Guardar como Template
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label
                          htmlFor="versionLabel"
                          className="block text-foreground mb-2"
                          style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
                        >
                          Etiqueta da versão
                        </label>
                        {isReadOnly ? (
                          <div
                            className="px-3 py-2 border border-border bg-muted text-foreground"
                            style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
                          >
                            {versionLabel || '—'}
                          </div>
                        ) : (
                          <input
                            id="versionLabel"
                            type="text"
                            value={versionLabel}
                            onChange={(event) => {
                              setVersionLabel(event.target.value);
                              markAutoSavePending();
                            }}
                            placeholder="Ex: Ajustes cliente"
                            maxLength={120}
                            className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
                          />
                        )}
                      </div>
                    </div>

                    {versionsLoading && (
                      <div
                        className="text-secondary-text"
                        style={{ fontSize: 'var(--text-caption)' }}
                      >
                        A carregar versões...
                      </div>
                    )}

                    {versionsError && (
                      <div
                        className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-2"
                        style={{ borderRadius: 'var(--radius)' }}
                      >
                        {versionsError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Card: Resumo do Orçamento */}
            <div className={resumoCardClassName} style={{ borderRadius: 'var(--radius-card)' }}>
              <div className="flex items-center justify-between mb-5">
                <h3 
                  className="text-foreground"
                  style={{ 
                    fontSize: 'var(--text-section-title)',
                    fontWeight: 'var(--font-weight-semi-bold)'
                  }}
                >
                  Resumo do Orçamento
                </h3>
                <button
                  type="button"
                  onClick={handleToggleResumoPinned}
                  aria-pressed={isResumoPinned}
                  className={`flex items-center gap-2 px-3 py-2 border rounded transition-colors ${
                    isResumoPinned
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'bg-background text-foreground border-border hover:bg-secondary'
                  }`}
                  style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
                >
                  {isResumoPinned ? (
                    <Pin20Filled className="w-4 h-4" />
                  ) : (
                    <Pin20Regular className="w-4 h-4" />
                  )}
                  <span>{isResumoPinned ? 'Fixado' : 'Fixar'}</span>
                </button>
              </div>

              {/* Tabela de Resumo */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border">
                  {/* Cabeçalho */}
                  <thead>
                    <tr className="bg-accent border-b border-border">
                      <th 
                        className="text-left px-4 py-3 text-foreground border-r border-border"
                        style={{ 
                          fontSize: 'var(--text-caption)',
                          fontWeight: 'var(--font-weight-semi-bold)',
                          width: '60px',
                        }}
                      >
                        Nº
                      </th>
                      <th 
                        className="text-left px-4 py-3 text-foreground border-r border-border"
                        style={{ 
                          fontSize: 'var(--text-caption)',
                          fontWeight: 'var(--font-weight-semi-bold)',
                        }}
                      >
                        Descrição
                      </th>
                      <th 
                        className="text-right px-4 py-3 text-foreground border-r border-border"
                        style={{ 
                          fontSize: 'var(--text-caption)',
                          fontWeight: 'var(--font-weight-semi-bold)',
                          width: '150px',
                        }}
                      >
                        Valor
                      </th>
                      <th 
                        className="text-right px-4 py-3 text-foreground border-r border-border"
                        style={{ 
                          fontSize: 'var(--text-caption)',
                          fontWeight: 'var(--font-weight-semi-bold)',
                          width: '150px',
                        }}
                      >
                        Custo total
                      </th>
                      <th 
                        className="text-right px-4 py-3 text-foreground border-r border-border"
                        style={{ 
                          fontSize: 'var(--text-caption)',
                          fontWeight: 'var(--font-weight-semi-bold)',
                          width: '120px',
                        }}
                      >
                        % margem
                      </th>
                      <th 
                        className="text-right px-4 py-3 text-foreground"
                        style={{ 
                          fontSize: 'var(--text-caption)',
                          fontWeight: 'var(--font-weight-semi-bold)',
                          width: '120px',
                        }}
                      >
                        Incidência
                      </th>
                    </tr>
                  </thead>

                  {/* Corpo */}
                  <tbody>
                    {areas.map(area => {
                      const capitulosDaArea = itens.filter(item => item.tipo === 'capitulo' && item.parentId === area.id);
                      const capitulosVisiveis = capitulosDaArea.filter(
                        (capitulo) =>
                          capitulo.numero.trim() !== '' &&
                          capitulo.descricao.trim() !== '',
                      );
                      const totalArea = calcularTotalArea(area.id);
                      const custoArea = calcularTotalCustoArea(area.id);
                      const margemArea = totalArea > 0 ? ((totalArea - custoArea) / totalArea) * 100 : 0;
                      const incidenciaArea = subtotal > 0 ? ((totalArea / subtotal) * 100).toFixed(2) : '0.00';

                      return (
                        <Fragment key={area.id}>
                          {/* Linha da Área */}
                          <tr className="bg-primary/10 border-b border-border">
                            <td
                              className="px-4 py-3 text-foreground border-r border-border"
                              style={{
                                fontSize: 'var(--text-base)',
                                fontWeight: 'var(--font-weight-bold)',
                                fontFamily: 'monospace',
                              }}
                            >
                              {area.numero}
                            </td>
                            <td
                              className="px-4 py-3 text-foreground border-r border-border uppercase"
                              style={{
                                fontSize: 'var(--text-base)',
                                fontWeight: 'var(--font-weight-bold)',
                              }}
                            >
                              {area.descricao}
                            </td>
                            <td 
                              className="px-4 py-3 text-right text-foreground border-r border-border"
                              style={{ 
                                fontSize: 'var(--text-base)',
                                fontWeight: 'var(--font-weight-bold)',
                                backgroundColor: 'var(--readonly-cell)',
                              }}
                            >
                              {formatarMoeda(totalArea)}
                            </td>
                            <td 
                              className="px-4 py-3 text-right text-foreground border-r border-border"
                              style={{ 
                                fontSize: 'var(--text-base)',
                                fontWeight: 'var(--font-weight-bold)',
                                backgroundColor: 'var(--readonly-cell)',
                              }}
                            >
                              {formatarMoeda(custoArea)}
                            </td>
                            <td 
                              className="px-4 py-3 text-right text-foreground"
                              style={{ 
                                fontSize: 'var(--text-base)',
                                fontWeight: 'var(--font-weight-bold)',
                                backgroundColor: 'var(--readonly-cell)',
                              }}
                            >
                              <span className="inline-flex items-center justify-end gap-1.5">
                                {(() => {
                                  const color = resolveMarginColor(margemArea, marginAlertRanges);
                                  return color ? (
                                    <span
                                      className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: color }}
                                      title={`Margem: ${margemArea.toFixed(2)}%`}
                                    />
                                  ) : null;
                                })()}
                                {margemArea.toFixed(2)}%
                              </span>
                            </td>
                            <td 
                              className="px-4 py-3 text-right text-foreground"
                              style={{ 
                                fontSize: 'var(--text-base)',
                                fontWeight: 'var(--font-weight-bold)',
                                backgroundColor: 'var(--readonly-cell)',
                              }}
                            >
                              {incidenciaArea}%
                            </td>
                          </tr>

                          {/* Linhas dos Capítulos */}
                          {capitulosVisiveis.map(capitulo => {
                            const totalCapitulo = calcularTotalCapitulo(capitulo.id);
                            const custoCapitulo = calcularTotalCustoCapitulo(capitulo.id);
                            const margemCapitulo =
                              totalCapitulo > 0
                                ? ((totalCapitulo - custoCapitulo) / totalCapitulo) * 100
                                : 0;
                            const incidenciaCapitulo = subtotal > 0 ? ((totalCapitulo / subtotal) * 100).toFixed(2) : '0.00';

                            return (
                              <tr key={capitulo.id} className="border-b border-border hover:bg-accent/50">
                                <td 
                                  className="px-4 py-3 text-foreground border-r border-border"
                                  style={{ 
                                    fontSize: 'var(--text-base)',
                                    fontWeight: 'var(--font-weight-normal)',
                                    fontFamily: 'monospace',
                                  }}
                                >
                                  {capitulo.numero}
                                </td>
                                <td 
                                  className="px-4 py-3 text-foreground border-r border-border"
                                  style={{ 
                                    fontSize: 'var(--text-base)',
                                    fontWeight: 'var(--font-weight-normal)',
                                  }}
                                >
                                  {capitulo.descricao}
                                </td>
                                <td 
                                  className="px-4 py-3 text-right text-foreground border-r border-border"
                                  style={{ 
                                    fontSize: 'var(--text-base)',
                                    fontWeight: 'var(--font-weight-normal)',
                                    backgroundColor: 'var(--readonly-cell)',
                                  }}
                                >
                                  {formatarMoeda(totalCapitulo)}
                                </td>
                                <td 
                                  className="px-4 py-3 text-right text-foreground border-r border-border"
                                  style={{ 
                                    fontSize: 'var(--text-base)',
                                    fontWeight: 'var(--font-weight-normal)',
                                    backgroundColor: 'var(--readonly-cell)',
                                  }}
                                >
                                  {formatarMoeda(custoCapitulo)}
                                </td>
                                <td 
                                  className="px-4 py-3 text-right text-foreground"
                                  style={{ 
                                    fontSize: 'var(--text-base)',
                                    fontWeight: 'var(--font-weight-normal)',
                                    backgroundColor: 'var(--readonly-cell)',
                                  }}
                                >
                                  <span className="inline-flex items-center justify-end gap-1.5">
                                    {(() => {
                                      const color = resolveMarginColor(margemCapitulo, marginAlertRanges);
                                      return color ? (
                                        <span
                                          className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                                          style={{ backgroundColor: color }}
                                          title={`Margem: ${margemCapitulo.toFixed(2)}%`}
                                        />
                                      ) : null;
                                    })()}
                                    {margemCapitulo.toFixed(2)}%
                                  </span>
                                </td>
                                <td 
                                  className="px-4 py-3 text-right text-foreground"
                                  style={{ 
                                    fontSize: 'var(--text-base)',
                                    fontWeight: 'var(--font-weight-normal)',
                                    backgroundColor: 'var(--readonly-cell)',
                                  }}
                                >
                                  {incidenciaCapitulo}%
                                </td>
                              </tr>
                            );
                          })}
                        </Fragment>
                      );
                    })}

                    {/* Linha de Total */}
                    <tr className="bg-accent border-t-2 border-border">
                      <td 
                        className="px-4 py-4 text-foreground border-r border-border"
                        style={{ 
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-bold)',
                        }}
                      >
                      </td>
                      <td 
                        className="px-4 py-4 text-right text-foreground border-r border-border uppercase"
                        style={{ 
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-bold)',
                        }}
                      >
                        TOTAL
                      </td>
                      <td 
                        className="px-4 py-4 text-right text-primary border-r border-border"
                        style={{ 
                          fontSize: 'var(--text-lg)',
                          fontWeight: 'var(--font-weight-bold)',
                          backgroundColor: 'var(--readonly-cell)',
                        }}
                      >
                        {formatarMoeda(subtotal)}
                      </td>
                      <td 
                        className="px-4 py-4 text-right text-foreground border-r border-border"
                        style={{ 
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-bold)',
                          backgroundColor: 'var(--readonly-cell)',
                        }}
                      >
                        {formatarMoeda(custoTotal)}
                      </td>
                      <td 
                        className="px-4 py-4 text-right text-foreground"
                        style={{ 
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-bold)',
                          backgroundColor: 'var(--readonly-cell)',
                        }}
                      >
                        <span className="inline-flex items-center justify-end gap-1.5">
                          {(() => {
                            const color = resolveMarginColor(percentagemMargemTotal, marginAlertRanges);
                            return color ? (
                              <span
                                className="inline-block w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: color }}
                                title={`Margem total: ${percentagemMargemTotal.toFixed(2)}%`}
                              />
                            ) : null;
                          })()}
                          {percentagemMargemTotal.toFixed(2)}%
                        </span>
                      </td>
                      <td 
                        className="px-4 py-4 text-right text-foreground"
                        style={{ 
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-bold)',
                          backgroundColor: 'var(--readonly-cell)',
                        }}
                      >
                        100,00%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Bloco de Detalhe de Custo */}
              <div className="mt-4">
                  <table className="w-full border-collapse border border-border">
                    {/* Cabeçalho - Fundo azul claro */}
                    <thead>
                      <tr className="bg-cost-detail-bg border-b border-border">
                        <th 
                          className="text-right px-4 py-3 text-foreground border-r border-border bg-muted"
                          style={{ 
                            fontSize: 'var(--text-caption)',
                            fontWeight: 'var(--font-weight-semi-bold)',
                            width: '20%',
                          }}
                        >
                          Valor compra
                        </th>
                        <th 
                          className="text-right px-4 py-3 text-foreground border-r border-border bg-muted"
                          style={{ 
                            fontSize: 'var(--text-caption)',
                            fontWeight: 'var(--font-weight-semi-bold)',
                            width: '20%',
                          }}
                        >
                          % margem
                        </th>
                        <th 
                          className="text-right px-4 py-3 text-foreground border-r border-border bg-muted"
                          style={{ 
                            fontSize: 'var(--text-caption)',
                            fontWeight: 'var(--font-weight-semi-bold)',
                            width: '20%',
                          }}
                        >
                          % lucro
                        </th>
                        <th 
                          className="text-right px-4 py-3 text-foreground border-r border-border bg-muted"
                          style={{ 
                            fontSize: 'var(--text-caption)',
                            fontWeight: 'var(--font-weight-semi-bold)',
                            width: '20%',
                          }}
                        >
                          Área total (m²)
                        </th>
                        <th 
                          className="text-right px-4 py-3 text-foreground bg-muted"
                          style={{ 
                            fontSize: 'var(--text-caption)',
                            fontWeight: 'var(--font-weight-semi-bold)',
                            width: '20%',
                          }}
                        >
                          €/m²
                        </th>
                      </tr>
                    </thead>
                    {/* Valores - Fundo cinzento */}
                    <tbody>
                      <tr className="bg-cost-detail-readonly">
                        <td 
                          className="px-4 py-3 text-right text-foreground border-r border-border bg-muted"
                          style={{ 
                            fontSize: 'var(--text-base)',
                            fontWeight: 'var(--font-weight-bold)',
                          }}
                        >
                          {formatarMoeda(custoTotal)}
                        </td>
                        <td
                          className="px-4 py-3 text-right text-foreground border-r border-border bg-muted"
                          style={{
                            fontSize: 'var(--text-base)',
                            fontWeight: 'var(--font-weight-bold)',
                          }}
                        >
                          <span className="inline-flex items-center justify-end gap-1.5">
                            {(() => {
                              const color = resolveMarginColor(percentagemMargemTotal, marginAlertRanges);
                              return color ? (
                                <span
                                  className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: color }}
                                  title={`Margem: ${percentagemMargemTotal.toFixed(2)}%`}
                                />
                              ) : null;
                            })()}
                            {percentagemMargemTotal.toFixed(2)}%
                          </span>
                        </td>
                        <td
                          className="px-4 py-3 text-right text-foreground border-r border-border bg-muted"
                          style={{
                            fontSize: 'var(--text-base)',
                            fontWeight: 'var(--font-weight-bold)',
                          }}
                        >
                          {formatarMoeda(lucroTotal)}
                        </td>
                        <td 
                          className="px-4 py-3 text-right text-foreground border-r border-border bg-muted"
                          style={{ 
                            fontSize: 'var(--text-base)',
                            fontWeight: 'var(--font-weight-bold)',
                          }}
                        >
                          {isReadOnly ? (
                            areaTotalM2 !== null ? formatarQuantidade(areaTotalM2) : '—'
                          ) : (
                            <input
                              type="text"
                              inputMode="decimal"
                              value={areaTotalLojaInputValue}
                              onChange={(event) => {
                                const value = event.target.value;
                                setNumericDraft(areaTotalLojaKey, value);
                                setAreaTotalLoja(parseNumeroInputNullable(value));
                              }}
                              onFocus={() => {
                                if (getNumericDraft(areaTotalLojaKey) === undefined) {
                                  setNumericDraft(
                                    areaTotalLojaKey,
                                    areaTotalLoja !== null ? areaTotalLoja.toString() : '',
                                  );
                                }
                              }}
                              onBlur={() => clearNumericDraft(areaTotalLojaKey)}
                              placeholder="0"
                              className="w-full bg-background border border-border rounded px-2 py-1 text-right text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                              style={{ borderRadius: 'var(--radius-button)' }}
                            />
                          )}
                        </td>
                        <td 
                          className="px-4 py-3 text-right text-foreground bg-muted"
                          style={{ 
                            fontSize: 'var(--text-base)',
                            fontWeight: 'var(--font-weight-bold)',
                          }}
                        >
                          {precoPorMetroQuadrado === null
                            ? '—'
                            : formatarMoeda(precoPorMetroQuadrado)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
              </div>
            </div>

            {/* Card: Editor de Capítulos e Linhas */}
            <div className="bg-card border border-border p-6" style={{ borderRadius: 'var(--radius-card)' }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex flex-wrap items-center gap-4">
                  <h3 
                    className="text-foreground"
                    style={{ 
                      fontSize: 'var(--text-section-title)',
                      fontWeight: 'var(--font-weight-semi-bold)'
                    }}
                  >
                    Detalhe do Orçamento
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 text-secondary-text">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 border border-border bg-background"
                      style={{ borderRadius: '999px', fontSize: 'var(--text-caption)' }}
                      title="Coluna editável"
                    >
                      <Edit20Regular className="w-3 h-3" />
                      Editável
                    </span>
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 border border-border bg-muted"
                      style={{ borderRadius: '999px', fontSize: 'var(--text-caption)' }}
                      title="Coluna calculada automaticamente"
                    >
                      <LockClosed20Regular className="w-3 h-3" />
                      Calculado
                    </span>
                  </div>
                </div>
                {!isReadOnly && (
                  <button
                    onClick={adicionarArea}
                    disabled={isLoading}
                    className="px-3 py-2 bg-secondary hover:bg-accent border border-border text-foreground rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
                  >
                    <Add20Regular className="w-4 h-4" />
                    Adicionar Área
                  </button>
                )}
              </div>

              {/* Áreas e seus Capítulos */}
              <div className="space-y-8">
                {areas.map(area => {
                  const capitulosDaArea = itens.filter(item => item.tipo === 'capitulo' && item.parentId === area.id);
                  const areaChapterTypes = area.budgetAreaTypeId
                    ? chapterTypesByArea.get(area.budgetAreaTypeId) ?? []
                    : orderedChapterTypes;
                  const chapterNameOptions = Array.from(
                    new Map(
                      areaChapterTypes.map((chapter) => [
                        normalizeKey(chapter.name),
                        {
                          value: chapter.name,
                          label: `${chapter.code} - ${chapter.name}`,
                        },
                      ]),
                    ).values(),
                  );
                  const podeAdicionarCapitulo =
                    !isReadOnly && !isLoading && area.descricao.trim() !== '';
                  const totalArea = calcularTotalArea(area.id);

                  return (
                    <div key={area.id} className="space-y-4">
                      {/* Cabeçalho da Área */}
                      <div 
                        className="bg-primary/10 border-l-4 border-primary px-5 py-4 flex flex-wrap justify-between items-center gap-3"
                        style={{ borderRadius: 'var(--radius)' }}
                      >
                        {isReadOnly ? (
                          <h4 
                            className="text-foreground uppercase"
                            style={{ 
                              fontSize: 'var(--text-lg)',
                              fontWeight: 'var(--font-weight-bold)',
                            }}
                          >
                            {area.descricao || 'Área por definir'}
                          </h4>
                        ) : (
                          <div style={{ flex: 1, minWidth: '320px', maxWidth: '980px' }}>
                            <ComboboxInput
                              value={area.descricao}
                              onValueChange={(value) =>
                                atualizarAreaDescricao(area.id, value)
                              }
                              options={areaNameOptions}
                              placeholder="Área"
                              tooltipValue={area.descricao}
                              tooltipOnOverflow
                              className="w-full px-3 py-2 border border-border rounded bg-background text-foreground uppercase focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                              style={{ 
                                borderRadius: 'var(--radius-button)',
                                fontSize: 'var(--text-lg)',
                                fontWeight: 'var(--font-weight-bold)',
                                width: 'clamp(320px, 58vw, 980px)',
                                maxWidth: '100%',
                              }}
                            />
                          </div>
                        )}
                        <div className="flex items-center gap-4 ml-auto">
                          {!isReadOnly && (
                            <button
                              onClick={() => adicionarCapitulo(area.id)}
                              disabled={!podeAdicionarCapitulo}
                              className="px-3 py-2 bg-secondary hover:bg-accent border border-border text-foreground rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
                            >
                              <Add20Regular className="w-4 h-4" />
                              Adicionar Capítulo
                            </button>
                          )}
                          {!isReadOnly && (
                            <button
                              onClick={() => removerArea(area.id)}
                              disabled={isLoading}
                              className="px-3 py-2 border border-border text-destructive hover:bg-destructive/10 rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                              style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
                            >
                              <Delete20Regular className="w-4 h-4" />
                              Eliminar Área
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Capítulos e Linhas */}
                      {capitulosDaArea.map(capitulo => {
                        const linhasDoCapitulo = itens.filter(item => item.tipo === 'linha' && item.parentId === capitulo.id);
                        const totalCapitulo = calcularTotalCapitulo(capitulo.id);
                        const custoCapitulo = calcularTotalCustoCapitulo(capitulo.id);
                        const gastosCapitulo = calcularGastosCapitulo(capitulo.id);
                        const margemCapitulo =
                          totalCapitulo > 0
                            ? ((totalCapitulo - custoCapitulo) / totalCapitulo) * 100
                            : 0;
                        const incidenciaCapitulo =
                          subtotal > 0 ? (totalCapitulo / subtotal) * 100 : 0;
                        const chapterNumber = capitulo.numero;
                        const chapterName = capitulo.descricao;
                        const capituloSelecionado =
                          chapterName.trim() !== '';

                        return (
                          <div key={capitulo.id} className="ml-4 space-y-2">
                            {/* Cabeçalho do Capítulo */}
                            <div 
                              className="bg-accent border border-border px-4 py-3 flex justify-between items-center"
                              style={{ borderRadius: 'var(--radius)' }}
                            >
                              <div
                                className="flex items-center gap-3"
                                style={{ flex: 1, minWidth: 0 }}
                              >
                                {isReadOnly ? (
                                  <>
                                    <span 
                                      className="text-foreground"
                                      style={{ 
                                        fontSize: 'var(--text-base)',
                                        fontWeight: 'var(--font-weight-bold)',
                                        fontFamily: 'monospace',
                                      }}
                                    >
                                      {chapterNumber || '—'}
                                    </span>
                                    <span 
                                      className="text-foreground uppercase"
                                      style={{ 
                                        fontSize: 'var(--text-base)',
                                        fontWeight: 'var(--font-weight-semi-bold)',
                                      }}
                                    >
                                      {chapterName || 'Capítulo por definir'}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <input
                                      type="text"
                                      value={chapterNumber}
                                      onChange={(event) =>
                                        atualizarItem(
                                          capitulo.id,
                                          'numero',
                                          event.target.value,
                                        )
                                      }
                                      placeholder="Nº"
                                      className="min-w-[56px] max-w-[96px] px-2 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                      style={{ 
                                        borderRadius: 'var(--radius-button)',
                                        fontSize: 'var(--text-base)',
                                        fontWeight: 'var(--font-weight-bold)',
                                        fontFamily: 'monospace',
                                      }}
                                    />
                                    <div style={{ flex: 1, minWidth: '320px', maxWidth: '720px' }}>
                                      <ComboboxInput
                                        value={chapterName}
                                        onValueChange={(value) =>
                                          atualizarNomeCapitulo(
                                            capitulo.id,
                                            value,
                                          )
                                        }
                                        options={chapterNameOptions}
                                        placeholder="Nome do capítulo"
                                        tooltipValue={chapterName}
                                        tooltipOnOverflow
                                        className="px-3 py-2 border border-border rounded bg-background text-foreground uppercase focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        style={{ 
                                          borderRadius: 'var(--radius-button)',
                                          fontSize: 'var(--text-base)',
                                          fontWeight: 'var(--font-weight-semi-bold)',
                                          width: '100%',
                                        }}
                                      />
                                    </div>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex flex-col items-end">
                                  <span 
                                    className="text-foreground"
                                    style={{ 
                                      fontSize: 'var(--text-base)',
                                      fontWeight: 'var(--font-weight-bold)',
                                    }}
                                  >
                                    {formatarMoeda(totalCapitulo)}
                                  </span>
                                  <span
                                    className="text-secondary-text"
                                    style={{
                                      fontSize: 'var(--text-caption)',
                                      fontWeight: 'var(--font-weight-normal)',
                                    }}
                                  >
                                    Custo total: {formatarMoeda(custoCapitulo)}
                                  </span>
                                  <span
                                    className="text-secondary-text"
                                    style={{
                                      fontSize: 'var(--text-caption)',
                                      fontWeight: 'var(--font-weight-normal)',
                                    }}
                                  >
                                    Margem: {margemCapitulo.toFixed(2)}%
                                  </span>
                                  <span
                                    className="text-secondary-text"
                                    style={{
                                      fontSize: 'var(--text-caption)',
                                      fontWeight: 'var(--font-weight-normal)',
                                    }}
                                  >
                                    Incidência: {incidenciaCapitulo.toFixed(2)}%
                                  </span>
                                  <span
                                    className="text-secondary-text"
                                    style={{
                                      fontSize: 'var(--text-caption)',
                                      fontWeight: 'var(--font-weight-normal)',
                                    }}
                                  >
                                    Gastos previstos: {formatarMoeda(gastosCapitulo)}
                                  </span>
                                </div>
                                {!isReadOnly && (
                                  <button
                                    onClick={() => removerCapitulo(capitulo.id)}
                                    disabled={isLoading}
                                    className="px-3 py-2 border border-border text-destructive hover:bg-destructive/10 rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
                                  >
                                    <Delete20Regular className="w-4 h-4" />
                                    Eliminar Capítulo
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Nota do capítulo (opcional) */}
                            {capituloSelecionado && (
                              <div className="ml-8 mb-2">
                                {isReadOnly ? (
                                  capitulo.notaCapitulo?.trim() ? (
                                    <div
                                      className="px-3 py-2 bg-accent/30 border border-border text-secondary-text"
                                      style={{
                                        borderRadius: 'var(--radius)',
                                        fontSize: 'var(--text-caption)',
                                        whiteSpace: 'pre-wrap',
                                      }}
                                    >
                                      <span
                                        className="text-foreground"
                                        style={{ fontWeight: 'var(--font-weight-semi-bold)' }}
                                      >
                                        Nota:
                                      </span>{' '}
                                      {capitulo.notaCapitulo}
                                    </div>
                                  ) : null
                                ) : (
                                  <textarea
                                    value={capitulo.notaCapitulo ?? ''}
                                    onChange={(event) =>
                                      atualizarItem(
                                        capitulo.id,
                                        'notaCapitulo',
                                        event.target.value,
                                      )
                                    }
                                    placeholder="Nota do capítulo (opcional)"
                                    rows={capitulo.notaCapitulo?.trim() ? 3 : 1}
                                    className="w-full px-3 py-2 bg-background border border-border text-foreground resize-y"
                                    style={{
                                      borderRadius: 'var(--radius)',
                                      fontSize: 'var(--text-caption)',
                                    }}
                                  />
                                )}
                              </div>
                            )}

                            {/* Tabela de Linhas */}
                            {(() => {
                              if (!capituloSelecionado) {
                                return (
                                  <div
                                    className="border border-dashed border-border px-4 py-3 text-secondary-text"
                                    style={{ borderRadius: 'var(--radius)' }}
                                  >
                                    Preencha o nome do capítulo para adicionar linhas.
                                  </div>
                                );
                              }

                              // Criar linha vazia apenas quando o capítulo ainda não tem linhas.
                              // Evita inserir placeholder automático após importações.
                              if (linhasDoCapitulo.length === 0 && capitulo.id) {
                                setTimeout(() => criarLinhaVazia(capitulo.id), 0);
                              }
                              
                              return (
                                <div className="ml-8 overflow-x-auto">
                                  <table className="w-full border-collapse">
                                    <thead>
                                      <tr className="bg-accent/50 border-b border-border">
                                        <th 
                                          className="text-left px-3 py-2 text-foreground"
                                          style={{
                                            fontSize: 'var(--text-caption)',
                                            fontWeight: 'var(--font-weight-semi-bold)',
                                            width: '95px',
                                            minWidth: '95px',
                                          }}
                                        >
                                          <span className="inline-flex items-center gap-1" title="Editável">
                                            Nº
                                            <Edit20Regular className="w-3 h-3 text-secondary-text" />
                                          </span>
                                        </th>
                                        <th 
                                          className="text-left px-3 py-2 text-foreground"
                                          style={{ 
                                            fontSize: 'var(--text-caption)',
                                            fontWeight: 'var(--font-weight-semi-bold)',
                                            minWidth: '320px',
                                          }}
                                        >
                                          <span className="inline-flex items-center gap-1" title="Editável">
                                            Descrição
                                            <Edit20Regular className="w-3 h-3 text-secondary-text" />
                                          </span>
                                        </th>
                                        <th 
                                          className="text-left px-3 py-2 text-foreground"
                                          style={{ 
                                            fontSize: 'var(--text-caption)',
                                            fontWeight: 'var(--font-weight-semi-bold)',
                                            width: '80px',
                                          }}
                                        >
                                          <span className="inline-flex items-center gap-1" title="Editável">
                                            Un.
                                            <Edit20Regular className="w-3 h-3 text-secondary-text" />
                                          </span>
                                        </th>
                                        <th 
                                          className="text-right px-3 py-2 text-foreground"
                                          style={{ 
                                            fontSize: 'var(--text-caption)',
                                            fontWeight: 'var(--font-weight-semi-bold)',
                                            width: '120px',
                                          }}
                                        >
                                          <span className="inline-flex items-center gap-1" title="Editável">
                                            Qtd.
                                            <Edit20Regular className="w-3 h-3 text-secondary-text" />
                                          </span>
                                        </th>
                                        <th 
                                          className="text-right px-3 py-2 text-foreground"
                                          style={{ 
                                            fontSize: 'var(--text-caption)',
                                            fontWeight: 'var(--font-weight-semi-bold)',
                                            width: '120px',
                                          }}
                                        >
                                          <span className="inline-flex items-center gap-1" title="Calculado automaticamente">
                                            Preço Unit.
                                            <LockClosed20Regular className="w-3 h-3 text-secondary-text" />
                                          </span>
                                        </th>
                                        <th 
                                          className="text-right px-3 py-2 text-foreground"
                                          style={{ 
                                            fontSize: 'var(--text-caption)',
                                            fontWeight: 'var(--font-weight-semi-bold)',
                                            width: '130px',
                                          }}
                                        >
                                          <span className="inline-flex items-center gap-1" title="Calculado automaticamente">
                                            Total
                                            <LockClosed20Regular className="w-3 h-3 text-secondary-text" />
                                          </span>
                                        </th>
                                        {/* Colunas de Detalhe de Custo - G, H, I, J */}
                                        <th 
                                          className="text-right px-3 py-2 text-foreground"
                                          style={{ 
                                            fontSize: 'var(--text-caption)',
                                            fontWeight: 'var(--font-weight-semi-bold)',
                                            width: '100px',
                                            backgroundColor: 'var(--cost-detail-bg)',
                                          }}
                                        >
                                          <span className="inline-flex items-center gap-1" title="Editável">
                                            Margem (%)
                                            <Edit20Regular className="w-3 h-3 text-secondary-text" />
                                          </span>
                                        </th>
                                        <th 
                                          className="text-right px-3 py-2 text-foreground"
                                          style={{ 
                                            fontSize: 'var(--text-caption)',
                                            fontWeight: 'var(--font-weight-semi-bold)',
                                            width: '130px',
                                            backgroundColor: 'var(--cost-detail-bg)',
                                          }}
                                        >
                                          <span className="inline-flex items-center gap-1" title="Editável">
                                            Preço Custo Unit.
                                            <Edit20Regular className="w-3 h-3 text-secondary-text" />
                                          </span>
                                        </th>
                                        <th 
                                          className="text-right px-3 py-2 text-foreground"
                                          style={{ 
                                            fontSize: 'var(--text-caption)',
                                            fontWeight: 'var(--font-weight-semi-bold)',
                                            width: '130px',
                                            backgroundColor: 'var(--cost-detail-readonly)',
                                          }}
                                        >
                                          <span className="inline-flex items-center gap-1" title="Calculado automaticamente">
                                            Total Custo
                                            <LockClosed20Regular className="w-3 h-3 text-secondary-text" />
                                          </span>
                                        </th>
                                        <th 
                                          className="text-right px-3 py-2 text-foreground"
                                          style={{ 
                                            fontSize: 'var(--text-caption)',
                                            fontWeight: 'var(--font-weight-semi-bold)',
                                            width: '130px',
                                            backgroundColor: 'var(--cost-detail-readonly)',
                                          }}
                                        >
                                          <span className="inline-flex items-center gap-1" title="Calculado automaticamente">
                                            Margem Lucro Real (%)
                                            <LockClosed20Regular className="w-3 h-3 text-secondary-text" />
                                          </span>
                                        </th>
                                        <th 
                                          className="text-left px-3 py-2 text-foreground"
                                          style={{ 
                                            fontSize: 'var(--text-caption)',
                                            fontWeight: 'var(--font-weight-semi-bold)',
                                            minWidth: '200px',
                                          }}
                                        >
                                          <span className="inline-flex items-center gap-1" title="Editável">
                                            Execução
                                            <Edit20Regular className="w-3 h-3 text-secondary-text" />
                                          </span>
                                        </th>
                                        <th 
                                          className="text-center px-3 py-2"
                                          style={{ width: '60px' }}
                                        ></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {linhasDoCapitulo.map((linha, index) => {
                                        const precoUnitarioCalculado = calcularPrecoUnitario(linha);
                                        const total = calcularTotal(linha.quantidade, precoUnitarioCalculado);
                                        const totalCusto = calcularTotalCusto(linha.quantidade, linha.precoCustoUnitario || 0);
                                        const margemLucroReal = calcularMargemLucroReal(total, totalCusto);
                                        const bgClass = index % 2 === 0 ? 'bg-background' : 'bg-card';
                                        const estaVazia = linhaEstaVazia(linha);
                                        const nextLine = linhasDoCapitulo[index + 1];
                                        const nextLineId = nextLine?.id;
                                        const nextLineIsEmpty = nextLine ? linhaEstaVazia(nextLine) : false;
                                        const margemKey = `${linha.id}:margem`;
                                        const custoKey = `${linha.id}:precoCustoUnitario`;
                                        const precoVendaKey = `${linha.id}:precoUnitario`;
                                        const margemValor = linha.margem ?? 0;
                                        const custoValor = linha.precoCustoUnitario ?? 0;
                                        const sugestaoReferencia = linha.sugestaoCustoBudgetReferencia?.trim();
                                        const sugestaoTooltip = sugestaoReferencia
                                          ? `Preço baseado no orçamento ${sugestaoReferencia}`
                                          : undefined;
                                        const margemDisplay = margemValor ? formatarPercentagem(margemValor) : '';
                                        const custoDisplay = custoValor ? formatarMoeda(custoValor) : '';
                                        const margemInputValue =
                                          getNumericDraft(margemKey) ?? margemDisplay;
                                        const custoInputValue =
                                          getNumericDraft(custoKey) ?? custoDisplay;
                                        const precoVendaInputValue =
                                          getNumericDraft(precoVendaKey) ??
                                          (precoUnitarioCalculado ? precoUnitarioCalculado.toString() : '');
                                        const precoVendaEditando = precoVendaEmEdicaoId === linha.id;
                                        const hasQuantidadeFormula = Boolean(
                                          linha.quantidadeFormula && linha.quantidadeFormula.trim() !== '',
                                        );
                                        const formulaNote = linha.quantidadeFormulaNota?.trim();
                                        const formulaPreview = hasQuantidadeFormula
                                          ? [
                                              `fx: ${linha.quantidadeFormula} = ${formatarQuantidade(linha.quantidade)}`,
                                              formulaNote ? `Nota: ${formulaNote}` : null,
                                            ]
                                              .filter((value): value is string => value !== null)
                                              .join('\n')
                                          : 'Adicionar fórmula';
                                        const handleEnterNavigation = (event: React.KeyboardEvent<HTMLElement>) => {
                                          const isEnterKey =
                                            event.key === 'Enter' ||
                                            event.key === 'NumpadEnter' ||
                                            event.key === 'Return' ||
                                            event.code === 'Enter' ||
                                            event.code === 'NumpadEnter';

                                          if (!isEnterKey || isReadOnly) {
                                            return;
                                          }

                                          event.preventDefault();

                                          if (event.shiftKey) {
                                            if (nextLineId && nextLineIsEmpty) {
                                              setTimeout(() => focarDescricaoLinha(nextLineId), 0);
                                              return;
                                            }

                                            const newLineId = criarLinhaVazia(capitulo.id);
                                            if (newLineId) {
                                              setTimeout(() => focarDescricaoLinha(newLineId), 0);
                                            }
                                            return;
                                          }

                                          if (nextLineId) {
                                            setTimeout(() => focarDescricaoLinha(nextLineId), 0);
                                          }
                                        };

                                        return (
                                          <tr key={linha.id} className={`border-b border-border ${bgClass}`}>
                                          {/* Nº */}
                                          <td 
                                            className={`px-3 py-2 ${isReadOnly ? 'calculated-cell' : 'editable-cell'} ${estaVazia ? 'text-secondary-text' : 'text-foreground'}`}
                                            style={{ 
                                              fontSize: 'var(--text-base)',
                                              fontFamily: 'monospace',
                                            }}
                                          >
                                            {isReadOnly ? (
                                              <span>{estaVazia ? '' : linha.numero}</span>
                                            ) : (
                                              <input
                                                type="text"
                                                value={linha.numero}
                                                onChange={(e) => atualizarItem(linha.id, 'numero', e.target.value)}
                                                onKeyDown={handleEnterNavigation}
                                                disabled={isReadOnly}
                                                placeholder={estaVazia ? 'Nº' : ''}
                                                className={`w-full bg-white border border-border rounded px-1 focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/50 ${estaVazia ? 'text-secondary-text placeholder:text-secondary-text' : 'text-foreground'} disabled:cursor-not-allowed`}
                                                style={{ fontSize: 'var(--text-base)', fontFamily: 'monospace' }}
                                              />
                                            )}
                                          </td>

                                          {/* Descrição */}
                                          <td
                                            className="px-3 py-2 editable-cell"
                                            style={{ minWidth: '320px' }}
                                          >
                                            <textarea
                                              value={linha.descricao}
                                              onChange={(e) => atualizarItem(linha.id, 'descricao', e.target.value)}
                                              onInput={(e) => ajustarAlturaTextarea(e.currentTarget)}
                                              onKeyDown={handleEnterNavigation}
                                              ref={(element) => ajustarAlturaTextarea(element)}
                                              disabled={isReadOnly}
                                              placeholder={estaVazia ? 'Descreva o artigo…' : ''}
                                              rows={1}
                                              data-line-id={linha.id}
                                              data-line-field="descricao"
                                              className={`w-full bg-white border border-border rounded px-1 focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/50 resize-y ${estaVazia ? 'text-secondary-text placeholder:text-secondary-text' : 'text-foreground'} disabled:cursor-not-allowed`}
                                              style={{ fontSize: 'var(--text-base)' }}
                                            />
                                            {(linha.isPercentualValorObra || isPercentualValorObraTarget(linha.descricao, percentageRules)) && (
                                              <div
                                                className="mt-2 flex flex-wrap items-center gap-2 text-secondary-text"
                                                style={{ fontSize: 'var(--text-caption)' }}
                                              >
                                                <label className="inline-flex items-center gap-1.5">
                                                  <input
                                                    type="checkbox"
                                                    checked={linha.isPercentualValorObra ?? false}
                                                    disabled={isReadOnly}
                                                    onChange={(event) =>
                                                      atualizarItem(
                                                        linha.id,
                                                        'isPercentualValorObra',
                                                        event.target.checked,
                                                      )
                                                    }
                                                  />
                                                  % valor global da obra
                                                </label>
                                                {linha.isPercentualValorObra && (
                                                  <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.01"
                                                    value={linha.percentagemValorObra ?? DEFAULT_PERCENTUAL_VALOR_OBRA}
                                                    disabled={isReadOnly}
                                                    onChange={(event) =>
                                                      atualizarItem(
                                                        linha.id,
                                                        'percentagemValorObra',
                                                        Number.parseFloat(event.target.value) || 0,
                                                      )
                                                    }
                                                    className="w-20 bg-white border border-border rounded px-1 text-right focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/50 disabled:cursor-not-allowed"
                                                    style={{ fontSize: 'var(--text-caption)' }}
                                                    aria-label="Percentagem sobre o valor global da obra"
                                                  />
                                                )}
                                              </div>
                                            )}
                                          </td>

                                          {/* Unidade */}
                                          <td className="px-3 py-2 editable-cell">
                                            <ComboboxInput
                                              value={linha.unidade}
                                              onValueChange={(value) => atualizarItem(linha.id, 'unidade', value)}
                                              options={unitOptions}
                                              disabled={isReadOnly}
                                              className={`w-full bg-white border border-border rounded px-1 focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/50 ${estaVazia ? 'text-secondary-text' : 'text-foreground'} disabled:cursor-not-allowed`}
                                              style={{ fontSize: 'var(--text-base)', minWidth: '80px' }}
                                            />
                                          </td>

                                          {/* Quantidade */}
                                          <td className="px-3 py-2 text-right editable-cell">
                                            <div className="flex items-center justify-end gap-2">
                                              <input
                                                type="number"
                                                value={linha.quantidade === 0 ? '' : linha.quantidade}
                                                onChange={(e) => atualizarItem(linha.id, 'quantidade', parseFloat(e.target.value) || 0)}
                                                disabled={isReadOnly || hasQuantidadeFormula}
                                                placeholder={estaVazia ? '0' : ''}
                                                className={`flex-1 min-w-0 bg-white border border-border rounded px-1 text-right focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/50 ${estaVazia ? 'text-secondary-text placeholder:text-secondary-text' : 'text-foreground'} disabled:cursor-not-allowed`}
                                                style={{ fontSize: 'var(--text-base)' }}
                                                step="0.01"
                                              />
                                              <button
                                                type="button"
                                                onClick={() => abrirFormulaQuantidade(linha)}
                                                title={formulaPreview}
                                                className={`px-2 py-1 border border-border rounded text-xs font-semibold ${hasQuantidadeFormula ? 'bg-primary/10 text-primary' : 'bg-secondary text-secondary-text'} ${isReadOnly ? 'opacity-70' : ''}`}
                                                style={{ borderRadius: 'var(--radius)' }}
                                              >
                                                fx
                                              </button>
                                            </div>
                                          </td>

                                          {/* Preço Unitário */}
                                          <td 
                                            className={`px-3 py-2 text-right calculated-cell ${estaVazia ? 'text-secondary-text' : 'text-foreground'}`}
                                            style={{ fontSize: 'var(--text-base)' }}
                                          >
                                            {estaVazia ? (
                                              '—'
                                            ) : (
                                              <div className="flex items-center justify-end gap-2">
                                                {precoVendaEditando && !isReadOnly ? (
                                                  <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={precoVendaInputValue}
                                                    onChange={(e) =>
                                                      setNumericDraft(precoVendaKey, e.target.value)
                                                    }
                                                    onKeyDown={(event) => {
                                                      if (event.key !== 'Enter') {
                                                        return;
                                                      }
                                                      event.preventDefault();
                                                      const novoValor = parseNumeroInput(precoVendaInputValue);
                                                      atualizarPrecoVenda(linha.id, novoValor);
                                                      clearNumericDraft(precoVendaKey);
                                                      setPrecoVendaEmEdicaoId(null);
                                                    }}
                                                    onBlur={() => {
                                                      const novoValor = parseNumeroInput(precoVendaInputValue);
                                                      atualizarPrecoVenda(linha.id, novoValor);
                                                      clearNumericDraft(precoVendaKey);
                                                      setPrecoVendaEmEdicaoId(null);
                                                    }}
                                                    disabled={isReadOnly}
                                                    className="w-full bg-white border border-border rounded px-1 text-right focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/50 disabled:cursor-not-allowed"
                                                    style={{ fontSize: 'var(--text-base)', maxWidth: '110px' }}
                                                  />
                                                ) : (
                                                  <span>{formatarMoeda(precoUnitarioCalculado)}</span>
                                                )}
                                                {!isReadOnly && !precoVendaEditando && (
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      setPrecoVendaEmEdicaoId(linha.id);
                                                      if (getNumericDraft(precoVendaKey) === undefined) {
                                                        setNumericDraft(
                                                          precoVendaKey,
                                                          precoUnitarioCalculado ? precoUnitarioCalculado.toString() : '',
                                                        );
                                                      }
                                                    }}
                                                    className="flex-shrink-0 text-secondary-text hover:text-foreground transition-colors"
                                                    title="Alterar Preço Unit. com cálculo inverso da margem"
                                                    aria-label="Alterar Preço Unit. com cálculo inverso da margem"
                                                  >
                                                    <Edit20Regular className="w-4 h-4" />
                                                  </button>
                                                )}
                                              </div>
                                            )}
                                          </td>

                                          {/* Total */}
                                          <td 
                                            className={`px-3 py-2 text-right calculated-cell ${estaVazia ? 'text-secondary-text' : 'text-foreground'}`}
                                            style={{ 
                                              fontSize: 'var(--text-base)',
                                              fontWeight: estaVazia ? 'var(--font-weight-normal)' : 'var(--font-weight-bold)',
                                            }}
                                          >
                                            {estaVazia ? '—' : formatarMoeda(total)}
                                          </td>

                                          {/* Colunas de Detalhe de Custo - G, H, I, J */}
                                          {/* G - Margem (%) */}
                                          <td 
                                            className="px-3 py-2 text-right editable-cell"
                                            style={{ backgroundColor: 'var(--cost-detail-bg)' }}
                                          >
                                            <input
                                              type="text"
                                              inputMode="decimal"
                                              value={margemInputValue}
                                              onChange={(e) => {
                                                const newValue = e.target.value;
                                                setNumericDraft(margemKey, newValue);
                                                atualizarItem(
                                                  linha.id,
                                                  'margem',
                                                  normalizarMargemPercentual(
                                                    parseNumeroInput(newValue),
                                                  ) ?? 0,
                                                );
                                              }}
                                              onFocus={() => {
                                                if (isReadOnly) {
                                                  return;
                                                }
                                                if (getNumericDraft(margemKey) === undefined) {
                                                  setNumericDraft(
                                                    margemKey,
                                                    margemValor ? margemValor.toString() : '',
                                                  );
                                                }
                                              }}
                                              onBlur={() => clearNumericDraft(margemKey)}
                                              disabled={isReadOnly}
                                              placeholder="0,00%"
                                              className={`w-full bg-white border border-border rounded px-1 text-right focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/50 ${estaVazia ? 'text-secondary-text placeholder:text-secondary-text' : 'text-foreground placeholder:text-foreground'} disabled:cursor-not-allowed`}
                                              style={{ fontSize: 'var(--text-base)' }}
                                            />
                                          </td>

                                          {/* H - Preço Custo Unit. (€) */}
                                          <td 
                                            className="px-3 py-2 text-right editable-cell"
                                            style={{ backgroundColor: 'var(--cost-detail-bg)' }}
                                          >
                                            <div className="flex items-center gap-1">
                                              <input
                                                type="text"
                                                inputMode="decimal"
                                                value={custoInputValue}
                                                onChange={(e) => {
                                                  const newValue = e.target.value;
                                                  setNumericDraft(custoKey, newValue);
                                                  atualizarItem(
                                                    linha.id,
                                                    'precoCustoUnitario',
                                                    parseNumeroInput(newValue),
                                                  );
                                                }}
                                                onFocus={() => {
                                                  if (isReadOnly) {
                                                    return;
                                                  }
                                                  if (getNumericDraft(custoKey) === undefined) {
                                                    setNumericDraft(
                                                      custoKey,
                                                      custoValor ? custoValor.toString() : '',
                                                    );
                                                  }
                                                }}
                                                onBlur={() => clearNumericDraft(custoKey)}
                                                disabled={isReadOnly}
                                                placeholder="0,00 €"
                                                className={`flex-1 bg-white border border-border rounded px-1 text-right focus:outline-none focus:ring-1 focus:ring-primary/30 focus:border-primary/50 ${estaVazia ? 'text-secondary-text placeholder:text-secondary-text' : 'text-foreground placeholder:text-foreground'} disabled:cursor-not-allowed`}
                                                style={{ fontSize: 'var(--text-base)' }}
                                                title={sugestaoTooltip}
                                                aria-label={
                                                  sugestaoTooltip
                                                    ? `${sugestaoTooltip}. Preço custo unitário.`
                                                    : 'Preço custo unitário.'
                                                }
                                              />
                                              {!isReadOnly && (
                                                <button
                                                  type="button"
                                                  onClick={() => setModalIAAberta({ 
                                                    linhaId: linha.id, 
                                                    descricao: linha.descricao,
                                                    unidade: linha.unidade,
                                                    precoAtual: linha.precoCustoUnitario,
                                                    sugestaoBudgetId: linha.sugestaoCustoBudgetId ?? null,
                                                    sugestaoBudgetReferencia: linha.sugestaoCustoBudgetReferencia ?? null,
                                                  })}
                                                  className="flex-shrink-0 text-base hover:opacity-70 transition-opacity"
                                                  style={{ fontSize: '16px', lineHeight: 1 }}
                                                  title="Sugerir preço com IA"
                                                  aria-label="Sugerir preço com IA"
                                                >
                                                  🤖
                                                </button>
                                              )}
                                              {sugestaoReferencia && (
                                                <span
                                                  className="flex-shrink-0 text-primary"
                                                  title={sugestaoTooltip}
                                                  aria-label={sugestaoTooltip}
                                                >
                                                  <Money20Regular className="w-4 h-4" />
                                                </span>
                                              )}
                                            </div>
                                          </td>

                                          {/* I - Total Custo (€) - READ ONLY */}
                                          <td 
                                            className={`px-3 py-2 text-right calculated-cell ${estaVazia ? 'text-secondary-text' : 'text-foreground'}`}
                                            style={{ 
                                              fontSize: 'var(--text-base)',
                                              fontWeight: estaVazia ? 'var(--font-weight-normal)' : 'var(--font-weight-semi-bold)',
                                              backgroundColor: 'var(--cost-detail-readonly)',
                                            }}
                                          >
                                            {estaVazia ? '—' : formatarMoeda(totalCusto)}
                                          </td>

                                          {/* J - Margem Lucro Real (%) - READ ONLY */}
                                          <td 
                                            className={`px-3 py-2 text-right calculated-cell ${estaVazia ? 'text-secondary-text' : 'text-foreground'}`}
                                            style={{ 
                                              fontSize: 'var(--text-base)',
                                              fontWeight: estaVazia ? 'var(--font-weight-normal)' : 'var(--font-weight-semi-bold)',
                                              backgroundColor: 'var(--cost-detail-readonly)',
                                            }}
                                          >
                                            {estaVazia ? '—' : `${margemLucroReal.toFixed(2)}%`}
                                          </td>

                                          {/* Execução */}
                                          <td className="px-3 py-2 editable-cell">
                                            {!estaVazia && (
                                              <div className="flex flex-col gap-2">
                                                {/* Resumo */}
                                                {linha.execucao && gerarResumoExecucao(linha.execucao) && (
                                                  <div className="space-y-1 pb-2 border-b border-border">
                                                    {gerarResumoExecucao(linha.execucao)!.map((resumo, idx) => (
                                                      <div 
                                                        key={idx}
                                                        className="text-secondary-text"
                                                        style={{ fontSize: 'var(--text-caption)' }}
                                                      >
                                                        {resumo}
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                                
                                                {/* Botões Gerir Execução */}
                                                <div className="flex items-center gap-3">
                                                  <button
                                                    onClick={() => setPainelExecucaoAberto({ linhaId: linha.id, descricao: linha.descricao })}
                                                    className="text-primary hover:text-primary/80 transition-colors flex items-center gap-1 w-fit"
                                                    style={{ fontSize: 'var(--text-caption)' }}
                                                    title={isReadOnly ? 'Ver execução' : 'Gerir execução'}
                                                  >
                                                    <Wrench20Regular className="w-4 h-4" />
                                                    <span>{isReadOnly ? 'Execução' : 'Gerir Execução'}</span>
                                                  </button>

                                                  {isReadOnly && linha.execucao && !gerarResumoExecucao(linha.execucao) && (
                                                    <span 
                                                      className="text-secondary-text italic"
                                                      style={{ fontSize: 'var(--text-caption)' }}
                                                    >
                                                      Sem distribuição
                                                    </span>
                                                  )}
                                                </div>

                                                {/* Nota */}
                                                {linha.nota !== undefined ? (
                                                  <textarea
                                                    value={linha.nota}
                                                    onChange={(e) => atualizarItem(linha.id, 'nota', e.target.value)}
                                                    onBlur={(e) => {
                                                      if (!isReadOnly && e.target.value.trim() === '') {
                                                        atualizarItem(linha.id, 'nota', undefined);
                                                      }
                                                    }}
                                                    onFocus={() => {
                                                      if (notaEmEdicaoId === linha.id) {
                                                        setNotaEmEdicaoId(null);
                                                      }
                                                    }}
                                                    placeholder="Adicionar nota..."
                                                    readOnly={isReadOnly}
                                                    autoFocus={notaEmEdicaoId === linha.id}
                                                    className="w-full px-3 py-2 border rounded text-foreground placeholder:text-secondary-text focus:outline-none focus:ring-2 focus:ring-primary/20 min-h-[72px] resize-y"
                                                    style={{
                                                      borderRadius: 'var(--radius)',
                                                      backgroundColor: '#fff4b1',
                                                      borderColor: '#f0d27c',
                                                    }}
                                                  />
                                                ) : (
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      if (isReadOnly) {
                                                        return;
                                                      }
                                                      setNotaEmEdicaoId(linha.id);
                                                      atualizarItem(linha.id, 'nota', '');
                                                    }}
                                                    disabled={isReadOnly}
                                                    className="flex items-center gap-1 px-1.5 py-0.5 border rounded text-secondary-text hover:text-foreground hover:bg-accent/60 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                                    style={{
                                                      borderRadius: 'var(--radius)',
                                                      backgroundColor: '#fff4b1',
                                                      borderColor: '#f0d27c',
                                                    }}
                                                  >
                                                    <Note20Regular className="w-4 h-4" />
                                                    <span style={{ fontSize: 'var(--text-small)' }}>Adicionar nota</span>
                                                  </button>
                                                )}
                                              </div>
                                            )}
                                          </td>

                                          {/* Ações */}
                                          <td className="px-3 py-2 text-center">
                                            {!estaVazia && !isReadOnly && (
                                              <div className="relative">
                                                <button
                                                  onClick={() => setMenuAberto(menuAberto === linha.id ? null : linha.id)}
                                                  className="p-1 hover:bg-accent rounded transition-colors"
                                                  style={{ borderRadius: 'var(--radius)' }}
                                                >
                                                  <MoreVertical20Regular className="w-5 h-5 text-foreground" />
                                                </button>

                                                {/* Menu Dropdown */}
                                                {menuAberto === linha.id && (
                                                  <div 
                                                    className="absolute right-0 mt-1 w-48 bg-card border border-border shadow-lg z-10"
                                                    style={{ borderRadius: 'var(--radius-card)' }}
                                                  >
                                                    <button
                                                      onClick={() => duplicarLinha(linha.id)}
                                                      className="w-full px-4 py-2 text-left hover:bg-accent transition-colors flex items-center gap-2 text-foreground"
                                                      style={{ fontSize: 'var(--text-base)' }}
                                                    >
                                                      <Copy20Regular className="w-4 h-4" />
                                                      Duplicar
                                                    </button>
                                                    <button
                                                      onClick={() => removerLinha(linha.id)}
                                                      className="w-full px-4 py-2 text-left hover:bg-accent transition-colors flex items-center gap-2 text-destructive"
                                                      style={{ fontSize: 'var(--text-base)' }}
                                                    >
                                                      <Delete20Regular className="w-4 h-4" />
                                                      Apagar
                                                    </button>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                {!isReadOnly && (
                                  <button
                                    type="button"
                                    onClick={() => criarLinhaVazia(capitulo.id)}
                                    className="mt-2 inline-flex items-center gap-2 px-3 py-2 border border-border text-foreground hover:bg-accent/50 rounded transition-colors"
                                    style={{
                                      borderRadius: 'var(--radius-button)',
                                      fontSize: 'var(--text-caption)',
                                    }}
                                  >
                                    <Add20Regular className="w-4 h-4" />
                                    Adicionar linha
                                  </button>
                                )}
                              </div>
                              );
                            })()}

                          </div>
                        );
                      })}

                      {/* Total da Área */}
                      <div 
                        className="flex justify-between items-center px-5 py-3 bg-accent/70 border border-border"
                        style={{ borderRadius: 'var(--radius)' }}
                      >
                        <span 
                          className="text-foreground uppercase"
                          style={{ 
                            fontSize: 'var(--text-base)',
                            fontWeight: 'var(--font-weight-bold)',
                          }}
                        >
                          Total da Área {area.descricao}
                        </span>
                        <span 
                          className="text-primary"
                          style={{ 
                            fontSize: 'var(--text-lg)',
                            fontWeight: 'var(--font-weight-bold)',
                          }}
                        >
                          {formatarMoeda(totalArea)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Card: Documentos do Orçamento */}
            <div className="bg-card border border-border p-6" style={{ borderRadius: 'var(--radius-card)' }}>
              <h3 
                className="text-foreground mb-5"
                style={{ 
                  fontSize: 'var(--text-section-title)',
                  fontWeight: 'var(--font-weight-semi-bold)'
                }}
              >
                Documentos do Orçamento
              </h3>

              {/* Tabs */}
              <div className="flex border-b border-border mb-4">
                {([
                  { key: 'mapa' as const, label: 'Mapa de Quantidades', count: documentos.filter(d => d.category === 'MapaQuantidades' || !d.category).length },
                  { key: 'projetos' as const, label: 'Projetos', count: documentos.filter(d => d.category === 'Projeto' || d.category === 'technical').length },
                  { key: 'geral' as const, label: 'Documentação Geral', count: documentos.filter(d => d.category === 'DocumentacaoGeral' || d.category === 'general').length },
                ]).map(tab => (
                  <button
                    key={tab.key}
                    className={`px-4 py-2 text-sm transition-colors border-b-2 -mb-px ${
                      documentosTab === tab.key
                        ? 'border-primary text-primary font-medium'
                        : 'border-transparent text-secondary-text hover:text-foreground'
                    }`}
                    onClick={() => { setDocumentosTab(tab.key); sessionStorage.setItem(`docTab-${budgetId}`, tab.key); }}
                  >
                    {tab.label}{tab.count > 0 ? ` (${tab.count})` : ''}
                  </button>
                ))}
              </div>

              {documentUploadError && (
                <div
                  className="mb-4 border border-destructive/30 bg-destructive/10 text-destructive px-4 py-2"
                  style={{ borderRadius: 'var(--radius)' }}
                >
                  {documentUploadError}
                </div>
              )}

              {/* Tab: Mapa de Quantidades */}
              {documentosTab === 'mapa' && (
                <div>
                  {!isReadOnly && (
                    <div className="flex items-center gap-3 mb-4">
                      <button
                        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2"
                        style={{ borderRadius: 'var(--radius-button)' }}
                        disabled={!canImportQuantityMap}
                        title={!canImportQuantityMap ? 'O orçamento ainda não está carregado ou não existe.' : undefined}
                        onClick={() => {
                          if (!canImportQuantityMap) return;
                          setImportError(null);
                          setImportProgressMessage(null);
                          setModalMapaQuantidades({ passo: 1 });
                        }}
                      >
                        <TableRegular className="w-5 h-5" />
                        Importar Mapa de Quantidades
                      </button>
                    </div>
                  )}
                  {(() => {
                    const filtered = documentos.filter(doc => doc.category === 'MapaQuantidades' || !doc.category);
                    return filtered.length > 0 ? (
                      <div className="space-y-2">
                        {filtered.map(doc => (
                          <div 
                            key={doc.id}
                            className="flex items-center justify-between p-3 bg-background border border-border hover:bg-accent transition-colors"
                            style={{ borderRadius: 'var(--radius)' }}
                          >
                            <div className="flex items-center gap-3">
                              <Document20Regular className="w-5 h-5 text-primary" />
                              <div>
                                <div className="text-foreground" style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-normal)' }}>{doc.nome}</div>
                                <div className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>{doc.tamanho}</div>
                                <div className={`${resolveDocumentStatusClass(doc.processingStatus)} flex items-center gap-1`} style={{ fontSize: 'var(--text-caption)' }} title={doc.processingError ?? undefined}>
                                  {resolveDocumentStatusLabel(doc.processingStatus)}
                                  {isDocumentProcessing(doc.processingStatus) && (
                                    <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {!isReadOnly && isDocumentFailed(doc.processingStatus) && (
                                <button className="px-3 py-1.5 border border-border rounded transition-colors text-secondary-text hover:text-foreground hover:bg-secondary" style={{ borderRadius: 'var(--radius)' }} onClick={() => reprocessarDocumento(doc)} title="Reprocessar documento">Reprocessar</button>
                              )}
                              <button className="p-2 hover:bg-secondary rounded transition-colors" style={{ borderRadius: 'var(--radius)' }} onClick={() => handleDownloadDocumento(doc.id)}><ArrowDownload20Regular className="w-5 h-5 text-foreground" /></button>
                              {!isReadOnly && (
                                <button onClick={() => removerDocumento(doc.id)} className="p-2 hover:bg-destructive/10 rounded transition-colors" style={{ borderRadius: 'var(--radius)' }}><Delete20Regular className="w-5 h-5 text-destructive" /></button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-secondary-text" style={{ fontSize: 'var(--text-base)' }}>Nenhum documento nesta secção</div>
                    );
                  })()}
                </div>
              )}

              {/* Tab: Projetos */}
              {documentosTab === 'projetos' && (
                <div>
                  {/* Alerta: API Key Anthropic não configurada */}
                  {hasAnthropicApiKey === false && (
                    <div
                      className="mb-4 flex items-start gap-3 px-4 py-3"
                      style={{ borderRadius: 'var(--radius)', border: '1px solid #fbbf24', backgroundColor: '#fffbeb' }}
                    >
                      <Warning20Regular className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#d97706' }} />
                      <div>
                        <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>
                          API Key da Anthropic não configurada
                        </p>
                        <p className="text-secondary-text mt-1" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
                          Para processar plantas técnicas com Claude Vision, configure a sua API Key em{' '}
                          <strong className="text-foreground">Definições → Inteligência Artificial</strong>
                          {' '}(ícone ⚙️ no menu lateral). Os custos de processamento são cobrados directamente na sua conta Anthropic.
                        </p>
                      </div>
                    </div>
                  )}
                  {!isReadOnly && (
                    <div className="mb-4 flex items-center gap-3 flex-wrap">
                      <TechnicalDocumentUpload
                        budgetId={parsedBudgetId}
                        onQueueChanged={(hasFiles) => setHasUploadQueue(hasFiles)}
                        onUploaded={() => {
                          // Reload documents from the budget
                          getBudgetById(parsedBudgetId).then(budgetData => {
                            setDocumentos(
                              budgetData.documents.map((doc) => ({
                                id: doc.id.toString(),
                                nome: doc.name,
                                tamanho: formatFileSize(doc.sizeInBytes),
                                sizeInBytes: doc.sizeInBytes,
                                url: doc.url ?? null,
                                processingStatus: doc.processingStatus ?? 'Pending',
                                processingError: doc.processingError ?? null,
                                category: (doc as any).category ?? null,
                                technicalDocumentStatus: (doc as any).technicalDocumentStatus ?? null,
                              })),
                            );
                            // Queue polling for documents still processing
                            budgetData.documents.forEach((doc) => {
                              if (isDocumentProcessing(doc.processingStatus)) {
                                queueDocumentoPolling(Number(doc.id));
                              }
                            });
                          }).catch(() => {});
                        }}
                      />
                      <ExtractBudgetDocumentsButton
                        budgetId={parsedBudgetId}
                        documentos={documentos}
                        onSent={() => {
                          // Reload budget to get updated document states, then start polling
                          getBudgetById(parsedBudgetId).then(budgetData => {
                            const updatedDocs = budgetData.documents.map((doc) => ({
                              id: doc.id.toString(),
                              nome: doc.name,
                              tamanho: formatFileSize(doc.sizeInBytes),
                              sizeInBytes: doc.sizeInBytes,
                              url: doc.url ?? null,
                              processingStatus: doc.processingStatus ?? 'Pending',
                              processingError: doc.processingError ?? null,
                              category: (doc as any).category ?? null,
                              technicalDocumentStatus: (doc as any).technicalDocumentStatus ?? null,
                            }));
                            setDocumentos(updatedDocs);
                            // Start polling for all documents now in processing state
                            updatedDocs.forEach((doc) => {
                              if (isDocumentProcessing(doc.processingStatus) || doc.technicalDocumentStatus === 'analyzing') {
                                queueDocumentoPolling(Number(doc.id));
                              }
                            });
                          }).catch(() => {});
                        }}
                      />
                      {(() => {
                        const analysesReady = documentos.filter(
                          (doc) =>
                            (doc.category === 'Projeto' || doc.category === 'technical') &&
                            (doc.technicalDocumentStatus ?? '').toLowerCase() === 'pending_validation',
                        );
                        if (analysesReady.length === 0) return null;
                        const allOpen =
                          analysesReady.length > 0 &&
                          analysesReady.every((d) => boqViewDocIds.has(Number(d.id)));
                        return (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setBoqViewDocIds(() => {
                                  if (allOpen) return new Set();
                                  return new Set(analysesReady.map((d) => Number(d.id)));
                                });
                              }}
                              className="px-4 py-2 border border-border rounded transition-colors text-foreground hover:bg-secondary"
                              style={{ borderRadius: 'var(--radius-button)' }}
                              title={allOpen ? 'Fechar todas as análises' : `Abrir as ${analysesReady.length} análises`}
                            >
                              {allOpen ? 'Fechar todas' : `Abrir todas (${analysesReady.length})`}
                            </button>
                            <button
                              type="button"
                              disabled={isBulkImporting}
                              onClick={async () => {
                                if (!Number.isFinite(parsedBudgetId)) return;
                                setIsBulkImporting(true);
                                setBulkImportStartedAt(Date.now());
                                setBulkImportNow(Date.now());
                                const tickInterval = window.setInterval(() => setBulkImportNow(Date.now()), 1000);
                                try {
                                  const result = await importAllConfidentLinesByBudget(parsedBudgetId);
                                  showToast(
                                    result.importedCount > 0
                                      ? `${result.importedCount} ${result.importedCount === 1 ? 'linha importada' : 'linhas importadas'} para o orçamento.`
                                      : 'Nenhuma linha confiante para importar.',
                                  );
                                  await reloadBudget();
                                } catch (err: unknown) {
                                  const msg = err instanceof Error ? err.message : 'Erro ao importar linhas em batch.';
                                  showToast(msg);
                                } finally {
                                  window.clearInterval(tickInterval);
                                  setIsBulkImporting(false);
                                  setBulkImportStartedAt(null);
                                }
                              }}
                              className="px-4 py-2 bg-primary text-primary-foreground rounded transition-colors hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
                              style={{ borderRadius: 'var(--radius-button)' }}
                              title="Importa todas as linhas confiantes (não estimadas) de todas as análises"
                            >
                              {isBulkImporting ? 'A importar...' : `Importar tudo (${analysesReady.length} ${analysesReady.length === 1 ? 'análise' : 'análises'})`}
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  )}
                  {isBulkImporting && (
                    <div
                      className="mb-3 flex items-start gap-3 px-4 py-3 w-full"
                      style={{ borderRadius: 'var(--radius)', border: '1px solid #bfdbfe', backgroundColor: '#f0f7ff' }}
                    >
                      <div className="relative flex-shrink-0 mt-0.5">
                        <BrainCircuit20Regular className="w-5 h-5" style={{ color: '#7c3aed' }} />
                        <span
                          className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
                          style={{ backgroundColor: '#7c3aed', animation: 'pulse 1.5s ease-in-out infinite' }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-3 flex-wrap">
                          <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>
                            A importar todas as linhas para o orçamento — pode demorar até 1 minuto...
                          </p>
                          {bulkImportStartedAt !== null && (
                            <span
                              className="text-secondary-text font-mono flex-shrink-0"
                              style={{ fontSize: 'var(--text-caption)' }}
                              title="Tempo decorrido"
                            >
                              {(() => {
                                const ms = bulkImportNow - bulkImportStartedAt;
                                const s = Math.floor(ms / 1000);
                                const m = Math.floor(s / 60);
                                const r = s % 60;
                                return m === 0 ? `${r}s` : `${m}m ${r.toString().padStart(2, '0')}s`;
                              })()}
                            </span>
                          )}
                        </div>
                        <p className="text-secondary-text mt-1" style={{ fontSize: 'var(--text-caption)' }}>
                          A consultar sugestões de preço de orçamentos passados e a inserir linhas no orçamento. Não feches a página.
                        </p>
                        <div
                          className="mt-2 h-1.5 w-full overflow-hidden relative"
                          style={{ borderRadius: '999px', backgroundColor: '#dbeafe' }}
                        >
                          <div
                            className="absolute h-full"
                            style={{
                              width: '40%',
                              background: 'linear-gradient(90deg, transparent, #7c3aed, transparent)',
                              animation: 'bulk-import-indeterminate 1.6s ease-in-out infinite',
                            }}
                          />
                        </div>
                        <style>{`
                          @keyframes bulk-import-indeterminate {
                            0% { left: -40%; }
                            100% { left: 100%; }
                          }
                        `}</style>
                      </div>
                    </div>
                  )}
                  {(() => {
                    const filtered = documentos.filter(doc => doc.category === 'Projeto' || doc.category === 'technical');
                    const total = filtered.length;
                    const completed = filtered.filter(d => ['pending_validation', 'validated'].includes((d.technicalDocumentStatus ?? '').toLowerCase())).length;
                    const failed = filtered.filter(d => (d.technicalDocumentStatus ?? '').toLowerCase() === 'failed').length;
                    const inProgress = filtered.filter(d => ['analyzing', 'generating'].includes((d.technicalDocumentStatus ?? '').toLowerCase())).length;
                    const showProgress = total > 1 && (inProgress > 0 || (completed + failed > 0 && completed + failed < total));
                    return (
                      <>
                        {showProgress && (
                          <div
                            className="mb-3 flex items-center gap-3 px-4 py-3"
                            style={{ borderRadius: 'var(--radius)', border: '1px solid #bfdbfe', backgroundColor: '#f0f7ff' }}
                          >
                            <div className="flex-1">
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <span className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>
                                  Extracção: {completed + failed}/{total}
                                </span>
                                {inProgress > 0 && (
                                  <span className="text-primary" style={{ fontSize: 'var(--text-caption)' }}>· {inProgress} a processar</span>
                                )}
                                {completed > 0 && (
                                  <span className="text-emerald-600" style={{ fontSize: 'var(--text-caption)' }}>· {completed} com sucesso</span>
                                )}
                                {failed > 0 && (
                                  <span className="text-destructive" style={{ fontSize: 'var(--text-caption)' }}>· {failed} falhou</span>
                                )}
                              </div>
                              <div
                                className="mt-2 h-1.5 w-full overflow-hidden"
                                style={{ borderRadius: '999px', backgroundColor: '#dbeafe' }}
                              >
                                <div
                                  className="h-full transition-all duration-500"
                                  style={{
                                    width: `${total > 0 ? Math.round(((completed + failed) / total) * 100) : 0}%`,
                                    backgroundColor: failed > 0 && completed + failed === total ? '#f59e0b' : '#3b82f6',
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {(filtered.length > 0 || hasUploadQueue) ? (
                      <div className="space-y-2">
                        {filtered.map(doc => (
                          <div key={doc.id}>
                            <div
                              className="p-3 bg-background border border-border hover:bg-accent transition-colors"
                              style={{ borderRadius: 'var(--radius)' }}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Document20Regular className="w-5 h-5 text-primary" />
                                  <div>
                                    <div className="text-foreground" style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-normal)' }}>{doc.nome}</div>
                                    <div className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>{doc.tamanho}</div>
                                    {!(doc.technicalDocumentStatus && ['analyzing', 'generating'].includes(doc.technicalDocumentStatus.toLowerCase())) && (
                                      <div className={`${resolveDocumentStatusClass(doc.processingStatus)} flex items-center gap-1`} style={{ fontSize: 'var(--text-caption)' }} title={doc.processingError ?? undefined}>
                                        {resolveTechnicalStatusLabel(doc.technicalDocumentStatus) ?? resolveDocumentStatusLabel(doc.processingStatus)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              {!(doc.technicalDocumentStatus && ['analyzing', 'generating'].includes(doc.technicalDocumentStatus.toLowerCase())) && (
                                <div className="flex items-center gap-2">
                                  {!isReadOnly && (isDocumentFailed(doc.processingStatus) || doc.technicalDocumentStatus?.toLowerCase() === 'failed') && (
                                    <button className="px-3 py-1.5 border border-border rounded transition-colors text-secondary-text hover:text-foreground hover:bg-secondary" style={{ borderRadius: 'var(--radius)' }} onClick={() => reprocessarDocumento(doc)} title="Reprocessar documento">Reprocessar</button>
                                  )}
                                  {(isDocumentReady(doc.processingStatus) || doc.technicalDocumentStatus?.toLowerCase() === 'pending_validation' || doc.technicalDocumentStatus?.toLowerCase() === 'validated') && (
                                    <button
                                      className="px-3 py-1.5 border border-border rounded transition-colors text-secondary-text hover:text-foreground hover:bg-secondary"
                                      style={{ borderRadius: 'var(--radius)' }}
                                      onClick={() => {
                                        const id = Number(doc.id);
                                        setBoqViewDocIds((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(id)) next.delete(id); else next.add(id);
                                          return next;
                                        });
                                      }}
                                    >
                                      {boqViewDocIds.has(Number(doc.id)) ? 'Fechar Análise' : 'Ver Análise'}
                                    </button>
                                  )}
                                  <button className="p-2 hover:bg-secondary rounded transition-colors" style={{ borderRadius: 'var(--radius)' }} onClick={() => handleDownloadDocumento(doc.id)}><ArrowDownload20Regular className="w-5 h-5 text-foreground" /></button>
                                  {!isReadOnly && (
                                    <button onClick={() => removerDocumento(doc.id)} className="p-2 hover:bg-destructive/10 rounded transition-colors" style={{ borderRadius: 'var(--radius)' }}><Delete20Regular className="w-5 h-5 text-destructive" /></button>
                                  )}
                                </div>
                              )}
                              </div>
                              {doc.technicalDocumentStatus && ['analyzing', 'generating'].includes(doc.technicalDocumentStatus.toLowerCase()) && (
                                <TechnicalDocumentProgress status={doc.technicalDocumentStatus} error={doc.processingError} />
                              )}
                            </div>
                            {boqViewDocIds.has(Number(doc.id)) && (
                              <div className="mt-2 mb-2">
                                <BoqValidationView
                                  budgetId={parsedBudgetId}
                                  documentId={Number(doc.id)}
                                  onClose={() => setBoqViewDocIds((prev) => {
                                    const next = new Set(prev);
                                    next.delete(Number(doc.id));
                                    return next;
                                  })}
                                  onBudgetChanged={reloadBudget}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                      <div className="text-center py-6 text-secondary-text" style={{ fontSize: 'var(--text-base)' }}>Nenhum documento técnico nesta secção</div>

                      {/* Info banners */}
                      <div className="mx-auto max-w-3xl flex flex-col gap-3 mt-2 mb-3">
                        {hasAnthropicApiKey === false && (
                          <div className="flex items-start gap-3 px-4 py-3" style={{ borderRadius: 'var(--radius)', border: '1px solid #bfdbfe', backgroundColor: '#f0f7ff' }}>
                            <Info20Regular className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#3b82f6' }} />
                            <div>
                              <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>
                                Como configurar
                              </p>
                              <p className="text-secondary-text mt-1" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
                                Para activar o processamento de plantas técnicas, é necessária uma <strong className="text-foreground">API Key da Anthropic</strong>.
                                Configure-a em <strong className="text-foreground">Definições → Inteligência Artificial</strong> (ícone ⚙️ no menu lateral).
                                Os custos são cobrados directamente na sua conta em{' '}
                                <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>console.anthropic.com</a>.
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="flex items-start gap-3 px-4 py-3" style={{ borderRadius: 'var(--radius)', border: '1px solid #bfdbfe', backgroundColor: '#f0f7ff' }}>
                          <Info20Regular className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#3b82f6' }} />
                          <div>
                            <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>
                              Modelos disponíveis
                            </p>
                            <p className="text-secondary-text mt-1" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
                              O modelo é configurável nas Definições. Modelos disponíveis:
                            </p>
                            <table className="mt-2 text-left" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr>
                                  <th className="text-foreground pr-4 pb-1" style={{ fontWeight: 'var(--font-weight-semi-bold)' }}>Modelo</th>
                                  <th className="text-foreground pr-4 pb-1" style={{ fontWeight: 'var(--font-weight-semi-bold)' }}>Característica</th>
                                  <th className="text-foreground pb-1" style={{ fontWeight: 'var(--font-weight-semi-bold)' }}>Custo</th>
                                </tr>
                              </thead>
                              <tbody className="text-secondary-text">
                                <tr><td className="pr-4 py-0.5" style={{ color: 'var(--primary)', fontWeight: 'var(--font-weight-semi-bold)' }}>claude-opus-4-7</td><td className="pr-4">Máxima capacidade (recomendado para plantas técnicas)</td><td style={{ color: 'var(--primary)' }}>Elevado</td></tr>
                                <tr><td className="pr-4 py-0.5">claude-sonnet-4-6</td><td className="pr-4">Equilibrado, mais rápido</td><td>Moderado</td></tr>
                                <tr><td className="pr-4 py-0.5">claude-opus-4-5</td><td className="pr-4">Legado</td><td>Elevado</td></tr>
                                <tr><td className="pr-4 py-0.5">claude-sonnet-4-5</td><td className="pr-4">Legado</td><td>Moderado</td></tr>
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="flex items-start gap-3 px-4 py-3" style={{ borderRadius: 'var(--radius)', border: '1px solid #bfdbfe', backgroundColor: '#f0f7ff' }}>
                          <Info20Regular className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#3b82f6' }} />
                          <div>
                            <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>
                              Variação nos resultados
                            </p>
                            <p className="text-secondary-text mt-1" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
                              O número de linhas geradas pode variar entre processamentos do mesmo documento — comportamento normal dos modelos de linguagem. Para documentos com muitos artigos, aumente o limite de tokens nas Definições.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mx-auto max-w-3xl rounded-lg overflow-hidden mt-2" style={{ borderRadius: 'var(--radius)', border: '1px solid #bfdbfe', backgroundColor: '#f0f7ff' }}>
                        {/* Header */}
                        <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #eff6ff 100%)', borderBottom: '1px solid #bfdbfe' }}>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                              <BrainCircuit20Regular className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                              <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--text-base)' }}>
                                Motor de Extracção com Claude Vision
                              </p>
                              <p className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
                                Processamento directo de plantas técnicas via API Anthropic — sem intermediários
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Pipeline visual */}
                        <div className="px-6 py-5">
                          <div className="flex items-center justify-between gap-1 mb-6">
                            <div className="flex-1 text-center">
                              <div className="inline-flex items-center justify-center w-20 h-20 mb-3">
                                <DocumentAdd20Regular className="w-10 h-10" style={{ color: '#0891b2' }} />
                              </div>
                              <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-base)' }}>Upload</p>
                            </div>
                            <div style={{ fontSize: '28px', marginTop: '-28px', color: '#94a3b8' }}>&#10140;</div>
                            <div className="flex-1 text-center">
                              <div className="inline-flex items-center justify-center w-20 h-20 mb-3">
                                <BrainCircuit20Regular className="w-10 h-10" style={{ color: '#7c3aed' }} />
                              </div>
                              <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-base)' }}>Claude Vision</p>
                            </div>
                            <div style={{ fontSize: '28px', marginTop: '-28px', color: '#94a3b8' }}>&#10140;</div>
                            <div className="flex-1 text-center">
                              <div className="inline-flex items-center justify-center w-20 h-20 mb-3">
                                <ScanDash20Regular className="w-10 h-10" style={{ color: '#16a34a' }} />
                              </div>
                              <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-base)' }}>BOQ</p>
                              <p className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>Mapa de Quantidades</p>
                            </div>
                            <div style={{ fontSize: '28px', marginTop: '-28px', color: '#94a3b8' }}>&#10140;</div>
                            <div className="flex-1 text-center">
                              <div className="inline-flex items-center justify-center w-20 h-20 mb-3">
                                <ArrowImport20Regular className="w-10 h-10" style={{ color: '#ea580c' }} />
                              </div>
                              <p className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-base)' }}>Importar</p>
                            </div>
                          </div>

                          {/* Detalhes das fases */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Fase 1: Upload */}
                            <div className="rounded-lg p-4 bg-white" style={{ borderRadius: 'var(--radius)', border: '1px solid #bfdbfe' }}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-bold)', backgroundColor: '#dbeafe', color: '#1d4ed8' }}>1</span>
                                <span className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>Upload de Plantas</span>
                              </div>
                              <p className="text-secondary-text mb-2" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
                                Seleccione um ou <strong className="text-foreground">múltiplos PDFs</strong> de plantas técnicas de uma só vez. Os ficheiros são guardados em armazenamento privado seguro (Azure Blob Storage).
                              </p>
                              <ul className="space-y-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
                                <li className="flex items-start gap-1.5">
                                  <span className="mt-0.5" style={{ color: '#3b82f6' }}>&#9679;</span>
                                  <span>Formato aceite: PDF</span>
                                </li>
                                <li className="flex items-start gap-1.5">
                                  <span className="mt-0.5" style={{ color: '#3b82f6' }}>&#9679;</span>
                                  <span>Upload de múltiplos ficheiros em simultâneo</span>
                                </li>
                                <li className="flex items-start gap-1.5">
                                  <span className="mt-0.5" style={{ color: '#3b82f6' }}>&#9679;</span>
                                  <span>Processamento sequencial e independente por ficheiro</span>
                                </li>
                              </ul>
                            </div>

                            {/* Fase 2: Claude Vision */}
                            <div className="rounded-lg p-4 bg-white" style={{ borderRadius: 'var(--radius)', border: '1px solid #bfdbfe' }}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-bold)', backgroundColor: '#ede9fe', color: '#6d28d9' }}>2</span>
                                <span className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>Análise com Claude Vision</span>
                              </div>
                              <p className="text-secondary-text mb-2" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
                                O modelo <strong className="text-foreground">Claude Vision (Anthropic)</strong> analisa directamente o documento como orcamentista especializado em construção civil portuguesa:
                              </p>
                              <ul className="space-y-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
                                <li className="flex items-start gap-1.5">
                                  <span className="mt-0.5" style={{ color: '#8b5cf6' }}>&#9679;</span>
                                  <span>Lê plantas, tabelas de acabamentos, quadros de áreas e anotações</span>
                                </li>
                                <li className="flex items-start gap-1.5">
                                  <span className="mt-0.5" style={{ color: '#8b5cf6' }}>&#9679;</span>
                                  <span>Extrai quantidades explícitas e estima as restantes</span>
                                </li>
                                <li className="flex items-start gap-1.5">
                                  <span className="mt-0.5" style={{ color: '#8b5cf6' }}>&#9679;</span>
                                  <span>Custos pagos directamente na sua conta Anthropic</span>
                                </li>
                              </ul>
                            </div>

                            {/* Fase 3: BOQ + Importar */}
                            <div className="rounded-lg p-4 bg-white" style={{ borderRadius: 'var(--radius)', border: '1px solid #bfdbfe' }}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-bold)', backgroundColor: '#dcfce7', color: '#15803d' }}>3</span>
                                <span className="text-foreground" style={{ fontWeight: 'var(--font-weight-semi-bold)', fontSize: 'var(--text-sm)' }}>Validação e Importação</span>
                              </div>
                              <p className="text-secondary-text mb-2" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
                                As linhas BOQ geradas são apresentadas para <strong className="text-foreground">revisão antes de importar</strong> — o utilizador valida, ajusta e aprova:
                              </p>
                              <ul className="space-y-1 text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
                                <li className="flex items-start gap-1.5">
                                  <span className="mt-0.5" style={{ color: '#22c55e' }}>&#9679;</span>
                                  <span>Descrições técnicas em PT-PT com área e capítulo sugeridos</span>
                                </li>
                                <li className="flex items-start gap-1.5">
                                  <span className="mt-0.5" style={{ color: '#22c55e' }}>&#9679;</span>
                                  <span>Edição de quantidades, unidades e descrições antes de importar</span>
                                </li>
                                <li className="flex items-start gap-1.5">
                                  <span className="mt-0.5" style={{ color: '#22c55e' }}>&#9679;</span>
                                  <span>Importação selectiva — linha a linha ou tudo de uma vez</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-3 flex items-center justify-between" style={{ borderTop: '1px solid #bfdbfe', backgroundColor: '#eff6ff' }}>
                          <p className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
                            Formato suportado: <strong>PDF</strong> · Configure a API Key em <strong>Definições → IA</strong>
                          </p>
                          <p className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
                            Todas as linhas são validadas por si antes de entrar no orçamento
                          </p>
                        </div>
                      </div>
                      </>
                    )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Tab: Documentação Geral */}
              {documentosTab === 'geral' && (
                <div>
                  {!isReadOnly && (
                    <div className="flex items-center gap-3 mb-4">
                      <input
                        ref={documentoInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleDocumentoSelecionado}
                      />
                      <button
                        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ borderRadius: 'var(--radius-button)' }}
                        onClick={() => documentoInputRef.current?.click()}
                        disabled={isUploadingDocumento}
                      >
                        <Add20Regular className="w-5 h-5" />
                        {isUploadingDocumento ? 'A carregar...' : 'Carregar Documento'}
                      </button>
                    </div>
                  )}
                  {(() => {
                    const filtered = documentos.filter(doc => doc.category === 'DocumentacaoGeral' || doc.category === 'general');
                    return filtered.length > 0 ? (
                      <div className="space-y-2">
                        {filtered.map(doc => (
                          <div 
                            key={doc.id}
                            className="flex items-center justify-between p-3 bg-background border border-border hover:bg-accent transition-colors"
                            style={{ borderRadius: 'var(--radius)' }}
                          >
                            <div className="flex items-center gap-3">
                              <Document20Regular className="w-5 h-5 text-primary" />
                              <div>
                                <div className="text-foreground" style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-normal)' }}>{doc.nome}</div>
                                <div className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>{doc.tamanho}</div>
                                <div className={`${resolveDocumentStatusClass(doc.processingStatus)} flex items-center gap-1`} style={{ fontSize: 'var(--text-caption)' }} title={doc.processingError ?? undefined}>
                                  {resolveDocumentStatusLabel(doc.processingStatus)}
                                  {isDocumentProcessing(doc.processingStatus) && (
                                    <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 22 6.477 22 12h-4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {!isReadOnly && isDocumentFailed(doc.processingStatus) && (
                                <button className="px-3 py-1.5 border border-border rounded transition-colors text-secondary-text hover:text-foreground hover:bg-secondary" style={{ borderRadius: 'var(--radius)' }} onClick={() => reprocessarDocumento(doc)} title="Reprocessar documento">Reprocessar</button>
                              )}
                              <button className="p-2 hover:bg-secondary rounded transition-colors" style={{ borderRadius: 'var(--radius)' }} onClick={() => handleDownloadDocumento(doc.id)}><ArrowDownload20Regular className="w-5 h-5 text-foreground" /></button>
                              {!isReadOnly && (
                                <button onClick={() => removerDocumento(doc.id)} className="p-2 hover:bg-destructive/10 rounded transition-colors" style={{ borderRadius: 'var(--radius)' }}><Delete20Regular className="w-5 h-5 text-destructive" /></button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-secondary-text" style={{ fontSize: 'var(--text-base)' }}>Nenhum documento nesta secção</div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {!isCommentsOpen && (
        <button
          type="button"
          onClick={() => setIsCommentsOpen(true)}
          aria-label="Abrir comentários"
          className={`fixed comments-toggle z-40 flex items-center gap-1 px-2 h-16 border rounded-l transition-colors ${
            comentariosCount > 0
              ? 'bg-primary border-primary text-primary-foreground shadow-md hover:bg-primary/90'
              : 'bg-card border-border hover:bg-accent'
          }`}
          style={{ borderRadius: 'var(--radius-button)', top: '50%', transform: 'translateY(-50%)' }}
        >
          <CommentMultiple20Regular
            className={`w-5 h-5 ${comentariosCount > 0 ? 'text-primary-foreground' : 'text-foreground'}`}
          />
          <span
            className={comentariosCount > 0 ? 'text-primary-foreground' : 'text-secondary-text'}
            style={{ fontSize: 'var(--text-caption)' }}
          >
            ({comentariosCount})
          </span>
        </button>
      )}
    </div>

      {isCommentsOpen && (
        <aside
          className="fixed inset-y-0 right-0 z-40 border-l border-border bg-card flex flex-col"
          style={{ width: `${commentsSidebarWidth}px` }}
        >
          <div className="flex items-center justify-end px-3 py-2 border-b border-border bg-card">
            <button
              type="button"
              onClick={() => setIsCommentsOpen(false)}
              className="flex items-center gap-2 px-3 py-1.5 bg-secondary hover:bg-accent border border-border rounded transition-colors"
              style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-caption)' }}
            >
              <Dismiss20Regular className="w-4 h-4" />
              Fechar
            </button>
          </div>
          <div className="flex-1 p-3 min-h-0 overflow-hidden">
            <Comments
              comments={comentarios}
              onAddComment={handleAdicionarComentario}
              onEditComment={handleEditarComentario}
              onDeleteComment={handleApagarComentario}
              isLoading={comentariosLoading}
            />
          </div>
        </aside>
      )}

      {/* Modal: Fórmula de Quantidade */}
      {quantidadeFormulaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setQuantidadeFormulaModal(null)}
          />
          <div
            className="relative bg-card border border-border p-6 shadow-xl"
            style={{
              borderRadius: 'var(--radius-dialog)',
              width: 'min(520px, 92vw)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className="text-foreground"
                style={{ fontSize: 'var(--text-section-title)', fontWeight: 'var(--font-weight-semi-bold)' }}
              >
                Fórmula de Quantidade
              </h3>
              <button
                type="button"
                onClick={() => setQuantidadeFormulaModal(null)}
                className="p-2 hover:bg-accent rounded transition-colors"
                style={{ borderRadius: 'var(--radius)' }}
              >
                <Dismiss20Regular className="w-5 h-5 text-foreground" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <div className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
                  Linha
                </div>
                <div className="text-foreground" style={{ fontSize: 'var(--text-base)' }}>
                  {linhaFormulaQuantidade?.descricao ?? 'Linha não encontrada'}
                </div>
              </div>

              <div>
                <label
                  className="text-secondary-text block mb-1"
                  style={{ fontSize: 'var(--text-caption)' }}
                >
                  Fórmula
                </label>
                <input
                  type="text"
                  value={quantidadeFormulaModal.formula}
                  onChange={(event) =>
                    setQuantidadeFormulaModal({
                      ...quantidadeFormulaModal,
                      formula: event.target.value,
                    })
                  }
                  readOnly={isReadOnly}
                  placeholder="Ex: (2+3)*4/5"
                  className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
                  style={{ borderRadius: 'var(--radius)' }}
                />
                <div className="text-secondary-text mt-1" style={{ fontSize: 'var(--text-caption)' }}>
                  Suporta + − × ÷ e parênteses.
                </div>
              </div>

              <div>
                <label
                  className="text-secondary-text block mb-1"
                  style={{ fontSize: 'var(--text-caption)' }}
                >
                  Nota
                </label>
                <textarea
                  value={quantidadeFormulaModal.nota}
                  onChange={(event) =>
                    setQuantidadeFormulaModal({
                      ...quantidadeFormulaModal,
                      nota: event.target.value,
                    })
                  }
                  readOnly={isReadOnly}
                  placeholder="Observações sobre a fórmula"
                  rows={2}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60 resize-none"
                  style={{ borderRadius: 'var(--radius)' }}
                />
              </div>

              <div
                className="flex items-center justify-between bg-secondary/40 px-3 py-2 border border-border"
                style={{ borderRadius: 'var(--radius)' }}
              >
                <span className="text-secondary-text" style={{ fontSize: 'var(--text-caption)' }}>
                  Resultado
                </span>
                <span
                  className="text-foreground"
                  style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semi-bold)' }}
                >
                  {previewFormulaQuantidade?.value !== null && previewFormulaQuantidade?.value !== undefined
                    ? formatarQuantidade(previewFormulaQuantidade.value)
                    : '—'}
                </span>
              </div>

              {previewFormulaQuantidade?.error && (
                <div className="text-destructive" style={{ fontSize: 'var(--text-caption)' }}>
                  {previewFormulaQuantidade.error}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-6">
              {linhaFormulaQuantidade?.quantidadeFormula ? (
                <button
                  type="button"
                  onClick={limparFormulaQuantidade}
                  disabled={isReadOnly}
                  className="px-3 py-2 border border-destructive text-destructive rounded transition-colors hover:bg-destructive/10"
                  style={{ borderRadius: 'var(--radius-button)' }}
                >
                  Remover fórmula
                </button>
              ) : (
                <div />
              )}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantidadeFormulaModal(null)}
                  className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors"
                  style={{ borderRadius: 'var(--radius-button)' }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={aplicarFormulaQuantidade}
                  disabled={
                    !linhaFormulaQuantidade ||
                    !previewFormulaQuantidade ||
                    previewFormulaQuantidade.value === null ||
                    Boolean(previewFormulaQuantidade.error) ||
                    isReadOnly
                  }
                  className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ borderRadius: 'var(--radius-button)' }}
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sugestões de IA */}
      {modalIAAberta && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setModalIAAberta(null)}
        >
          <div
            className="bg-card border border-border w-full max-w-[560px] max-h-[90vh] overflow-y-auto"
            style={{
              boxShadow: 'var(--elevation-sm)',
              borderRadius: 'var(--radius-dialog)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 
                className="text-foreground"
                style={{ 
                  fontSize: 'var(--text-h3)',
                  fontWeight: 'var(--font-weight-semi-bold)',
                }}
              >
                Sugestões de Preço de Custo (IA)
              </h2>
              <button
                onClick={() => setModalIAAberta(null)}
                className="p-2 hover:bg-accent rounded transition-colors"
                style={{ borderRadius: 'var(--radius)' }}
                aria-label="Fechar"
              >
                <Dismiss20Regular className="w-5 h-5 text-foreground" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {/* Info do artigo */}
              <div 
                className="mb-4 p-3 bg-muted rounded"
                style={{ 
                  borderRadius: 'var(--radius)',
                  fontSize: 'var(--text-base)',
                }}
              >
                <div 
                  className="text-muted-foreground"
                  style={{ fontSize: 'var(--text-caption)' }}
                >
                  Artigo:
                </div>
                <div 
                  className="text-foreground mt-1"
                  style={{ fontWeight: 'var(--font-weight-semi-bold)' }}
                >
                  {modalIAAberta.descricao}
                </div>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="sugestao-pesquisa"
                  className="block text-foreground mb-2"
                  style={{ fontSize: 'var(--text-caption)' }}
                >
                  Pesquisa semântica
                </label>
                <input
                  id="sugestao-pesquisa"
                  type="text"
                  value={sugestaoPesquisa}
                  onChange={(event) => setSugestaoPesquisa(event.target.value)}
                  placeholder="Escreve um artigo para pesquisar..."
                  className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                  style={{ borderRadius: 'var(--radius)' }}
                />
                <div
                  className="text-secondary-text mt-2"
                  style={{ fontSize: 'var(--text-caption)' }}
                >
                  Atualiza automaticamente enquanto escreves.
                </div>
              </div>

              <div className="mb-4">
                <label
                  htmlFor="sugestao-filtro-data"
                  className="block text-foreground mb-2"
                  style={{ fontSize: 'var(--text-caption)' }}
                >
                  Filtro de data
                </label>
                <select
                  id="sugestao-filtro-data"
                  value={sugestaoFiltroData}
                  onChange={(event) =>
                    setSugestaoFiltroData(event.target.value as '6m' | '12m' | 'all')
                  }
                  className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                  style={{ borderRadius: 'var(--radius)' }}
                >
                  <option value="6m">Últimos 6 meses</option>
                  <option value="12m">Últimos 12 meses</option>
                  <option value="all">Sempre</option>
                </select>
              </div>

              {/* Lista de Sugestões */}
              <div className="space-y-2">
                {sugestoesPrecoLoading ? (
                  <div
                    className="border border-dashed border-border bg-muted/40 text-secondary-text px-4 py-6 text-center"
                    style={{ borderRadius: 'var(--radius)' }}
                  >
                    A procurar sugestões...
                  </div>
                ) : sugestoesPrecoError ? (
                  <div
                    className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-4 text-center"
                    style={{ borderRadius: 'var(--radius)' }}
                  >
                    {sugestoesPrecoError}
                  </div>
                ) : sugestoesIA.length === 0 ? (
                  <div
                    className="border border-dashed border-border bg-muted/40 text-secondary-text px-4 py-6 text-center"
                    style={{ borderRadius: 'var(--radius)' }}
                  >
                    Sem sugestões disponíveis para esta pesquisa/filtro.
                  </div>
                ) : (
                  sugestoesIA.map((sugestao, idx) => {
                    const isSugestaoSelecionada =
                      modalIAAberta.sugestaoBudgetId === sugestao.budgetId &&
                      modalIAAberta.precoAtual !== null &&
                      modalIAAberta.precoAtual !== undefined &&
                      Math.abs(sugestao.costUnitPrice - modalIAAberta.precoAtual) < 0.01;

                    return (
                      <button
                        key={idx}
                        onClick={() => aplicarSugestaoPreco(modalIAAberta.linhaId, sugestao)}
                        className={`w-full text-left p-4 border transition-colors ${
                          isSugestaoSelecionada
                            ? 'bg-accent border-primary'
                            : 'border-border hover:bg-accent hover:border-primary'
                        }`}
                        style={{
                          borderRadius: 'var(--radius)',
                        }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 space-y-1">
                            <div
                              className="text-foreground"
                              style={{
                                fontSize: 'var(--text-h4)',
                                fontWeight: 'var(--font-weight-semi-bold)',
                              }}
                            >
                              {formatarMoeda(sugestao.costUnitPrice)}
                            </div>
                            <div
                              className={isSugestaoSelecionada ? 'text-accent-foreground' : 'text-muted-foreground'}
                              style={{ fontSize: 'var(--text-caption)' }}
                            >
                              Orçamento: {sugestao.budgetReference}
                            </div>
                            <div
                              className={isSugestaoSelecionada ? 'text-accent-foreground' : 'text-muted-foreground'}
                              style={{ fontSize: 'var(--text-caption)' }}
                            >
                              Cliente: {sugestao.clientName} · Obra: {sugestao.workName}
                            </div>
                            <div
                              className={isSugestaoSelecionada ? 'text-accent-foreground' : 'text-muted-foreground'}
                              style={{ fontSize: 'var(--text-caption)' }}
                            >
                              Código: {sugestao.lineCode} · {sugestao.lineDescription}
                            </div>
                            <div
                              className={isSugestaoSelecionada ? 'text-accent-foreground' : 'text-muted-foreground'}
                              style={{ fontSize: 'var(--text-caption)' }}
                            >
                              Data do orçamento: {sugestao.budgetDate}
                            </div>
                          </div>
                          {isSugestaoSelecionada && (
                            <div className="flex-shrink-0">
                              <CheckmarkCircle20Filled
                                className="text-primary"
                                style={{ fontSize: '20px' }}
                              />
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Nova Versão do Orçamento */}
      {isVersionModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-card border border-border w-full max-w-lg overflow-hidden"
            style={{
              borderRadius: 'var(--radius-card)',
              width: '100%',
              maxWidth: '560px',
              maxHeight: '90vh',
            }}
          >
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2
                className="text-foreground"
                style={{
                  fontSize: 'var(--text-h3)',
                  fontWeight: 'var(--font-weight-semi-bold)',
                }}
              >
                Nova versão do orçamento
              </h2>
              <button
                onClick={() => setIsVersionModalOpen(false)}
                className="text-secondary-text hover:text-foreground transition-colors"
              >
                <Dismiss20Regular className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div
                className="text-secondary-text"
                style={{ fontSize: 'var(--text-base)' }}
              >
                Escolha a versão base e defina uma etiqueta opcional para esta nova versão.
              </div>

              <div>
                <label
                  htmlFor="novaVersaoBase"
                  className="block text-foreground mb-2"
                  style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
                >
                  Copiar dados de
                </label>
                <select
                  id="novaVersaoBase"
                  value={novaVersaoBaseId ?? parsedBudgetId}
                  onChange={(event) =>
                    setNovaVersaoBaseId(
                      Number.parseInt(event.target.value, 10),
                    )
                  }
                  disabled={versionsLoading || isLoading}
                  className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
                >
                  {orderedBudgetVersions.length === 0 ? (
                    <option value={parsedBudgetId}>V{budget?.versionNumber ?? 1}</option>
                  ) : (
                    orderedBudgetVersions.map((version) => (
                      <option key={version.id} value={version.id}>
                        V{version.versionNumber}
                        {version.versionLabel ? ` - ${version.versionLabel}` : ''}
                        {version.isActiveVersion ? ' (Ativa)' : ''}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label
                  htmlFor="novaVersaoLabel"
                  className="block text-foreground mb-2"
                  style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
                >
                  Etiqueta da versão
                </label>
                <input
                  id="novaVersaoLabel"
                  type="text"
                  value={novaVersaoLabel}
                  onChange={(event) => setNovaVersaoLabel(event.target.value)}
                  placeholder="Ex: Ajustes finais"
                  maxLength={120}
                  className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
                />
              </div>

              {versionsError && (
                <div
                  className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-2"
                  style={{ borderRadius: 'var(--radius)' }}
                >
                  {versionsError}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <button
                className="px-4 py-2 bg-secondary hover:bg-accent border border-border text-foreground transition-colors"
                style={{ borderRadius: 'var(--radius-button)' }}
                onClick={() => setIsVersionModalOpen(false)}
              >
                Cancelar
              </button>
              <button
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: 'var(--radius-button)' }}
                disabled={isCreatingVersion}
                onClick={() => void criarNovaVersao()}
              >
                {isCreatingVersion ? 'A criar...' : 'Criar versão'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Confirmar Alteração de Estado */}
      {workflowModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-card border border-border w-full max-w-lg overflow-hidden"
            style={{
              borderRadius: 'var(--radius-card)',
              width: '100%',
              maxWidth: '520px',
              maxHeight: '90vh',
            }}
          >
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2
                className="text-foreground"
                style={{
                  fontSize: 'var(--text-h3)',
                  fontWeight: 'var(--font-weight-semi-bold)',
                }}
              >
                Confirmar estado do orçamento
              </h2>
              <button
                onClick={() => setWorkflowModal(null)}
                className="text-secondary-text hover:text-foreground transition-colors"
              >
                <Dismiss20Regular className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div
                className="text-secondary-text"
                style={{ fontSize: 'var(--text-base)' }}
              >
                {workflowModal.status === 'aceite'
                  ? 'Tem a certeza que pretende marcar o orçamento como aceite?'
                  : 'Para marcar o orçamento como recusado, indique o motivo da recusa.'}
              </div>

              {workflowModal.status === 'recusado' && (
                <div>
                  <label
                    htmlFor="workflowRejectionReason"
                    className="block text-foreground mb-2"
                    style={{ fontSize: 'var(--text-caption)', fontWeight: 'var(--font-weight-semi-bold)' }}
                  >
                    Motivo da recusa
                  </label>
                  <textarea
                    id="workflowRejectionReason"
                    value={workflowModal.reason}
                    onChange={(event) =>
                      setWorkflowModal((prev) =>
                        prev
                          ? { ...prev, reason: event.target.value }
                          : prev,
                      )
                    }
                    placeholder="Ex: Proposta acima do orçamento do cliente."
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-y"
                    style={{ borderRadius: 'var(--radius-button)', fontSize: 'var(--text-base)' }}
                  />
                </div>
              )}

              {workflowModalError && (
                <div
                  className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-2"
                  style={{ borderRadius: 'var(--radius)' }}
                >
                  {workflowModalError}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <button
                className="px-4 py-2 bg-secondary hover:bg-accent border border-border text-foreground transition-colors"
                style={{ borderRadius: 'var(--radius-button)' }}
                onClick={() => setWorkflowModal(null)}
              >
                Cancelar
              </button>
              <button
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderRadius: 'var(--radius-button)' }}
                disabled={isWorkflowUpdating}
                onClick={() => void handleConfirmWorkflowModal()}
              >
                {isWorkflowUpdating ? 'A atualizar...' : 'Confirmar estado'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Importar Mapa de Quantidades */}
      {modalMapaQuantidades && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-card border border-border w-full max-w-2xl overflow-hidden"
            style={{ borderRadius: 'var(--radius-card)' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2
                className="text-foreground"
                style={{
                  fontSize: 'var(--text-h3)',
                  fontWeight: 'var(--font-weight-semi-bold)',
                }}
              >
                Importar Mapa de Quantidades
              </h2>
              <button
                onClick={() => {
                  cancelImportIfRunning();
                  setImportError(null);
                  setImportProgressMessage(null);
                  setImportProgressPercent(null);
                  setLastImportUpdateAt(null);
                  setModalMapaQuantidades(null);
                }}
                className="text-secondary-text hover:text-foreground transition-colors"
              >
                <Dismiss20Regular className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              <div className="space-y-4">
                <div
                  className="text-secondary-text mb-4"
                  style={{ fontSize: 'var(--text-base)' }}
                >
                  Carregue o Mapa de Quantidades em PDF, imagem ou Excel. A extração e classificação
                  são feitas por IA e o orçamento é gravado de imediato.
                </div>

                <div
                  className={`border-2 border-dashed border-border bg-secondary transition-colors p-8 text-center ${
                    isImportingMapaQuantidades
                      ? 'cursor-not-allowed opacity-60'
                      : 'cursor-pointer hover:bg-accent'
                  }`}
                  style={{ borderRadius: 'var(--radius)' }}
                  onClick={() => {
                    if (isImportingMapaQuantidades) {
                      return;
                    }

                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.pdf,.png,.jpg,.jpeg,.tif,.tiff,.xlsx,.xls,.xlsm';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        void handleImportFile(file);
                      }
                    };
                    input.click();
                  }}
                >
                  <TableRegular className="w-12 h-12 text-primary mx-auto mb-3" />
                  <div
                    className="text-foreground mb-1"
                    style={{
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-weight-semi-bold)',
                    }}
                  >
                    {isImportingMapaQuantidades ? (
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className="inline-block h-3 w-3 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span>A processar{importPulseDots}</span>
                      </span>
                    ) : (
                      importDisplayMessage
                    )}
                  </div>
                  {!isImportingMapaQuantidades && (
                    <div
                      className="text-secondary-text"
                      style={{ fontSize: 'var(--text-caption)' }}
                    >
                      Formatos aceites: .pdf, .png, .jpg, .jpeg, .tif, .tiff, .xlsx, .xls, .xlsm
                    </div>
                  )}
                  {isImportingMapaQuantidades && (
                    <div className="mt-4 space-y-2">
                      <style>{`@keyframes boq-indeterminate { 0% { transform: translateX(-100%); } 100% { transform: translateX(350%); } }`}</style>
                      <div
                        className="h-2 w-full bg-border overflow-hidden relative"
                        style={{ borderRadius: 'var(--radius)' }}
                      >
                        <div
                          className="h-full bg-primary absolute"
                          style={{ width: '30%', animation: 'boq-indeterminate 1.4s ease-in-out infinite' }}
                        />
                      </div>
                      <div
                        className="text-secondary-text text-center"
                        style={{ fontSize: 'var(--text-caption)' }}
                      >
                        {importStatusMessage}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {importError && (
                <div
                  className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-2"
                  style={{ borderRadius: 'var(--radius)' }}
                >
                  {importError}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
              <button
                className="px-4 py-2 bg-secondary hover:bg-accent border border-border text-foreground transition-colors disabled:opacity-50"
                style={{ borderRadius: 'var(--radius-button)' }}
                onClick={() => {
                  cancelImportIfRunning();
                  setImportError(null);
                  setImportProgressMessage(null);
                  setImportProgressPercent(null);
                  setLastImportUpdateAt(null);
                  setModalMapaQuantidades(null);
                }}
              >
                {isImportingMapaQuantidades ? 'Cancelar' : 'Fechar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Guardar como Template */}
      <GuardarComoTemplateModal
        isOpen={isGuardarTemplateModalOpen}
        onClose={() => {
          if (!isSavingTemplate) {
            setIsGuardarTemplateModalOpen(false);
          }
        }}
        onConfirm={handleGuardarComoTemplate}
        isSaving={isSavingTemplate}
      />

      {/* Painel de Gerir Execução */}
      <GerirExecucaoPanel
        isOpen={!!painelExecucaoAberto}
        onClose={() => setPainelExecucaoAberto(null)}
        linhaDescricao={painelExecucaoAberto?.descricao ?? ''}
        execucaoAtual={
          painelExecucaoAberto
            ? itens.find(item => item.id === painelExecucaoAberto.linhaId)?.execucao ?? null
            : null
        }
        onSalvar={salvarExecucaoLinha}
        subempreiteirosDisponiveis={subempreiteirosDisponiveis}
        fornecedoresDisponiveis={fornecedoresDisponiveis}
        onCriarSubempreiteiro={criarSubempreiteiroRapido}
        onCriarFornecedor={criarFornecedorRapido}
        isReadOnly={isReadOnly}
      />
    </div>
  );
}
